/**
 * ErvaPOS - POS Terminal Page
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  useNavigate } from 'react-router-dom'; import { Button } from '../components/ui/button'; import { Input } from '../components/ui/input'; import { Label } from '../components/ui/label'; import { Badge } from '../components/ui/badge'; import { Card } from '../components/ui/card'; import { ScrollArea } from '../components/ui/scroll-area'; import { Textarea } from '../components/ui/textarea'; import {   Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, } from '../components/ui/dialog'; import {   Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '../components/ui/select'; import {   Search, X, Plus, Minus, Trash2, CreditCard, Banknote, Smartphone, FileText, Pause, Play, ShoppingCart, User, ChevronLeft, Loader2, Check, Receipt, Tag, Percent, MessageSquare, Gift, Ticket, Mail, History, Users, Eye, Wallet
} from 'lucide-react';
import { productsAPI, batchesAPI, salesAPI, customersAPI, campaignsAPI, vouchersAPI, ordersAPI, settingsAPI, providersAPI, authAPI, reportsAPI, posSyncAPI } from '../services/api';
import SelectCustomerModal from '../components/SelectCustomerModal';
import POSLockOverlay from '../components/POSLockOverlay';
import UserSecurityModal from '../components/UserSecurityModal';
import useFamaBranding from '../hooks/useFamaBranding';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { buildReceiptPreviewPdfFromElement } from '../lib/receiptPreviewPdf';

const PAYMENT_LABELS = {
  cash: 'Dinheiro',
  card: 'Multibanco/Cartão',
  mbway: 'MBWay',
  multibanco: 'Multibanco/Cartão',
  transfer: 'Transferência',
  credit_card: 'Cartão Crédito',
  voucher: 'Voucher',
  other: 'Outro'
};

const normalizePOSCategory = (value = '') =>
  String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ');

const findProductByCommercialCode = (products = [], code = '') => {
  const normalizedCode = String(code || '').replace(/\s+/g, '').trim();
  if (!normalizedCode) return null;

  const matches = products.filter((product) => {
    const barcode = String(product?.barcode || '').replace(/\s+/g, '').trim();
    const reference = String(product?.reference || '').replace(/\s+/g, '').trim();
    return barcode === normalizedCode || reference === normalizedCode;
  });

  // Dados antigos podem conter mais do que um produto ativo com a mesma
  // referência. Nessa situação, nunca deixar um registo sem stock ocultar o
  // artigo que tem stock real disponível.
  return matches.sort((a, b) => {
    const stockDifference = Number(b?.total_stock || 0) - Number(a?.total_stock || 0);
    if (stockDifference !== 0) return stockDifference;
    return String(b?.updated_at || '').localeCompare(String(a?.updated_at || ''));
  })[0] || null;
};

const isClinicalServiceProduct = (product = {}) => {
  const category = normalizePOSCategory(product?.category);

  return (
    category === 'medicina' ||
    category === 'medicina dentaria' ||
    category === 'servicos' ||
    category === 'services' ||
    !!product?.is_service
  );
};

// A ausência de stock/lotes não transforma um artigo num serviço. Portes,
// taxas, sacos e outros artigos não inventariados seguem como produtos.
const isNonStockProduct = (product = {}) =>
  !isClinicalServiceProduct(product) && product?.control_stock === false;

// ============================================================
// CASH MODAL COMPONENT
// ============================================================

const CashModal = ({ type, onClose, userName, onSaved, prepaymentsSummary=null }) => {
  const [amounts, setAmounts] = useState({cash:0, card:0, mbway:0, multibanco:0, transfer:0, voucher:0, other:0});
  const [countedCash, setCountedCash] = useState('');
  const [outAmount, setOutAmount] = useState('');
  const [outNote, setOutNote] = useState('');
  const [inAmount, setInAmount] = useState('');
  const [inNote, setInNote] = useState('');
  const [openAmount, setOpenAmount] = useState('');
  const [summary, setSummary] = useState(null);
  const [modalPrepaymentsSummary, setModalPrepaymentsSummary] = useState(prepaymentsSummary || null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('ervapos_token') || '';
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  React.useEffect(() => {
    if (type === 'close') {
      fetch(`${API}/cash-movements/session-summary`, {headers:{Authorization:`Bearer ${token}`}})
        .then(r=>r.json())
        .then(async d=>{
          setSummary(d);
          setAmounts({
            cash:d.cash||0,
            card:d.card||0,
            mbway:d.mbway||0,
            multibanco:d.multibanco||0,
            transfer:d.transfer||0,
            voucher:d.voucher||0,
            other:d.other||0
          });

          try {
            const today = new Date().toISOString().slice(0, 10);
            const pre = await reportsAPI.getPrepaymentsDaySummary(today);
            setModalPrepaymentsSummary(pre?.data?.summary || null);
          } catch (_) {
            setModalPrepaymentsSummary(null);
          }
        })
        .catch(()=>{
          setModalPrepaymentsSummary(prepaymentsSummary || null);
        });
    }
  }, [type, API, token, prepaymentsSummary]);

  const baseInputStyle = {
    background:'#ffffff',
    border:'1px solid #cbd5e1',
    color:'#111827',
    caretColor:'#111827',
    WebkitTextFillColor:'#111827'
  };

  const handleSubmit = async () => {
    try {
      let body = { type, operator: userName };

      if (type === 'open') {
        const normalizedAmount = String(openAmount ?? '').trim().replace(',', '.');
        const amount = Number(normalizedAmount);
        if (normalizedAmount === '' || !Number.isFinite(amount) || amount < 0) {
          toast.error('Indica um fundo de caixa inicial válido (pode ser 0 €)');
          return;
        }
        body = {...body, amount, note:'Abertura de caixa'};
      } else if (type === 'in') {
        const amount = parseFloat(inAmount) || 0;
        if (amount <= 0) {
          toast.error('Indica um montante válido para a entrada');
          return;
        }
        if (!String(inNote || '').trim()) {
          toast.error('É obrigatório justificar a entrada de caixa');
          return;
        }
        body = {...body, amount, note: inNote};
      } else if (type === 'out') {
        const amount = parseFloat(outAmount) || 0;
        if (amount <= 0) {
          toast.error('Indica um montante válido para a saída');
          return;
        }
        if (!String(outNote || '').trim()) {
          toast.error('É obrigatório justificar a saída de caixa');
          return;
        }
        body = {...body, amount, note: outNote};
      } else if (type === 'close') {
        const expectedCash = summary?.expected_cash || 0;
        const counted = parseFloat(countedCash) || 0;
        const diff = counted - expectedCash;
        body = {
          ...body,
          amount: counted,
          payment_breakdown: amounts,
          expected_cash: expectedCash,
          counted_cash: counted,
          difference: diff,
          status: diff === 0 ? 'ok' : diff > 0 ? 'over' : 'under',
          note: diff === 0 ? 'Caixa certa' : diff > 0 ? `Sobra €${Math.abs(diff).toFixed(2)}` : `Falta €${Math.abs(diff).toFixed(2)}`
        };
      }

      setLoading(true);

      const response = await fetch(`${API}/cash-movements`, {
        method:'POST',
        headers:{
          'Content-Type':'application/json',
          Authorization:`Bearer ${token}`
        },
        body:JSON.stringify(body)
      });
      let responseBody = null;
      try { responseBody = await response.json(); } catch {}
      if (!response.ok) {
        throw new Error(responseBody?.detail || 'Não foi possível registar o movimento de caixa');
      }

      const msgs = {
        open:'Caixa aberta com sucesso',
        in:'Entrada registada',
        out:'Saída registada',
        close:'Caixa fechada'
      };

      toast.success(msgs[type]);
      try {
        const r = await fetch(`${API}/cash-movements/session-status`, { headers:{Authorization:`Bearer ${token}`} });
        const d = await r.json();
        window.__cashSessionOpen = !!d?.is_open;
      } catch {}
      onSaved?.(type, {
        type,
        movement: body,
        summary,
        amounts,
        counted_cash: parseFloat(countedCash) || 0,
        expected_cash: summary?.expected_cash || 0,
        difference: type === 'close' ? ((parseFloat(countedCash) || 0) - (summary?.expected_cash || 0)) : 0,
        operator: userName,
        created_at: new Date().toISOString(),
      });
      onClose();
    } catch (error) {
      toast.error(error?.message || 'Erro ao registar movimento de caixa');
    } finally {
      setLoading(false);
    }
  };

  const diff = type==='close' ? (parseFloat(countedCash)||0) - (summary?.expected_cash||0) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{background:'rgba(0,0,0,0.75)'}} onClick={onClose}>
      <div className="rounded-2xl w-[520px] max-h-[90vh] overflow-y-auto" style={{background:'#f8fafc',border:'1px solid #e2e8f0'}} onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b" style={{borderColor:'#e2e8f0'}}>
          <div>
            <p className="text-slate-900 text-base font-bold">
              {type==='open'?'Abertura de Caixa':type==='in'?'Entrada de Caixa':type==='out'?'Saída de Caixa':'Fecho de Caixa'}
            </p>
            <p className="text-slate-700 text-sm font-medium">{new Date().toLocaleDateString('pt-PT')} — {userName}</p>
          </div>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-900"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-5 space-y-4">
          {type==='open' && (
            <div>
              <p className="text-slate-700 text-sm mb-2">Fundo de caixa inicial (€)</p>
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={openAmount}
                onChange={e=>setOpenAmount(e.target.value)}
                placeholder="0.00"
                autoFocus
                className="w-full px-4 py-4 rounded-xl text-2xl font-bold outline-none placeholder:text-slate-400"
                style={baseInputStyle}
              />
            </div>
          )}

          {type==='in' && (
            <>
              <div>
                <p className="text-slate-700 text-sm mb-2">Montante entrada (€)</p>
                <input
                  type="number"
                  value={inAmount}
                  onChange={e=>setInAmount(e.target.value)}
                  placeholder="0.00"
                  autoFocus
                  className="w-full px-4 py-4 rounded-xl text-2xl font-bold outline-none placeholder:text-slate-400"
                  style={baseInputStyle}
                />
              </div>
              <div>
                <p className="text-slate-700 text-sm mb-2">Justificativo</p>
                <input
                  type="text"
                  value={inNote}
                  onChange={e=>setInNote(e.target.value)}
                  placeholder="Ex: Reforço de troco, ajuste de caixa..."
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none placeholder:text-slate-400"
                  style={baseInputStyle}
                />
              </div>
            </>
          )}

          {type==='out' && (
            <>
              <div>
                <p className="text-slate-700 text-sm mb-2">Montante saída (€)</p>
                <input
                  type="number"
                  value={outAmount}
                  onChange={e=>setOutAmount(e.target.value)}
                  placeholder="0.00"
                  autoFocus
                  className="w-full px-4 py-4 rounded-xl text-2xl font-bold outline-none placeholder:text-slate-400"
                  style={baseInputStyle}
                />
              </div>
              <div>
                <p className="text-slate-700 text-sm mb-2">Justificativo</p>
                <input
                  type="text"
                  value={outNote}
                  onChange={e=>setOutNote(e.target.value)}
                  placeholder="Ex: Pagamento, troco, despesa..."
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none placeholder:text-slate-400"
                  style={baseInputStyle}
                />
              </div>
            </>
          )}

          {type==='close' && (
            <>
              <div className="rounded-xl p-4 space-y-2" style={{background:'#ffffff',border:'1px solid #cbd5e1'}}>
                <p className="text-slate-900 text-sm font-semibold mb-3">Resumo de Caixa</p>

                {[{k:'cash',label:'Dinheiro',icon:'💵'},
                  {k:'card',label:'Multibanco/Cartão',icon:'💳'},
                  {k:'mbway',label:'MBWay',icon:'📱'},
                  {k:'transfer',label:'Transferência',icon:'🏦'},
                    {k:'account_current',label:'Conta Corrente',icon:'📒'},
                  {k:'voucher',label:'Vouchers',icon:'🎟️'},
                  {k:'other',label:'Outros',icon:'📋'}].map(({k,label,icon})=>(
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-slate-700 text-sm font-medium">{icon} {label}</span>
                    <span className="text-slate-900 text-xs font-bold">€{(amounts[k]||0).toFixed(2)}</span>
                  </div>
                ))}

                <div className="flex items-center justify-between pt-2 border-t" style={{borderColor:'#e2e8f0'}}>
                  <span className="text-slate-700 text-sm font-semibold">Total vendas</span>
                  <span className="text-emerald-700 text-base font-bold">€{(summary?.total||0).toFixed(2)}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-slate-700 text-sm font-semibold">Abertura</span>
                  <span className="text-slate-900 text-sm font-medium">€{(summary?.opening||0).toFixed(2)}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-slate-700 text-sm font-semibold">Entradas de caixa</span>
                  <span className="text-blue-700 text-sm font-medium">+€{(summary?.cash_in||0).toFixed(2)}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-slate-700 text-sm font-semibold">Saídas de caixa</span>
                  <span className="text-rose-600 text-sm font-medium">-€{(summary?.cash_out||0).toFixed(2)}</span>
                </div>

                <div className="flex items-center justify-between pt-1 border-t" style={{borderColor:'#e2e8f0'}}>
                  <span className="text-slate-700 text-sm font-semibold">Esperado em caixa</span>
                  <span className="text-blue-700 text-sm font-bold">€{(summary?.expected_cash||0).toFixed(2)}</span>
                </div>
              </div>

              {modalPrepaymentsSummary && (
                <div className="rounded-xl p-4 space-y-2" style={{background:'#f8fafc',border:'1px solid #cbd5e1'}}>
                  <p className="text-slate-900 text-sm font-semibold">Pré-pagamentos do dia</p>

                  <div className="flex items-center justify-between">
                    <span className="text-slate-700 text-sm font-medium">Recebido no dia</span>
                    <span className="text-slate-900 text-sm font-bold">€{Number(modalPrepaymentsSummary.received_total || 0).toFixed(2)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-slate-700 text-sm font-medium">Consumido no dia</span>
                    <span className="text-emerald-700 text-sm font-bold">€{Number(modalPrepaymentsSummary.consumed_in_sales || 0).toFixed(2)}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-slate-700 text-sm font-medium">Efeito líquido do dia</span>
                    <span className={`text-sm font-bold ${Number(modalPrepaymentsSummary.net_day_effect || 0) >= 0 ? 'text-amber-600' : 'text-rose-600'}`}>
                      €{Number(modalPrepaymentsSummary.net_day_effect || 0).toFixed(2)}
                    </span>
                  </div>

                  <p className="text-slate-500 text-xs pt-1">
                    Mostra apenas movimentos de pré-pagamentos deste dia. Não altera diretamente o esperado em caixa.
                  </p>
                </div>
              )}

              <div>
                <p className="text-slate-700 text-sm mb-2">Dinheiro contado fisicamente (€)</p>
                <input
                  type="number"
                  value={countedCash}
                  onChange={e=>setCountedCash(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-4 py-4 rounded-xl text-2xl font-bold outline-none placeholder:text-slate-400"
                  style={baseInputStyle}
                />
              </div>

              {countedCash!=='' && (
                <div
                  className="rounded-xl p-4 text-center"
                  style={{
                    background: diff===0?'rgba(34,197,94,0.10)': diff>0?'rgba(245,158,11,0.10)':'rgba(239,68,68,0.10)',
                    border:`1px solid ${diff===0?'rgba(34,197,94,0.25)':diff>0?'rgba(245,158,11,0.25)':'rgba(239,68,68,0.25)'}`
                  }}
                >
                  <p className="text-xs mb-1" style={{color: diff===0?'#15803d':diff>0?'#d97706':'#dc2626'}}>
                    {diff===0?'✓ Caixa certa':diff>0?'⬆ Sobra na caixa':'⬇ Falta na caixa'}
                  </p>
                  <p className="text-2xl font-bold" style={{color: diff===0?'#15803d':diff>0?'#d97706':'#dc2626'}}>
                    {diff===0?'€0.00': `${diff>0?'+':'-'}€${Math.abs(diff).toFixed(2)}`}
                  </p>
                </div>
              )}
            </>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-3 rounded-xl font-bold text-sm disabled:opacity-50"
            style={{background:'linear-gradient(135deg,#4ade80,#22c55e)',color:'#ffffff'}}
          >
            {loading ? 'A processar...' : type==='open' ? 'Abrir Caixa' : type==='in' ? 'Registar Entrada' : type==='out' ? 'Registar Saída' : 'Fechar Caixa'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CashHistoryModal = ({ open, onClose }) => {
  const token = localStorage.getItem('ervapos_token') || '';
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const today = new Date().toISOString().slice(0,10);

  const [date, setDate] = useState(today);
  const [operator, setOperator] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadHistory = React.useCallback(async () => {
    if (!open) return;
    try {
      setLoading(true);
      const qs = new URLSearchParams();
      if (date) qs.set('date', date);
      if (operator) qs.set('operator', operator);

      const r = await fetch(`${API}/cash-movements/history?${qs.toString()}`, {
        headers:{Authorization:`Bearer ${token}`}
      });
      const d = await r.json();
      setData(d);
    } catch {
      toast.error('Erro ao carregar histórico de caixa');
    } finally {
      setLoading(false);
    }
  }, [open, date, operator, API, token]);

  React.useEffect(() => { loadHistory(); }, [loadHistory]);

  const printReceipt = () => {
    if (!data) return;

    const s = data.summary || {};
    const rows = (data.movements || []).map(m => `
      <tr>
        <td>${m.created_at ? new Date(m.created_at).toLocaleTimeString('pt-PT', {hour:'2-digit', minute:'2-digit'}) : ''}</td>
        <td>${m.type || ''}</td>
        <td>${m.operator || ''}</td>
        <td>${m.note || ''}</td>
        <td style="text-align:right;">€${Number(m.amount || 0).toFixed(2)}</td>
      </tr>
    `).join('');

    const html = `
      <html>
      <head>
        <meta charset="utf-8"/>
        <title>Relatório de Caixa</title>
        <style>
          body { font-family: Arial, sans-serif; width: 80mm; margin: 0 auto; color: #111; }
          .title { text-align: center; font-size: 18px; font-weight: bold; margin: 8px 0; }
          .sub { text-align: center; font-size: 11px; color: #444; margin-bottom: 10px; }
          .box { border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 8px 0; margin: 8px 0; }
          .line { display:flex; justify-content:space-between; font-size:12px; margin: 2px 0; }
          .strong { font-weight: bold; }
          table { width:100%; border-collapse: collapse; font-size: 11px; }
          td { padding: 3px 0; vertical-align: top; }
          .footer { text-align:center; font-size:11px; margin-top: 10px; }
        </style>
      </head>
      <body>
        <div class="title">Relatório de Caixa</div>
        <div class="sub">${data.date || ''}${data.operator ? ' · ' + data.operator : ''}</div>

        <div class="box">
          <div class="line"><span>Abertura</span><span>€${Number(s.opening || 0).toFixed(2)}</span></div>
          <div class="line"><span>Entradas</span><span>€${Number(s.cash_in || 0).toFixed(2)}</span></div>
          <div class="line"><span>Saídas</span><span>-€${Number(s.cash_out || 0).toFixed(2)}</span></div>
          <div class="line"><span>Vendas Dinheiro</span><span>€${Number(s.cash || 0).toFixed(2)}</span></div>
          <div class="line"><span>Cartão</span><span>€${Number(s.card || 0).toFixed(2)}</span></div>
          <div class="line"><span>MBWay</span><span>€${Number(s.mbway || 0).toFixed(2)}</span></div>
          <div class="line"><span>Multibanco</span><span>€${Number(s.multibanco || 0).toFixed(2)}</span></div>
          <div class="line"><span>Transferência</span><span>€${Number(s.transfer || 0).toFixed(2)}</span></div>
          <div class="line"><span>Vouchers</span><span>€${Number(s.voucher || 0).toFixed(2)}</span></div>
          <div class="line strong"><span>Esperado em Caixa</span><span>€${Number(s.expected_cash || 0).toFixed(2)}</span></div>
          <div class="line strong"><span>Contado</span><span>€${Number(s.counted_cash || 0).toFixed(2)}</span></div>
          <div class="line strong"><span>Diferença</span><span>€${Number(s.difference || 0).toFixed(2)}</span></div>
        </div>

        <table>${rows}</table>

        <div class="footer">FamaPOS · Relatório profissional de caixa</div>
        <script>window.onload = () => window.print();</script>
      </body>
      </html>
    `;

    const w = window.open('', '_blank', 'width=420,height=800');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  if (!open) return null;

  const s = data?.summary || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{background:'rgba(0,0,0,0.75)'}} onClick={onClose}>
      <div className="rounded-2xl w-[860px] max-h-[90vh] overflow-y-auto" style={{background:'#f8fafc',border:'1px solid #e2e8f0'}} onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b" style={{borderColor:'#e2e8f0'}}>
          <div>
            <p className="text-slate-950 font-bold">Histórico de Caixa</p>
            <p className="text-slate-700 text-sm font-medium">Consulta por dia e operador</p>
          </div>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-900"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <input
                type="date"
                value={date}
                onChange={e=>setDate(e.target.value)}
                className="px-3 py-2 rounded-xl text-sm outline-none"
                style={{
                  background:"#fff",
                  border:"1px solid #cbd5e1",
                  color:"#111827",
                  WebkitTextFillColor:"#111827",
                  colorScheme:"light"
                }}
              />
              <select
                value={operator}
                onChange={e=>setOperator(e.target.value)}
                className="px-3 py-2 rounded-xl text-sm outline-none"
                style={{
                  background:"#fff",
                  border:"1px solid #cbd5e1",
                  color:"#111827",
                  WebkitTextFillColor:"#111827",
                  colorScheme:"light"
                }}
              >
                <option value="">Todos os operadores</option>
                {(data?.operators || []).map(op => <option key={op} value={op}>{op}</option>)}
              </select>
              <button
                onClick={printReceipt}
                className="py-2 rounded-xl font-semibold"
                style={{background:"#0f172a",color:"#fff"}}
              >
                Imprimir Relatório
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ['Abertura', s.opening],
                ['Entradas', s.cash_in],
                ['Saídas', s.cash_out],
                ['Conta Corrente', s.account_current],
                ['Vendas Dinheiro', s.cash],
                ['Cartão', s.card],
                ['MBWay', s.mbway],
                ['Multibanco', s.multibanco],
                ['Transferência', s.transfer],
                ['Vouchers', s.voucher],
                ['Outros', s.other],
                ['Esperado em Caixa', s.expected_cash],
                ['Contado', s.counted_cash],
                ['Diferença', s.difference]
              ].map(([label, val]) => {
                const strong = ['Esperado em Caixa', 'Contado', 'Diferença'].includes(label);
                const negative = label === 'Saídas';

                return (
                  <div
                    key={label}
                    className="rounded-xl p-4 shadow-sm"
                    style={{background:'#fff', border:'1px solid #cbd5e1'}}
                  >
                    <p className="text-slate-600 text-sm font-medium">{label}</p>
                    <p className={`mt-1 text-2xl font-bold ${strong ? 'text-slate-900' : 'text-slate-800'} ${negative ? '!text-rose-600' : ''}`}>
                      €{Number(val || 0).toFixed(2)}
                    </p>
                  </div>
                );
              })}
            </div>

          <div className="rounded-xl overflow-hidden" style={{background:'#ffffff',border:'1px solid #94a3b8',boxShadow:'0 1px 3px rgba(15,23,42,0.06)',color:'#0f172a',WebkitTextFillColor:'#0f172a'}}>
            <table className="w-full text-sm" style={{background:'#ffffff',color:'#0f172a',WebkitTextFillColor:'#0f172a',borderCollapse:'collapse'}}>
              <thead style={{background:'#e2e8f0'}}>
                <tr style={{textAlign:'left',color:'#334155'}}>
                  <th className="px-3 py-2" style={{color:'#334155',WebkitTextFillColor:'#334155',fontWeight:700,borderBottom:'1px solid #cbd5e1'}}>Hora</th>
                  <th className="px-3 py-2" style={{color:'#334155',WebkitTextFillColor:'#334155',fontWeight:700,borderBottom:'1px solid #cbd5e1'}}>Tipo</th>
                  <th className="px-3 py-2" style={{color:'#334155',WebkitTextFillColor:'#334155',fontWeight:700,borderBottom:'1px solid #cbd5e1'}}>Operador</th>
                  <th className="px-3 py-2" style={{color:'#334155',WebkitTextFillColor:'#334155',fontWeight:700,borderBottom:'1px solid #cbd5e1'}}>Justificativo</th>
                  <th className="px-3 py-2 text-right" style={{color:'#334155',WebkitTextFillColor:'#334155',fontWeight:700,borderBottom:'1px solid #cbd5e1'}}>Montante</th>
                </tr>
              </thead>
              <tbody style={{background:'#ffffff'}}>
                {loading ? (
                  <tr style={{background:'#ffffff'}}>
                    <td colSpan="5" className="px-3 py-6 text-center" style={{color:'#475569',WebkitTextFillColor:'#475569',background:'#ffffff'}}>A carregar...</td>
                  </tr>
                ) : (data?.movements || []).length === 0 ? (
                  <tr style={{background:'#ffffff'}}>
                    <td colSpan="5" className="px-3 py-6 text-center" style={{color:'#475569',WebkitTextFillColor:'#475569',background:'#ffffff'}}>Sem movimentos neste filtro</td>
                  </tr>
                ) : (
                  (data?.movements || []).map((m, idx)=>(
                    <tr key={m.id} style={{borderTop:'1px solid #cbd5e1',background: idx % 2 === 0 ? '#ffffff' : '#f8fafc'}}>
                      <td className="px-3 py-2" style={{color:'#334155',WebkitTextFillColor:'#334155',fontWeight:500}}>
                        {m.created_at ? new Date(m.created_at).toLocaleTimeString('pt-PT', {hour:'2-digit', minute:'2-digit'}) : ''}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold"
                          style={
                            m.type === 'open'
                              ? {background:'rgba(34,197,94,0.12)',color:'#166534',WebkitTextFillColor:'#166534'}
                              : m.type === 'in'
                              ? {background:'rgba(37,99,235,0.12)',color:'#1d4ed8',WebkitTextFillColor:'#1d4ed8'}
                              : m.type === 'out'
                              ? {background:'rgba(234,88,12,0.12)',color:'#c2410c',WebkitTextFillColor:'#c2410c'}
                              : m.type === 'close'
                              ? {background:'rgba(15,23,42,0.10)',color:'#0f172a',WebkitTextFillColor:'#0f172a'}
                              : {background:'rgba(100,116,139,0.12)',color:'#475569',WebkitTextFillColor:'#475569'}
                          }
                        >
                          {m.type === 'open' ? 'Abertura' : m.type === 'in' ? 'Entrada' : m.type === 'out' ? 'Saída' : m.type === 'close' ? 'Fecho' : m.type}
                        </span>
                      </td>
                      <td className="px-3 py-2" style={{color:'#334155',WebkitTextFillColor:'#334155',fontWeight:500}}>{m.operator}</td>
                      <td className="px-3 py-2" style={{color:'#475569',WebkitTextFillColor:'#475569',fontWeight:500}}>{m.note || '—'}</td>
                      <td className="px-3 py-2 text-right" style={{color:'#0f172a',WebkitTextFillColor:'#0f172a',fontWeight:700}}>€{Number(m.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const CashCloseReceiptModal = ({ open, onClose, data }) => {
  if (!open || !data) return null;

  const s = data?.summary || {};
  const movement = data?.movement || {};
  const operator = data?.operator || '';
  const createdAt = data?.created_at ? new Date(data.created_at) : new Date();

  const rows = [
    ['Abertura', s.opening || 0],
    ['Entradas', s.cash_in || 0],
    ['Saídas', s.cash_out || 0],
    ['Conta Corrente', s.account_current || 0],
    ['Vendas Dinheiro', s.cash || 0],
    ['Cartão', s.card || 0],
    ['MBWay', s.mbway || 0],
    ['Multibanco', s.multibanco || 0],
    ['Transferência', s.transfer || 0],
    ['Vouchers', s.voucher || 0],
    ['Outros', s.other || 0],
    ['Esperado em Caixa', data?.expected_cash || 0],
    ['Contado', data?.counted_cash || 0],
    ['Diferença', data?.difference || 0],
  ];

  const highlightRowLabels = new Set(['Esperado em Caixa', 'Contado', 'Diferença']);
  const negativeRowLabels = new Set(['Saídas']);

  const receiptHtml = `
    <!doctype html>
    <html lang="pt">
      <head>
        <meta charset="utf-8" />
        <title>Fecho de Caixa</title>
        <style>
          body {
            margin: 0;
            font-family: Arial, Helvetica, sans-serif;
            background: #fff;
            color: #111827;
          }
          .receipt {
            width: 80mm;
            margin: 0 auto;
            padding: 4mm;
            box-sizing: border-box;
          }
          .center { text-align: center; }
          .title { font-size: 16px; font-weight: 700; margin-bottom: 4px; }
          .sub { font-size: 11px; color: #475569; margin-bottom: 2px; }
          .line {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            font-size: 12px;
            padding: 3px 0;
            border-bottom: 1px dashed #cbd5e1;
          }
          .line.strong {
            font-weight: 700;
            color: #0f172a;
          }
          .section {
            margin-top: 8px;
            margin-bottom: 8px;
          }
          .footer {
            margin-top: 10px;
            text-align: center;
            font-size: 10px;
            color: #64748b;
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="center">
            <div class="title">Fecho de Caixa</div>
            <div class="sub">${createdAt.toLocaleDateString('pt-PT')} ${createdAt.toLocaleTimeString('pt-PT', {hour:'2-digit', minute:'2-digit'})}</div>
            <div class="sub">Operador: ${operator}</div>
          </div>

          <div class="section">
            ${rows.map(([label, val]) => `
              <div class="line ${['Esperado em Caixa','Contado','Diferença'].includes(label) ? 'strong' : ''}">
                <span>${label}</span>
                <span>€${Number(val || 0).toFixed(2)}</span>
              </div>
            `).join('')}
          </div>

          <div class="section">
            <div class="line strong">
              <span>Resultado</span>
              <span>${Number(data?.difference || 0) === 0 ? 'Caixa certa' : Number(data?.difference || 0) > 0 ? 'Sobra' : 'Falta'}</span>
            </div>
            <div class="line">
              <span>Justificativo</span>
              <span>${movement?.note || '—'}</span>
            </div>
          </div>

          <div class="footer">FamaPOS · Talão de Fecho de Caixa</div>
        </div>
      </body>
    </html>
  `;

  const openPrintWindow = () => {
    const w = window.open('', '_blank', 'width=420,height=800');
    if (!w) return;
    w.document.open();
    w.document.write(receiptHtml);
    w.document.close();
  };

  const handlePrint = () => {
    const w = window.open('', '_blank', 'width=420,height=800');
    if (!w) return;
    w.document.open();
    w.document.write(receiptHtml.replace('</body>', '<script>window.onload = () => window.print();</script></body>'));
    w.document.close();
  };

  const handleDownload = () => {
    const blob = new Blob([receiptHtml], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fecho-caixa-${createdAt.toISOString().slice(0,19).replace(/[:T]/g,'-')}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{background:'rgba(0,0,0,0.75)'}} onClick={onClose}>
        <div className="rounded-2xl w-[380px] max-h-[90vh] overflow-y-auto" style={{background:'#f8fafc',border:'1px solid #e2e8f0'}} onClick={e=>e.stopPropagation()}>
          <div className="flex items-center justify-between p-4 border-b" style={{borderColor:'#e2e8f0'}}>
            <div>
              <p className="text-slate-900 text-base font-bold">Talão de Fecho</p>
              <p className="text-slate-700 text-sm font-medium">Pré-visualização 80 mm</p>
            </div>
            <button onClick={onClose} className="text-slate-600 hover:text-slate-900"><X className="w-5 h-5"/></button>
          </div>

          <div className="p-4">
            <div className="mx-auto bg-white rounded-xl shadow-sm" style={{width:'92mm', minHeight:'160mm', border:'1px solid #cbd5e1'}}>
              <div className="p-4 text-[12px]" style={{color:'#111827'}}>
                <div className="text-center mb-3">
                  <div className="text-[16px] font-bold">Fecho de Caixa</div>
                  <div className="text-sm font-medium text-slate-600">{createdAt.toLocaleDateString('pt-PT')} {createdAt.toLocaleTimeString('pt-PT', {hour:'2-digit', minute:'2-digit'})}</div>
                  <div className="text-sm font-medium text-slate-600">Operador: {operator}</div>
                </div>

                <div className="space-y-1">
                  {rows.map(([label, val]) => {
                    const strong = highlightRowLabels.has(label);
                    const negative = negativeRowLabels.has(label);

                    return (
                      <div key={label} className="flex items-center justify-between border-b border-dashed py-1.5" style={{borderColor:'#cbd5e1'}}>
                        <span className={strong ? 'font-bold text-slate-900' : 'text-slate-700'}>{label}</span>
                        <span className={`${strong ? 'font-bold text-slate-900' : 'text-slate-700'} ${negative ? '!text-rose-600 font-bold' : ''}`}>
                          €{Number(val || 0).toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-3 pt-3 border-t" style={{borderColor:'#cbd5e1'}}>
                  <div
                    className="flex items-center justify-between rounded-lg px-3 py-2"
                    style={{
                      background: Number(data?.difference || 0) === 0 ? '#ecfdf5' : Number(data?.difference || 0) > 0 ? '#eff6ff' : '#fff1f2',
                      border: Number(data?.difference || 0) === 0 ? '1px solid #a7f3d0' : Number(data?.difference || 0) > 0 ? '1px solid #bfdbfe' : '1px solid #fecdd3'
                    }}
                  >
                    <span className="font-bold text-slate-900">Resultado</span>
                    <span className="font-bold text-slate-900">
                      {Number(data?.difference || 0) === 0 ? 'Caixa certa' : Number(data?.difference || 0) > 0 ? 'Sobra' : 'Falta'}
                    </span>
                  </div>
                  <div className="mt-3 text-slate-600 text-[11px]">Justificativo: {movement?.note || '—'}</div>
                </div>

                <div className="mt-4 text-center text-[10px] text-slate-500">
                  FamaPOS · Talão de Fecho de Caixa
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              <button onClick={openPrintWindow} className="py-2 rounded-xl font-semibold text-sm" style={{background:'#e2e8f0',color:'#0f172a'}}>Abrir</button>
              <button onClick={handlePrint} className="py-2 rounded-xl font-semibold text-sm" style={{background:'#0f172a',color:'#ffffff'}}>Imprimir</button>
              <button onClick={handleDownload} className="py-2 rounded-xl font-semibold text-sm" style={{background:'#334155',color:'#ffffff'}}>Descarregar</button>
            </div>
          </div>
        </div>
      </div>
    );
};

const POS_DRAFT_KEY = 'ervapos_pos_draft_v1';
const POS_LOCK_KEY = 'ervapos_pos_locked_v1';
const POS_LOCK_REASON_KEY = 'ervapos_pos_lock_reason_v1';
const POS_LOCK_EMAIL_KEY = 'ervapos_pos_lock_email_v1';

const UnclosedCashAlertModal = ({ open, onClose, data, onOpenHistory, onOpenClose }) => {
  if (!open || !data?.has_unclosed_previous_day) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{background:'rgba(0,0,0,0.72)'}} onClick={onClose}>
      <div className="rounded-2xl w-[460px] max-w-[92vw]" style={{background:'#f8fafc',border:'1px solid #e2e8f0'}} onClick={e=>e.stopPropagation()}>
        <div className="p-5 border-b" style={{borderColor:'#e2e8f0'}}>
          <p className="text-slate-900 text-base font-bold">Caixa anterior por fechar</p>
          <p className="text-slate-600 text-sm mt-1">{data?.message || 'Existe uma caixa anterior por regularizar.'}</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-xl p-4" style={{background:'#ffffff',border:'1px solid #cbd5e1'}}>
            <p className="text-slate-700 text-sm">
              Antes de continuar, convém confirmar o histórico e proceder ao fecho correspondente.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <button onClick={onOpenHistory} className="py-2 rounded-xl font-semibold text-sm" style={{background:'#e2e8f0',color:'#0f172a'}}>Histórico</button>
            <button onClick={onOpenClose} className="py-2 rounded-xl font-semibold text-sm" style={{background:'#0f172a',color:'#ffffff'}}>Abrir Fecho</button>
            <button onClick={onClose} className="py-2 rounded-xl font-semibold text-sm" style={{background:'#334155',color:'#ffffff'}}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const normalizeSearchText = (value) => {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

export const POSPage = () => {
  const navigate = useNavigate();
  const { user, logout, isManager } = useAuth();
  const { brandName, logoUrl } = useFamaBranding();
  const searchInputRef = useRef(null);
  const posSyncRevisionRef = useRef(null);
  const posSyncCheckingRef = useRef(false);

  const scannerBufferRef = useRef('');
  const scannerLastKeyAtRef = useRef(0);
  const scannerClearTimerRef = useRef(null);
  const handleScanRef = useRef(null);
  const lastOrderVerificationScanRef = useRef({ code: '', at: 0 });
  const lastCustomerOrderOpenRef = useRef({ code: '', at: 0 });

  // State
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [currentSale, setCurrentSale] = useState(null);
  const [suspendedSales, setSuspendedSales] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showInvoiceIdentityChoice, setShowInvoiceIdentityChoice] = useState(false);
  useEffect(() => {
    if (selectedCustomer && typeof selectedCustomer.invoiceWithNif !== 'boolean') setShowInvoiceIdentityChoice(true);
  }, [selectedCustomer]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerToComplete, setCustomerToComplete] = useState(null);
  const [showCustomerCompletionModal, setShowCustomerCompletionModal] = useState(false);
  const [savingCustomerCompletion, setSavingCustomerCompletion] = useState(false);
  const [customerCompletionForm, setCustomerCompletionForm] = useState({
    name: '', phone: '', nif: '', email: '', address: '', city: '', postal_code: '',
  });

  // Resolve por telefone (familiares com o mesmo número) — pergunta sempre
  const [phoneResolveOpen, setPhoneResolveOpen] = useState(false);
  const [phoneResolvePhone, setPhoneResolvePhone] = useState('');
  const [phoneResolveMatches, setPhoneResolveMatches] = useState([]);



  const resolveAndSelectByPhone = async (phone, fallbackCustomer = null) => {
    try {
      const res = await customersAPI.resolveByPhone(phone);
      const data = res?.data || {};
      const matches = data.matches || [];
      const count = data.count ?? matches.length;

      // 0 ou 1 resultado: selecciona directo
      if (count <= 1) {
        const chosen = matches[0] || fallbackCustomer;
        selectCustomerWithCompletion(chosen || null);
        return true;
      }

      // vários: abre modal e tu escolhes (não memoriza escolha)
      setPhoneResolvePhone(data.phone || phone);
      setPhoneResolveMatches(matches);
      setPhoneResolveOpen(true);
      return false;
    } catch (e) {
      // se falhar, não bloqueia: selecciona fallback
      selectCustomerWithCompletion(fallbackCustomer || null);
      return true;
    }
  };
  const [customers, setCustomers] = useState([]);
  const [providers, setProviders] = useState([]);
  const activeProviders = (providers || []).filter((provider) => provider?.is_active !== false);

  const missingCustomerBasics = (customer) => {
    if (!customer) return [];
    const missing = [];
    if (String(customer.name || '').trim().split(/\s+/).filter(Boolean).length < 2) missing.push('nome completo');
    if (!String(customer.phone || '').trim()) missing.push('telefone');
    if (!String(customer.nif || '').trim()) missing.push('NIF');
    return missing;
  };

  const selectCustomerWithCompletion = (customer) => {
    if (!customer) {
      setSelectedCustomer(null);
      return;
    }
    if (!customer.id || missingCustomerBasics(customer).length === 0) {
      setSelectedCustomer(customer);
      return;
    }
    setCustomerToComplete(customer);
    setCustomerCompletionForm({
      name: customer.name || '',
      phone: customer.phone || '',
      nif: customer.nif || '',
      email: customer.email || '',
      address: customer.address || '',
      city: customer.city || '',
      postal_code: customer.postal_code || '',
    });
    setShowCustomerModal(false);
    setPhoneResolveOpen(false);
    setShowCustomerCompletionModal(true);
  };

  const saveAndSelectCompletedCustomer = async () => {
    const name = String(customerCompletionForm.name || '').trim();
    const phone = String(customerCompletionForm.phone || '').trim();
    const nif = String(customerCompletionForm.nif || '').trim();
    setSavingCustomerCompletion(true);
    try {
      const original = customerToComplete || {};
      const payload = {
        name,
        phone,
        nif,
        email: String(customerCompletionForm.email || '').trim() || null,
        address: String(customerCompletionForm.address || '').trim() || null,
        city: String(customerCompletionForm.city || '').trim() || null,
        postal_code: String(customerCompletionForm.postal_code || '').trim() || null,
        country: original.country || 'PT',
        website: original.website || null,
        notes: original.notes || null,
        is_active: original.is_active !== false,
      };
      const response = await customersAPI.update(original.id, payload);
      const updated = response.data;
      setCustomers((current) => current.map((customer) => customer.id === updated.id ? updated : customer));
      setSelectedCustomer(updated);
      setCustomerToComplete(null);
      setShowCustomerCompletionModal(false);
      setCustomerSearch('');
      toast.success('Ficha do cliente atualizada e selecionada');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível atualizar a ficha do cliente');
    } finally {
      setSavingCustomerCompletion(false);
    }
  };

  const filteredCustomers = (customers || []).filter((customer) => {
    const q = normalizeSearchText(customerSearch);
    if (!q) return true;

    const name = normalizeSearchText(customer?.name);
    const phone = normalizeSearchText(customer?.phone);
    const email = normalizeSearchText(customer?.email);
    const nif = normalizeSearchText(customer?.nif);

    return (
      name.includes(q) ||
      phone.includes(q) ||
      email.includes(q) ||
      nif.includes(q)
    );
  });
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [availableVouchers, setAvailableVouchers] = useState([]);

  // Modals
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCustomerConfirmationModal, setShowCustomerConfirmationModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showSuspendedModal, setShowSuspendedModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [showCustomerVoucherPrompt, setShowCustomerVoucherPrompt] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedServiceForProvider, setSelectedServiceForProvider] = useState(null);
  const [productBatches, setProductBatches] = useState([]);
  const [editingCartIndex, setEditingCartIndex] = useState(null);
  const [discountValue, setDiscountValue] = useState(0);
  const [discountType, setDiscountType] = useState('percent'); // 'percent' or 'fixed'
  const [itemNotes, setItemNotes] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [lastRemovedItem, setLastRemovedItem] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const receiptPreviewRef = useRef(null);
  const [showGeneratedVoucherModal, setShowGeneratedVoucherModal] = useState(false);
  const [pendingOpenGeneratedVoucher, setPendingOpenGeneratedVoucher] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [completedSaleId, setCompletedSaleId] = useState(null);
  const [isFiscalSale, setIsFiscalSale] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [sendingReceiptEmail, setSendingReceiptEmail] = useState(false);
  const [receiptPhone, setReceiptPhone] = useState('');
  const [sendingReceiptSms, setSendingReceiptSms] = useState(false);
  const [receiptPdfUrl, setReceiptPdfUrl] = useState('');
  const [receiptPdfLoading, setReceiptPdfLoading] = useState(false);
  const [convertingOrderMetaState, setConvertingOrderMetaState] = useState(null);

  const [pendingOrderVerification, setPendingOrderVerification] = useState(null);
  const [showOrderVerificationModal, setShowOrderVerificationModal] = useState(false);
  const [orderVerificationInput, setOrderVerificationInput] = useState('');
  const [verifiedOrderItems, setVerifiedOrderItems] = useState([]);
  const [verifyingOrderScan, setVerifyingOrderScan] = useState(false);

  // Estado edição item carrinho + calculadora
  const [editingItem, setEditingItem] = useState(null); // {index, field:'price'|'qty', value:''}
  // Estado caixa
  const [showCashModal, setShowCashModal] = useState(false);
  const [cashModalType, setCashModalType] = useState('open'); // 'open'|'in'|'out'|'close'
  const [prepaymentsSummary, setPrepaymentsSummary] = useState(null);
  const [cashAmount, setCashAmount] = useState('');
  const [cashNote, setCashNote] = useState('');
  const [cashSessionOpen, setCashSessionOpen] = useState(false);
  const [showCashHistoryModal, setShowCashHistoryModal] = useState(false);
  const [showQuickSalesHistoryModal, setShowQuickSalesHistoryModal] = useState(false);
  const [showCustomerHistoryModal, setShowCustomerHistoryModal] = useState(false);
  const [quickSalesHistory, setQuickSalesHistory] = useState([]);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistorySale, setSelectedHistorySale] = useState(null);
  const [showHistorySaleDetailModal, setShowHistorySaleDetailModal] = useState(false);
  const [historyEmail, setHistoryEmail] = useState('');
  const [historyPhone, setHistoryPhone] = useState('');
  const [sendingHistoryEmail, setSendingHistoryEmail] = useState(false);
  const [showCashCloseReceiptModal, setShowCashCloseReceiptModal] = useState(false);
  const [cashCloseReceiptData, setCashCloseReceiptData] = useState(null);
  const [showUnclosedCashAlert, setShowUnclosedCashAlert] = useState(false);
  const [unclosedCashAlertData, setUnclosedCashAlertData] = useState(null);

  // Top selling - guardado em localStorage
  const [topSelling, setTopSelling] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pos_top_selling') || '{}'); } catch { return {}; }
  });

  const [posLocked, setPosLocked] = useState(() => sessionStorage.getItem(POS_LOCK_KEY) === '1');
  const [lockReason, setLockReason] = useState(() => sessionStorage.getItem(POS_LOCK_REASON_KEY) || 'manual');
  const [lockUsers, setLockUsers] = useState([]);
  const [selectedLockEmail, setSelectedLockEmail] = useState(() => sessionStorage.getItem(POS_LOCK_EMAIL_KEY) || '');
  const [lockSecret, setLockSecret] = useState('');
  const [unlockingPOS, setUnlockingPOS] = useState(false);
  const [showUserSecurityModal, setShowUserSecurityModal] = useState(false);
  const posIdleTimerRef = useRef(null);

  // Payment state
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [invoiceType, setInvoiceType] = useState('internal');
  const [processing, setProcessing] = useState(false);
  const [mbwayPhone, setMbwayPhone] = useState('');
  const [mbwayWaiting, setMbwayWaiting] = useState(false);
  const [cashReceived, setCashReceived] = useState('');
  const [showCashKeypad, setShowCashKeypad] = useState(false);
  const [cashKeypadValue, setCashKeypadValue] = useState('');
  const [generatedVoucherAfterSale, setGeneratedVoucherAfterSale] = useState(null);
  const [residualVoucherAfterSale, setResidualVoucherAfterSale] = useState(null);
  const [sendingVoucherEmail, setSendingVoucherEmail] = useState(false);
  const [useCustomerPoints, setUseCustomerPoints] = useState(false);
  const [pointsToUse, setPointsToUse] = useState(0);
  const [pointsValueConfig, setPointsValueConfig] = useState(0.01);
  const [mixedPrimaryMethod, setMixedPrimaryMethod] = useState('cash');
  const [mixedSecondaryMethod, setMixedSecondaryMethod] = useState('card');
  const [mixedPrimaryAmount, setMixedPrimaryAmount] = useState('');
    const [accountCurrentPaidNow, setAccountCurrentPaidNow] = useState('');
    const [accountCurrentPaidMethod, setAccountCurrentPaidMethod] = useState('cash');

  const normalizePhoneForMbway = (value) => {
    return String(value || '').replace(/[^\d+]/g, '').trim();
  };

  const clearPOSIdleTimer = useCallback(() => {
    if (posIdleTimerRef.current) {
      clearTimeout(posIdleTimerRef.current);
      posIdleTimerRef.current = null;
    }
  }, []);

  const openPOSLock = useCallback((reason = 'manual') => {
    const fallbackEmail = selectedLockEmail || user?.email || '';
    setLockReason(reason);
    setLockSecret('');
    setSelectedLockEmail(fallbackEmail);
    setPosLocked(true);

    sessionStorage.setItem(POS_LOCK_KEY, '1');
    sessionStorage.setItem(POS_LOCK_REASON_KEY, reason);
    if (fallbackEmail) sessionStorage.setItem(POS_LOCK_EMAIL_KEY, fallbackEmail);
  }, [selectedLockEmail, user]);

  const resetPOSIdleTimer = useCallback(() => {
    clearPOSIdleTimer();
    if (posLocked) return;
    posIdleTimerRef.current = setTimeout(() => {
      setLockReason('inactive');
      setLockSecret('');
      setPosLocked(true);
    }, 5 * 60 * 1000);
  }, [clearPOSIdleTimer, posLocked]);

  const handlePOSLogout = useCallback(() => {
    sessionStorage.removeItem(POS_LOCK_KEY);
    sessionStorage.removeItem(POS_LOCK_REASON_KEY);
    sessionStorage.removeItem(POS_LOCK_EMAIL_KEY);
    logout();
    navigate('/login');
  }, [logout, navigate]);


  useEffect(() => {
    if (posLocked) {
      sessionStorage.setItem(POS_LOCK_KEY, '1');
      sessionStorage.setItem(POS_LOCK_REASON_KEY, lockReason || 'manual');
      if (selectedLockEmail) sessionStorage.setItem(POS_LOCK_EMAIL_KEY, selectedLockEmail);
    } else {
      sessionStorage.removeItem(POS_LOCK_KEY);
      sessionStorage.removeItem(POS_LOCK_REASON_KEY);
    }
  }, [posLocked, lockReason, selectedLockEmail]);

const handleUnlockPOS = useCallback(async () => {
    if (!selectedLockEmail) {
      toast.error('Selecione um utilizador');
      return;
    }

    if (!String(lockSecret || '').trim()) {
      toast.error('Introduza o PIN');
      return;
    }

    try {
      setUnlockingPOS(true);

      const token = localStorage.getItem('ervapos_token') || '';
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/pos-pin/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: selectedLockEmail,
          pin: lockSecret,
        }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        data = {};
      }

      console.log('POS PIN VERIFY STATUS:', res.status);
      console.log('POS PIN VERIFY DATA:', data);

      if (!res.ok || !data?.ok) {
        toast.error(data?.detail || 'PIN inválido');
        return;
      }

      setPosLocked(false);
      setLockSecret('');
      setLockReason('manual');

      sessionStorage.removeItem(POS_LOCK_KEY);
      sessionStorage.removeItem(POS_LOCK_REASON_KEY);
      sessionStorage.setItem(POS_LOCK_EMAIL_KEY, selectedLockEmail);

      toast.success('POS desbloqueado');
    } catch (error) {
      console.error('POS PIN UNLOCK ERROR:', error);
      toast.error('Não foi possível desbloquear o POS');
    } finally {
      setUnlockingPOS(false);
    }
  }, [selectedLockEmail, lockSecret]);

  useEffect(() => {
    let cancelled = false;

    async function loadPOSLockUsers() {
      if (!user) return;

      const fallback = [{
        id: user?.id || 'me',
        name: user?.name || user?.email || 'Utilizador',
        email: user?.email || '',
        role: user?.role || '',
      }].filter((u) => u.email);

      try {
        if (isManager) {
          const res = await authAPI.getPosUsers();
          const rows = Array.isArray(res?.data) ? res.data : [];
          const active = rows
            .filter((u) => u?.is_active !== false && u?.email)
            .map((u) => ({
              id: u.id,
              name: u.name || u.email,
              email: u.email,
              role: u.role || '',
            }));

          const finalUsers = active.length ? active : fallback;
          if (!cancelled) {
            setLockUsers(finalUsers);
            setSelectedLockEmail((prev) => prev || user?.email || finalUsers[0]?.email || '');
          }
          return;
        }

        if (!cancelled) {
          setLockUsers(fallback);
          setSelectedLockEmail((prev) => prev || user?.email || fallback[0]?.email || '');
        }
      } catch {
        if (!cancelled) {
          setLockUsers(fallback);
          setSelectedLockEmail((prev) => prev || user?.email || fallback[0]?.email || '');
        }
      }
    }

    loadPOSLockUsers();
    return () => { cancelled = true; };
  }, [user, isManager]);

  useEffect(() => {
    if (!user) return;

    const activityHandler = () => {
      if (!posLocked) resetPOSIdleTimer();
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((evt) => window.addEventListener(evt, activityHandler, true));

    if (!posLocked) resetPOSIdleTimer();

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, activityHandler, true));
      clearPOSIdleTimer();
    };
  }, [user, posLocked, resetPOSIdleTimer, clearPOSIdleTimer]);

  const customerPointsBalance = Math.max(
    0,
    Number(
      selectedCustomer?.points ??
      customers.find((c) => c.id === selectedCustomer?.id)?.points ??
      0
    )
  );

  useEffect(() => {
    if (selectedCustomer?.id) return;
    setCart(prev => prev.map(item => item.points_redemption_campaign_id ? {
      ...item,
      discount: Number(item.points_redemption_previous_discount || 0),
      discount_percent: Number(item.points_redemption_previous_discount_percent || 0),
      total: Number(item.quantity || 0) * Number(item.unit_price || 0) - Number(item.points_redemption_previous_discount || 0),
      points_redemption_campaign_id: null,
      points_redemption_points: 0,
      points_redemption_campaign_name: '',
    } : item));
  }, [selectedCustomer?.id]);

  useEffect(() => {
    if (paymentMethod !== 'mbway') return;

    const candidate =
      selectedCustomer?.phone ||
      convertingOrderMetaState?.customer_phone ||
      '';

    if (candidate && !String(mbwayPhone || '').trim()) {
      setMbwayPhone(normalizePhoneForMbway(candidate));
    }
  }, [paymentMethod, selectedCustomer, convertingOrderMetaState, mbwayPhone]);

  useEffect(() => {
    let active = true;

    const loadLoyaltySettings = async () => {
      try {
        const res = await settingsAPI.getPosConfig();
        const value = Number(res?.data?.points_value ?? 0.01);
        if (active && Number.isFinite(value) && value > 0) {
          setPointsValueConfig(value);
        }
      } catch (e) {
        console.error('Erro ao carregar settings de fidelização:', e);
      }
    };

    loadLoyaltySettings();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    setUseCustomerPoints(false);
    setPointsToUse(0);
    setMixedPrimaryMethod('cash');
    setMixedSecondaryMethod('card');
    setMixedPrimaryAmount('');
      setAccountCurrentPaidNow('');
      setAccountCurrentPaidMethod('cash');
  }, [selectedCustomer?.id]);

  const clearPosDraft = () => {
    try {
      localStorage.removeItem(POS_DRAFT_KEY);
    } catch {}
  };
  const mbwayRecoveryKey = `famapos_mbway_sale_${user?.id || 'unknown'}`;
  // This pointer is saved synchronously BEFORE asking the provider to charge.
  // It is deliberately independent of the debounced cart draft.
  const rememberMbwaySale = (sale) => {
    localStorage.setItem(mbwayRecoveryKey, sale.id);
    setCurrentSale(sale);
  };
  const savePosDraft = useCallback(() => {
    try {
      const draft = {
        cart,
        selectedCustomer,
        paymentMethod,
        invoiceType,
        appliedVoucher,
        cashReceived,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(POS_DRAFT_KEY, JSON.stringify(draft));
    } catch {}
  }, [cart, selectedCustomer, paymentMethod, invoiceType, appliedVoucher, cashReceived]);

  const restorePosDraft = () => {
    try {
      const raw = localStorage.getItem(POS_DRAFT_KEY);
      if (!raw) return false;

      const draft = JSON.parse(raw);
      if (!draft || typeof draft !== 'object') return false;

      if (Array.isArray(draft.cart)) setCart(draft.cart);
      if (draft.selectedCustomer) setSelectedCustomer(draft.selectedCustomer);
      if (draft.paymentMethod) setPaymentMethod(draft.paymentMethod);
      if (draft.invoiceType) setInvoiceType(draft.invoiceType);
      if (draft.appliedVoucher) setAppliedVoucher(draft.appliedVoucher);
      if (typeof draft.cashReceived === 'string') setCashReceived(draft.cashReceived);

      if (Array.isArray(draft.cart) && draft.cart.length > 0) {
        toast.info('Venda em curso recuperada automaticamente');
      }
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    const saleId = localStorage.getItem(mbwayRecoveryKey);
    if (!saleId) return;
    salesAPI.getById(saleId).then(({ data: sale }) => {
      if (!active) return;
      if (sale.operator_id !== user.id) throw new Error('Operador diferente');
      if (sale.status === 'cancelled' || sale.vendus_invoice_id) {
        localStorage.removeItem(mbwayRecoveryKey);
        return;
      }
      setCurrentSale(sale);
      setCart((sale.items || []).map(item => ({ ...item, max_quantity: item.quantity })));
      if (sale.invoice_type) setInvoiceType(sale.invoice_type);
      setPaymentMethod('mbway');
      setMbwayPhone(sale.payment_phone || '');
      setSelectedCustomer(sale.customer_id ? { id: sale.customer_id, name: sale.customer_name,
        phone: sale.customer_phone, nif: sale.customer_nif, invoiceWithNif: sale.invoice_with_nif === true } : null);
      toast.info('Venda MB WAY recuperada. Ao fechar, será verificado o pedido existente, sem nova cobrança.');
    }).catch(() => {
      if (active) toast.error('Não foi possível recuperar a venda MB WAY. Não peça novo pagamento; tente novamente quando a ligação voltar.');
    });
    return () => { active = false; };
  }, [user?.id]);

  // Load data
  useEffect(() => {
    loadInitialData();
    loadCashSessionStatus();
    loadUnclosedCashAlert();
    checkForOrderToConvert();
    restorePosDraft();
    // Focus search on mount
    searchInputRef.current?.focus();
  }, []);

  // Check if there's an order to convert from sessionStorage
  const checkForOrderToConvert = () => {
    const orderDataStr = sessionStorage.getItem('orderToConvert');
    if (orderDataStr) {
      try {
        const orderData = JSON.parse(orderDataStr);
        sessionStorage.removeItem('orderToConvert');

        if (orderData.items && orderData.items.length > 0) {
          setPendingOrderVerification(orderData);
          setVerifiedOrderItems([]);
          setOrderVerificationInput('');
          setShowOrderVerificationModal(true);

          toast.info(`Encomenda ${orderData.order_number} pronta para confirmação de artigos`);
        }
      } catch (error) {
        console.error('Error parsing order data:', error);
      }
    }
  };

  const recalcOrderCartLine = (item) => {
    const quantity = Number(item?.quantity || 0);
    const unitPrice = Number(item?.unit_price || 0);
    const discountPercent = Number(item?.discount_percent || 0);
    const grossTotal = quantity * unitPrice;
    const discountValue = Number(((grossTotal * discountPercent) / 100).toFixed(2));
    const finalTotal = Number((grossTotal - discountValue).toFixed(2));

    return {
      ...item,
      quantity,
      unit_price: unitPrice,
      discount_percent: discountPercent,
      discount: discountValue,
      total: finalTotal,
    };
  };

  const handleOrderVerificationScan = async (rawCode) => {
    const code = String(rawCode || '')
      .replace(/[\r\n\t]+/g, '')
      .trim();

    const normalizedCode = code.replace(/\s+/g, '');

    if (!code || !pendingOrderVerification) return;

    const dedupeCode = normalizedCode || code;
    const now = Date.now();
    if (
      lastOrderVerificationScanRef.current.code === dedupeCode &&
      (now - Number(lastOrderVerificationScanRef.current.at || 0)) < 900
    ) {
      return;
    }
    lastOrderVerificationScanRef.current = { code: dedupeCode, at: now };

    try {
      setVerifyingOrderScan(true);

      let batch = null;
      let product = null;

      const qrCandidates = Array.from(new Set([
        code,
        normalizedCode,
        normalizedCode.replace(/Ç/g, ':'),
        normalizedCode.replace(/'/g, '-'),
        normalizedCode.replace(/Ç/g, ':').replace(/'/g, '-'),
      ])).filter(Boolean);

      for (const qrCode of qrCandidates) {
        try {
          const response = await batchesAPI.getByQR(qrCode);
          if (response?.data) {
            const payload = response.data || {};
            batch = payload.batch || payload || null;
            product =
              payload.product ||
              batch?.product ||
              products.find(
                (p) => String(p.id || '').trim() === String(batch?.product_id || '').trim()
              ) ||
              null;

            if (batch && product) break;
          }
        } catch (_) {
          // tenta o formato seguinte
        }
      }

      // O leitor também pode enviar o código comercial da embalagem. Nesse
      // caso resolve o produto como no POS normal e só escolhe lote quando a
      // decisão é inequívoca.
      if (!batch || !product) {
        const scanned = normalizedCode.toLowerCase();
        product = products.find((candidate) => {
          const barcode = String(candidate?.barcode || '').replace(/\s+/g, '').toLowerCase();
          const reference = String(candidate?.reference || '').replace(/\s+/g, '').toLowerCase();
          return scanned && (barcode === scanned || reference === scanned);
        }) || null;

        if (product) {
          const response = await batchesAPI.getAll({ product_id: product.id });
          const validBatches = sortBatchesFEFO(
            (response.data || []).filter(
              (candidate) => Number(candidate.quantity || 0) > 0 && !isExpiredBatch(candidate.expiry_date)
            )
          );

          if (validBatches.length === 1) {
            batch = validBatches[0];
          } else if (validBatches.length > 1) {
            toast.error('Existem vários lotes com stock. Leia o QR do lote correto.');
            return;
          }
        }
      }

      if (!batch || !product) {
        toast.error('Artigo não encontrado ou sem lote válido com stock');
        return;
      }

      const requiredItems = Array.isArray(pendingOrderVerification.items) ? pendingOrderVerification.items : [];
      const requiredItem = requiredItems.find((item) => String(item.product_id || '') === String(product.id || ''));

      if (!requiredItem) {
        toast.error('Este artigo não pertence à encomenda');
        return;
      }

      const alreadyVerifiedQty = verifiedOrderItems
        .filter((item) => String(item.product_id || '') === String(product.id || ''))
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

      const requiredQty = Number(requiredItem.quantity || 0);

      if (alreadyVerifiedQty >= requiredQty) {
        toast.error('Quantidade desse artigo já confirmada');
        return;
      }

      setVerifiedOrderItems((prev) => {
        const resolvedBatchId =
          String(batch.id || batch._id || batch.batch_id || batch.qr_code || code || '').trim();

        const existingIndex = prev.findIndex((item) =>
          String(item.batch_id || '').trim() === resolvedBatchId
        );

        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = recalcOrderCartLine({
            ...updated[existingIndex],
            batch_id: resolvedBatchId,
            batch_qr_code: updated[existingIndex].batch_qr_code || batch.qr_code || code,
            lot_number: updated[existingIndex].lot_number || batch.lot_number || '',
            expiry_date: updated[existingIndex].expiry_date || batch.expiry_date || '',
            quantity: Number(updated[existingIndex].quantity || 0) + 1,
          });
          return updated;
        }

        return [
          ...prev,
          recalcOrderCartLine({
            product_id: product.id,
            product_name: product.name,
            product_reference: product.reference || '',
            product_barcode: product.barcode || '',
            batch_id: resolvedBatchId,
            batch_qr_code: batch.qr_code || code,
            lot_number: batch.lot_number || '',
            expiry_date: batch.expiry_date || '',
            quantity: 1,
            unit_price: Number(requiredItem.unit_price || product.sell_price || 0),
            discount_percent: Number(requiredItem.discount_percent || 0),
            vat_rate: Number(product.vat_rate ?? 23),
            max_quantity: Number(batch.quantity || 0),
            notes: requiredItem.notes || '',
            discount_reason: 'encomenda',
          })
        ];
      });

      setOrderVerificationInput('');
      toast.success(`Artigo confirmado: ${product.name}`);
    } catch (error) {
      console.error('Erro na conferência da encomenda:', error);
      toast.error('QR não encontrado ou sem lote válido');
    } finally {
      setVerifyingOrderScan(false);
    }
  };

  const finalizeVerifiedOrderToPOS = () => {
    if (!pendingOrderVerification) return;

    const requiredItems = Array.isArray(pendingOrderVerification.items) ? pendingOrderVerification.items : [];

    const allMatched = requiredItems.every((req) => {
      const verifiedQty = verifiedOrderItems
        .filter((item) => item.product_id === req.product_id)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

      return verifiedQty === Number(req.quantity || 0);
    });

    if (!allMatched) {
      toast.error('Ainda faltam artigos por confirmar');
      return;
    }

    const finalVerifiedCart = (verifiedOrderItems || []).map((item) =>
      recalcOrderCartLine({
        ...item,
        batch_id: item.batch_id || '',
        batch_qr_code: item.batch_qr_code || '',
        lot_number: item.lot_number || '',
        expiry_date: item.expiry_date || '',
        quantity: Number(item.quantity || 0),
        unit_price: Number(item.unit_price || 0),
        discount_percent: Number(item.discount_percent || 0),
        vat_rate: Number(item.vat_rate ?? 23),
        max_quantity: Number(item.max_quantity || 0),
        notes: item.notes || '',
        discount_reason: item.discount_reason || 'encomenda',
      })
    );

    const missingBatch = finalVerifiedCart.find((item) => !String(item.batch_id || '').trim());
    if (missingBatch) {
      toast.error(`Falta lote/QR confirmado no artigo: ${missingBatch.product_name || 'Artigo'}`);
      return;
    }

    setCart(finalVerifiedCart);
    setCurrentSale(null);

    const matchedCustomer = customers.find((c) => c.id === pendingOrderVerification.customer_id);
    if (matchedCustomer) {
      selectCustomerWithCompletion(matchedCustomer);
    } else if (pendingOrderVerification.customer_name) {
      setSelectedCustomer({
        id: pendingOrderVerification.customer_id || null,
        name: pendingOrderVerification.customer_name || 'Cliente',
        phone: pendingOrderVerification.customer_phone || '',
        email: pendingOrderVerification.customer_email || '',
      });
    }

    const prepaidMeta = {
      order_id: pendingOrderVerification.order_id || '',
      order_number: pendingOrderVerification.order_number || '',
      payment_required: !!pendingOrderVerification.payment_required,
      payment_status: pendingOrderVerification.payment_status || '',
      is_prepaid: !!pendingOrderVerification.is_prepaid,
      payment_method: (pendingOrderVerification.payment_method || '').toLowerCase(),
      payment_amount: Number(pendingOrderVerification.payment_amount || 0),
      payment_paid_amount: Number(
        pendingOrderVerification.payment_paid_amount
        ?? pendingOrderVerification.prepaid_amount
        ?? 0
      ),
    };

    if (prepaidMeta.order_id) {
      sessionStorage.setItem('convertingOrderId', prepaidMeta.order_id);
      sessionStorage.setItem('convertingOrderMeta', JSON.stringify(prepaidMeta));
    }

    if (
      prepaidMeta.payment_required &&
      (prepaidMeta.is_prepaid || prepaidMeta.payment_status.toLowerCase() === 'paid') &&
      prepaidMeta.payment_method
    ) {
      setPaymentMethod(prepaidMeta.payment_method);
      setConvertingOrderMetaState(prepaidMeta);
      toast.success(`Pré-pagamento assumido: ${prepaidMeta.payment_method.toUpperCase()}`);
    } else {
      setConvertingOrderMetaState(prepaidMeta);
    }

    setShowOrderVerificationModal(false);
    setPendingOrderVerification(null);
    setVerifiedOrderItems([]);
    setOrderVerificationInput('');
    toast.success('Encomenda confirmada e carregada no POS');
  };

  const loadUnclosedCashAlert = async () => {
    try {
      const token = localStorage.getItem('ervapos_token') || '';
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const r = await fetch(`${API}/cash-movements/unclosed-session-alert`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      setUnclosedCashAlertData(d);
      setShowUnclosedCashAlert(!!d?.has_unclosed_previous_day);
      return d;
    } catch {
      setUnclosedCashAlertData(null);
      setShowUnclosedCashAlert(false);
      return null;
    }
  };

  const loadCashSessionStatus = async () => {
    try {
      const token = localStorage.getItem('ervapos_token') || '';
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const r = await fetch(`${API}/cash-movements/session-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      const isOpen = !!d?.is_open;
      setCashSessionOpen(isOpen);
      window.__cashSessionOpen = isOpen;
      return isOpen;
    } catch {
      setCashSessionOpen(false);
      window.__cashSessionOpen = false;
      return false;
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [productsRes, categoriesRes, suspendedRes, providersRes, customersRes, campaignsRes, vouchersRes] = await Promise.all([
        productsAPI.getAll(),
        productsAPI.getCategories(),
        salesAPI.getSuspended(),
        providersAPI.getAll(),
        customersAPI.getAll({ limit: 10000 }),
        campaignsAPI.getActive().catch(() => ({ data: [] })),
        vouchersAPI.getAll({ active_only: true }).catch(() => ({ data: [] })),
      ]);
      setProducts(productsRes.data || []);
      setCategories(categoriesRes.data || []);
      setSuspendedSales(suspendedRes.data || []);
      setProviders((Array.isArray(providersRes?.data) ? providersRes.data : []).filter((p) => p?.is_active !== false));
      setCustomers(customersRes.data || []);
      setActiveCampaigns(campaignsRes.data || []);
      setAvailableVouchers(vouchersRes.data || []);
    } catch (error) {
      console.error('Error loading POS data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };


  const refreshPOSStockData = async () => {
    try {
      const [productsRes, suspendedRes] = await Promise.all([
        productsAPI.getAll(),
        salesAPI.getSuspended(),
      ]);
      setProducts(productsRes.data || []);
      setSuspendedSales(suspendedRes.data || []);
    } catch (error) {
      console.error('Error refreshing POS stock data:', error);
    }
  };

  const refreshPOSReferenceData = useCallback(async () => {
    try {
      const [productsRes, categoriesRes, suspendedRes, providersRes, customersRes, campaignsRes, vouchersRes, settingsRes] = await Promise.all([
        productsAPI.getAll(),
        productsAPI.getCategories(),
        salesAPI.getSuspended(),
        providersAPI.getAll(),
        customersAPI.getAll({ limit: 10000 }),
        campaignsAPI.getActive().catch(() => ({ data: [] })),
        vouchersAPI.getAll({ active_only: true }).catch(() => ({ data: [] })),
        settingsAPI.getPosConfig().catch(() => ({ data: null })),
      ]);

      const freshCustomers = customersRes.data || [];
      setProducts(productsRes.data || []);
      setCategories(categoriesRes.data || []);
      setSuspendedSales(suspendedRes.data || []);
      setProviders((Array.isArray(providersRes?.data) ? providersRes.data : []).filter((p) => p?.is_active !== false));
      setCustomers(freshCustomers);
      setActiveCampaigns(campaignsRes.data || []);
      setAvailableVouchers(vouchersRes.data || []);

      setSelectedCustomer((current) => {
        if (!current?.id) return current;
        const refreshed = freshCustomers.find((customer) => customer.id === current.id);
        if (!refreshed) return current;
        return {
          ...refreshed,
          invoiceWithNif: current.invoiceWithNif,
        };
      });

      const pointsValue = Number(settingsRes?.data?.points_value);
      if (Number.isFinite(pointsValue) && pointsValue > 0) {
        setPointsValueConfig(pointsValue);
      }
    } catch (error) {
      // Uma falha momentânea de sincronização nunca deve bloquear uma venda.
      console.error('Error refreshing POS reference data:', error);
    }
  }, []);

  useEffect(() => {
    let active = true;

    const checkForBackofficeChanges = async () => {
      if (!active || document.visibilityState === 'hidden' || posSyncCheckingRef.current) return;
      posSyncCheckingRef.current = true;
      try {
        const response = await posSyncAPI.getVersion();
        const revision = String(response?.data?.revision || '');
        if (!revision) return;

        if (posSyncRevisionRef.current === null) {
          posSyncRevisionRef.current = revision;
          return;
        }

        if (revision !== posSyncRevisionRef.current) {
          posSyncRevisionRef.current = revision;
          await refreshPOSReferenceData();
        }
      } catch (error) {
        console.error('Error checking POS synchronization:', error);
      } finally {
        posSyncCheckingRef.current = false;
      }
    };

    checkForBackofficeChanges();
    const intervalId = window.setInterval(checkForBackofficeChanges, 30000);
    const handleVisibilityOrFocus = () => checkForBackofficeChanges();
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, [refreshPOSReferenceData]);

  // Filter products
  useEffect(() => {
    savePosDraft();
  }, [savePosDraft]);

  const filteredProducts = products.filter(p => {
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;

    const q = normalizeSearchText(searchQuery);
    const matchesSearch = !q ||
      normalizeSearchText(p.name).includes(q) ||
      normalizeSearchText(p.reference).includes(q) ||
      normalizeSearchText(p.barcode).includes(q) ||
      normalizeSearchText(p.category).includes(q);

    return matchesCategory && matchesSearch && p.is_active !== false;
  });
  // Handle barcode/QR scan

  async function autoOpenCustomerOrderInPOS() {
    const orderId = sessionStorage.getItem('convertingOrderId');
    const alreadyDone = sessionStorage.getItem('customerOrderAutoOpenDone');

    if (!orderId) return;
    if (alreadyDone === orderId) return;

    sessionStorage.setItem('customerOrderAutoOpenDone', orderId);

    try {
      await handleScan(`CUSTOMER_ORDER:${orderId}`);
    } catch (error) {
      console.error('Erro ao abrir encomenda automaticamente no POS:', error);
      sessionStorage.removeItem('customerOrderAutoOpenDone');
    }
  }

  useEffect(() => {
    autoOpenCustomerOrderInPOS();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizeVoucherScanCode = (value) => {
    let v = String(value || '')
      .trim()
      .replace(/[\r\n\t]+/g, '')
      .replace(/%2F/gi, '/');

    // O código é a única parte necessária para validar o voucher. Extraí-lo
    // primeiro torna a leitura independente do layout do teclado do leitor
    // (ex.: "VOUCHER:ÇPROMO...|VALOR:Ç5.00|VALIDO:Ç...").
    const codeMatch = v.toUpperCase().match(/(?:PROMO|DEV)[A-Z0-9_-]+/);
    if (codeMatch) return codeMatch[0];

    // Alguns leitores em Mac/layout PT trocam caracteres do QR:
    // VOUCHER:PROMODN8WZGPX|VALOR:0.15|VALIDO:2026/05/05
    // pode chegar como VOUCHERÇPROMODN8WZGPX|VALORÇ0.15|VALIDOÇ2026'05'05.
    v = v
      .replace(/^VOUCHER[^A-Z0-9]?/i, 'VOUCHER:')
      .replace(/[^A-Z0-9]VALOR[^A-Z0-9]?/i, '|VALOR:')
      .replace(/[^A-Z0-9]VALIDO[^A-Z0-9]?/i, '|VALIDO:')
      .replace(/'/g, '/');

    return v;
  };

  const isVoucherScanCode = (value) =>
    /^(?:PROMO|DEV)[A-Z0-9_-]+$/i.test(
      normalizeVoucherScanCode(value).replace(/\s+/g, '')
    );

const handleScan = async (code) => {
    try {
      const rawCode = String(code || '')
        .replace(/[\r\n\t]+/g, '')
        .trim();

      const normalizedCode = rawCode.replace(/\s+/g, '');

      if (!rawCode) return;

      // QR / código de voucher, incluindo leituras distorcidas pelo teclado do Mac.
      if (isVoucherScanCode(rawCode)) {
        const voucherScan = normalizeVoucherScanCode(rawCode);

        try {
          const response = await vouchersAPI.validateScan(voucherScan);
          const voucher = response.data.voucher;

          if (voucher.is_percentage && cart.length > 0 && cart.every((item) => isClinicalServiceProduct(item))) {
            toast.error('Os vouchers de desconto percentual não podem ser aplicados a serviços.');
            setSearchQuery('');
            return;
          }

          if (voucher.min_purchase > cartSubtotal) {
            toast.error(`Compra mínima: €${voucher.min_purchase.toFixed(2)}`);
            setSearchQuery('');
            return;
          }

          setAppliedVoucher(voucher);
          setShowVoucherModal(false);
          setVoucherCode('');
          setSearchQuery('');
          toast.success(`Voucher aplicado: ${voucher.is_percentage ? voucher.value + '%' : '€' + voucher.value.toFixed(2)}`);
          return;
        } catch (error) {
          console.error('Erro ao validar voucher por scan:', error);
          toast.error(error.response?.data?.detail || 'Voucher inválido');
          setSearchQuery('');
          return;
        }
      }

      // Se o modal de conferência da encomenda estiver aberto,
      // qualquer leitura de artigo deve ir para a conferência
      // e não diretamente para o carrinho do POS.
      if (showOrderVerificationModal && pendingOrderVerification && !rawCode.startsWith('CUSTOMER_ORDER:')) {
        await handleOrderVerificationScan(rawCode);
        setSearchQuery('');
        return;
      }

      // QR da encomenda de cliente
      let decodedRawCode = rawCode;
      try {
        decodedRawCode = decodeURIComponent(rawCode);
      } catch (_) {}

      const normalizeCustomerOrderScanCode = (value) => {
        let v = String(value || '')
          .trim()
          .replace(/[\r\n\t]+/g, '')
          .replace(/%2F/gi, '/');

        // Alguns leitores em Mac/layout PT trocam caracteres do QR:
        // CUSTOMER_ORDER:ECP2026/0057 pode chegar como CUSTOMER?ORDERÇECP2026-0057.
        v = v
          .replace(/^CUSTOMER[?_\-\s]*ORDER[Çç:|]?/i, 'CUSTOMER_ORDER:')
          .replace(/^CUSTOMERORDER[Çç:|]?/i, 'CUSTOMER_ORDER:')
          .replace(/^ENCOMENDA[Çç:|]?/i, 'ENCOMENDA:')
          .replace(/^ORDER[Çç:|]?/i, 'ORDER:');

        return v;
      };

      const scannerNormalizedRaw = normalizeCustomerOrderScanCode(rawCode);
      const scannerNormalizedDecoded = normalizeCustomerOrderScanCode(decodedRawCode);

      const upperRaw = String(rawCode || '').trim().toUpperCase();
      const upperDecoded = String(decodedRawCode || '').trim().toUpperCase();
      const upperScannerRaw = scannerNormalizedRaw.toUpperCase();
      const upperScannerDecoded = scannerNormalizedDecoded.toUpperCase();

      const maybeCustomerOrderCode =
        upperRaw.startsWith('CUSTOMER_ORDER:') ||
        upperRaw.startsWith('CUSTOMER_ORDER|') ||
        upperRaw.startsWith('CUSTOMERORDER:') ||
        upperRaw.startsWith('CUSTOMERORDER|') ||
        upperRaw.startsWith('ENCOMENDA:') ||
        upperRaw.startsWith('ENCOMENDA|') ||
        upperRaw.startsWith('ORDER:') ||
        upperRaw.startsWith('ORDER|') ||
        upperDecoded.startsWith('CUSTOMER_ORDER:') ||
        upperDecoded.startsWith('CUSTOMER_ORDER|') ||
        upperDecoded.startsWith('CUSTOMERORDER:') ||
        upperDecoded.startsWith('CUSTOMERORDER|') ||
        upperDecoded.startsWith('ENCOMENDA:') ||
        upperDecoded.startsWith('ENCOMENDA|') ||
        upperDecoded.startsWith('ORDER:') ||
        upperDecoded.startsWith('ORDER|') ||
        upperScannerRaw.startsWith('CUSTOMER_ORDER:') ||
        upperScannerRaw.startsWith('ENCOMENDA:') ||
        upperScannerRaw.startsWith('ORDER:') ||
        upperScannerDecoded.startsWith('CUSTOMER_ORDER:') ||
        upperScannerDecoded.startsWith('ENCOMENDA:') ||
        upperScannerDecoded.startsWith('ORDER:') ||
        /^ECP\d{4}[\/\-\s]\d+$/i.test(rawCode) ||
        /^ECP\d{4}[\/\-\s]\d+$/i.test(decodedRawCode) ||
        /^ECP\d{4}[\/\-\s]\d+$/i.test(scannerNormalizedRaw) ||
        /^ECP\d{4}[\/\-\s]\d+$/i.test(scannerNormalizedDecoded) ||
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rawCode) ||
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(decodedRawCode);

      if (maybeCustomerOrderCode) {
        let orderLookup = scannerNormalizedDecoded || scannerNormalizedRaw || decodedRawCode || rawCode;

        const normalizedOrderCode = String(orderLookup || '').trim();

        if (/^CUSTOMER_ORDER[:|]/i.test(normalizedOrderCode)) {
          orderLookup = normalizedOrderCode.replace(/^CUSTOMER_ORDER[:|]/i, '').trim();
        } else if (/^CUSTOMERORDER[:|]/i.test(normalizedOrderCode)) {
          orderLookup = normalizedOrderCode.replace(/^CUSTOMERORDER[:|]/i, '').trim();
        } else if (/^ENCOMENDA[:|]/i.test(normalizedOrderCode)) {
          orderLookup = normalizedOrderCode.replace(/^ENCOMENDA[:|]/i, '').trim();
        } else if (/^ORDER[:|]/i.test(normalizedOrderCode)) {
          orderLookup = normalizedOrderCode.replace(/^ORDER[:|]/i, '').trim();
        } else {
          orderLookup = normalizedOrderCode;
        }

        orderLookup = String(orderLookup || '')
          .trim()
          .replace(/%2F/gi, '/');

        if (/^ECP\d{4}-\d+$/i.test(orderLookup) || /^ECP\d{4}\s+\d+$/i.test(orderLookup)) {
          orderLookup = orderLookup.replace(/^ECP(\d{4})[-\s]+(\d+)$/i, 'ECP$1/$2');
        }

        if (!orderLookup) {
          toast.error('QR de encomenda inválido');
          return;
        }

        const dedupeCode = String(orderLookup || '').trim();
        const now = Date.now();
        if (
          lastCustomerOrderOpenRef.current.code === dedupeCode &&
          (now - Number(lastCustomerOrderOpenRef.current.at || 0)) < 1200
        ) {
          return;
        }
        lastCustomerOrderOpenRef.current = { code: dedupeCode, at: now };

        const token = localStorage.getItem('ervapos_token') || '';
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

        let orderData = null;

        const tryFetchByLookup = async (code) => {
          const r = await fetch(`${API}/customer-orders/lookup?code=${encodeURIComponent(code)}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!r.ok) return null;
          return await r.json();
        };

        const tryFetchById = async (id) => {
          const r = await fetch(`${API}/customer-orders/${encodeURIComponent(id)}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!r.ok) return null;
          return await r.json();
        };

        try {
          const isOrderNumberLookup = /^ECP\d{4}\/\d+$/i.test(orderLookup);

          orderData = await tryFetchByLookup(orderLookup);

          if (!orderData && !isOrderNumberLookup) {
            orderData = await tryFetchById(orderLookup);
          }

          if (!orderData) {
            const listResp = await fetch(`${API}/customer-orders`, {
              headers: { Authorization: `Bearer ${token}` }
            });

            if (listResp.ok) {
              const orders = await listResp.json();
              const allOrders = Array.isArray(orders) ? orders : [];

              const normalizeOrderLookup = (value) =>
                String(value || '')
                  .trim()
                  .toUpperCase()
                  .replace(/%2F/g, '/')
                  .replace(/-/g, '/')
                  .replace(/\s+/g, '');

              const lookupUpper = normalizeOrderLookup(orderLookup);

              orderData =
                allOrders.find((o) =>
                  normalizeOrderLookup(o?.order_number) === lookupUpper
                ) ||
                allOrders.find((o) =>
                  normalizeOrderLookup(o?.id) === lookupUpper
                ) ||
                null;
            }
          }
        } catch (error) {
          console.error('Erro ao procurar encomenda por QR:', error);
        }

        if (!orderData) {
          toast.error('Encomenda não encontrada');
          return;
        }

        if (String(orderData.status || '').toLowerCase() === 'collected') {
          toast.error('Esta encomenda já foi levantada/fechada');
          setSearchQuery('');
          return;
        }

        if (String(orderData.status || '').toLowerCase() === 'cancelled') {
          toast.error('Esta encomenda está cancelada');
          setSearchQuery('');
          return;
        }

        sessionStorage.setItem('orderToConvert', JSON.stringify({
          order_id: orderData.id,
          order_number: orderData.order_number,
          items: orderData.items || [],
          customer_id: orderData.customer_id || null,
          customer_name: orderData.customer_name || '',
          customer_phone: orderData.customer_phone || '',
          customer_email: orderData.customer_email || '',
          payment_required: !!orderData.payment_required,
          payment_method: orderData.payment_method || '',
          payment_status: orderData.payment_status || '',
          payment_amount: Number(orderData.payment_amount || 0),
          payment_paid_amount: Number(orderData.payment_paid_amount || 0),
          is_prepaid: !!orderData.is_prepaid,
          status: orderData.status || '',
        }));

        checkForOrderToConvert();
        setSearchQuery('');
        return;
      }

      // QR de etiqueta de lote: tentativa direta e prioritária
      if (rawCode.startsWith('BATCHQR|')) {
        try {
          const response = await batchesAPI.getByQR(rawCode);

          if (response?.data) {
            const payload = response.data || {};
            const batch = payload.batch || payload;
            const product =
              payload.product ||
              batch.product ||
              products.find(
                (p) => String(p.id || '').trim() === String(batch.product_id || '').trim()
              );

            if (product && batch && batch.id) {
              addToCart(
                product,
                {
                  ...batch,
                  id: String(batch.id || '').trim(),
                  quantity: Number(batch.quantity || 0),
                  qr_code: batch.qr_code || rawCode,
                },
                1
              );
              setSearchQuery('');
              return;
            }
          }
        } catch (err) {
          // segue para fallback abaixo
        }
      }

      // Tentar QR de lote/produto em vários formatos
      const qrCandidates = Array.from(new Set([
        rawCode,
        normalizedCode,
        normalizedCode.replace(/Ç/g, ':'),
        normalizedCode.replace(/'/g, '-'),
        normalizedCode.replace(/Ç/g, ':').replace(/'/g, '-'),
      ])).filter(Boolean);

      for (const qrCode of qrCandidates) {
        if (rawCode.startsWith('BATCHQR|') && qrCode !== rawCode) continue;

        try {
          const response = await batchesAPI.getByQR(qrCode);

          if (response?.data) {
            const payload = response.data || {};
            const batch = payload.batch || payload;
            const product =
              payload.product ||
              batch.product ||
              products.find(
                (p) => String(p.id || '').trim() === String(batch.product_id || '').trim()
              );

            if (product && batch && batch.id) {
              addToCart(
                product,
                {
                  ...batch,
                  id: String(batch.id || '').trim(),
                  quantity: Number(batch.quantity || 0),
                  qr_code: batch.qr_code || qrCode,
                },
                1
              );
              setSearchQuery('');
              return;
            }
          }
        } catch (err) {
          // ignora e tenta o formato seguinte
        }
      }

      // Código de barras ou referência
      const product = findProductByCommercialCode(products, normalizedCode);

      if (product) {
        await selectProductForCart(product);
        setSearchQuery('');
        return;
      }

      toast.error('Produto ou encomenda não encontrado');
    } catch (error) {
      console.error('Erro no scan:', error);
      toast.error('Erro ao ler código');
    }
  };

  // Handle search input (including barcode scanner)
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery) {
      handleScan(searchQuery);
    }
  };

  handleScanRef.current = handleScan;

  useEffect(() => {
    const clearBuffer = () => {
      scannerBufferRef.current = '';
      if (scannerClearTimerRef.current) {
        clearTimeout(scannerClearTimerRef.current);
        scannerClearTimerRef.current = null;
      }
    };

    const onGlobalScannerKeyDown = (e) => {
      const key = String(e.key || '');
      const target = e.target;
      const tag = String(target?.tagName || '').toLowerCase();

      const isEditable =
        !!target?.isContentEditable ||
        tag === 'textarea' ||
        tag === 'select';

      if (['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Escape'].includes(key)) {
        return;
      }

      const now = Date.now();
      if (now - scannerLastKeyAtRef.current > 120) {
        scannerBufferRef.current = '';
      }
      scannerLastKeyAtRef.current = now;

      if (key === 'Enter' || key === 'Tab') {
        const code = String(scannerBufferRef.current || '').trim();
        clearBuffer();

        if (code.length >= 4) {
          e.preventDefault();
          handleScanRef.current?.(code);
        }
        return;
      }

      if (key === 'Backspace') {
        scannerBufferRef.current = scannerBufferRef.current.slice(0, -1);
        return;
      }

      if (key.length !== 1) return;

      if (isEditable && target !== searchInputRef?.current) {
        return;
      }

      scannerBufferRef.current += key;

      if (scannerClearTimerRef.current) {
        clearTimeout(scannerClearTimerRef.current);
      }
      scannerClearTimerRef.current = setTimeout(() => {
        scannerBufferRef.current = '';
        scannerClearTimerRef.current = null;
      }, 180);
    };

    window.addEventListener('keydown', onGlobalScannerKeyDown, true);

    return () => {
      window.removeEventListener('keydown', onGlobalScannerKeyDown, true);
      if (scannerClearTimerRef.current) {
        clearTimeout(scannerClearTimerRef.current);
      }
    };
  }, []);

  const isExpiredBatch = (expiryDate) => {
    if (!expiryDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exp = new Date(expiryDate + 'T00:00:00');
    return exp < today;
  };

  const sortBatchesFEFO = (batches = []) => {
    return [...batches].sort((a, b) => {
      const aExp = a?.expiry_date || '9999-12-31';
      const bExp = b?.expiry_date || '9999-12-31';
      if (aExp !== bExp) return aExp.localeCompare(bExp);

      const aLot = a?.lot_number || '';
      const bLot = b?.lot_number || '';
      return aLot.localeCompare(bLot);
    });
  };

  const openServiceProviderModal = (product) => {
    const availableProviders = activeProviders;

    if (availableProviders.length === 0) {
      toast.error('Não existem prestadores ativos para associar a este serviço');
      return;
    }

    setSelectedServiceForProvider(product);
    setShowProviderModal(true);
  };

  const confirmServiceProviderSelection = (provider = null) => {
    if (!selectedServiceForProvider) return;

    addToCart(
      selectedServiceForProvider,
      {
        id: `SERVICE:${selectedServiceForProvider.id}:${provider?.id || 'NOPROVIDER'}`,
        qr_code: '',
        lot_number: '',
        expiry_date: '',
        quantity: 999999,
        is_service_batch: true,
      },
      1,
      provider
    );

    setShowProviderModal(false);
    setSelectedServiceForProvider(null);
  };

  // Select product and show batch selection
  const selectProductForCart = async (product) => {
    try {
      const isMedicineService = isClinicalServiceProduct(product);

      if (isMedicineService) {
        // Todos os serviços clínicos exigem uma escolha explícita em cada
        // venda, mesmo quando existe um prestador predefinido no cadastro.
        openServiceProviderModal(product);
        return;
      }

      if (isNonStockProduct(product)) {
        addToCart(product, {
          id: `NONSTOCK:${product.id}`,
          qr_code: '',
          lot_number: '',
          expiry_date: '',
          quantity: 999999,
          is_non_stock_item: true,
        }, 1);
        return;
      }

      const response = await batchesAPI.getAll({ product_id: product.id });

      const batches = sortBatchesFEFO(
        (response.data || []).filter(
          (b) => (b.quantity || 0) > 0 && !isExpiredBatch(b.expiry_date)
        )
      );

      if (batches.length === 0) {
        toast.error('Produto sem stock disponível ou apenas com lotes expirados');
        return;
      }

      if (batches.length === 1) {
        // Only one valid batch, add directly
        addToCart(product, batches[0], 1);
      } else {
        // Multiple valid batches, show selection ordered by FEFO
        setSelectedProduct(product);
        setProductBatches(batches);
        setShowBatchModal(true);
      }
    } catch (error) {
      toast.error('Erro ao verificar stock');
    }
  };

  // Regista produto como vendido (para mais vendidos)
  const trackTopSelling = (productId) => {
    setTopSelling(prev => {
      const updated = { ...prev, [productId]: (prev[productId] || 0) + 1 };
      localStorage.setItem('pos_top_selling', JSON.stringify(updated));
      return updated;
    });
  };

  // Add item to cart
  const pointsCampaignForProduct = (productId) => {
    const today = new Date().toISOString().slice(0, 10);
    return (activeCampaigns || []).find(campaign =>
      String(campaign?.reward_type || '').toLowerCase() === 'product_points' &&
      campaign?.is_active !== false &&
      !['cancelled', 'suspended'].includes(String(campaign?.status || '').toLowerCase()) &&
      String(campaign?.start_date || '') <= today &&
      String(campaign?.end_date || '') >= today &&
      Number(campaign?.redemption_points || 0) > 0 &&
      (campaign?.applicable_products || []).map(String).includes(String(productId))
    ) || null;
  };

  const addToCart = (product, batch, quantity, provider = null) => {
    if (!isClinicalServiceProduct(product) && !isNonStockProduct(product) && Number(batch?.quantity || 0) < Number(quantity || 0)) {
      toast.error(`Sem stock disponível para '${product?.name || 'artigo'}' no lote ${batch?.lot_number || '-'}`);
      return;
    }
    trackTopSelling(product.id);
    setCart(prev => {
      const isMedicineService = isClinicalServiceProduct(product);
      const isNonStock = isNonStockProduct(product);

      const providerId = String(provider?.id || '').trim();
      const resolvedBatchId = isMedicineService
        ? String(batch?.id || `SERVICE:${product.id}:${providerId || 'NOPROVIDER'}`).trim()
        : String(batch?.id || '').trim();

      if (!isMedicineService && !isNonStock && !resolvedBatchId) {
        toast.error('Lote inválido ou incompleto');
        return prev;
      }

      const existingIndex = prev.findIndex(item =>
        item.batch_id === resolvedBatchId &&
        String(item.provider_id || '') === providerId
      );

      if (existingIndex >= 0) {
        // Update quantity
        const updated = [...prev];
        const newQty = updated[existingIndex].quantity + quantity;

        if (!isMedicineService && !isNonStock && newQty > Number(batch?.quantity || 0)) {
          toast.error('Quantidade excede stock disponível');
          return prev;
        }

        updated[existingIndex].quantity = newQty;
        const gross = newQty * updated[existingIndex].unit_price;
        if (updated[existingIndex].points_redemption_campaign_id) {
          const otherPoints = updated.reduce((sum, line, idx) => idx === existingIndex ? sum : sum + (line.points_redemption_campaign_id ? Number(line.points_redemption_points || 0) * Number(line.quantity || 0) : 0), 0);
          const required = otherPoints + Number(updated[existingIndex].points_redemption_points || 0) * newQty;
          if (required > customerPointsBalance) {
            toast.error(`Saldo insuficiente: são necessários ${required} pontos`);
            return prev;
          }
          updated[existingIndex].discount = gross;
          updated[existingIndex].discount_percent = 100;
          updated[existingIndex].total = 0;
        } else {
          updated[existingIndex].total = gross;
        }
        return updated;
      }

      // Add new item
      const pointsCampaign = !isMedicineService ? pointsCampaignForProduct(product.id) : null;
      return [...prev, {
        product_id: product.id,
        product_name: product.name,
        product_reference: product.reference || '',
        product_barcode: product.barcode || '',
        batch_id: resolvedBatchId,
        batch_qr_code: batch?.qr_code || '',
        lot_number: batch?.lot_number || '',
        expiry_date: batch?.expiry_date || '',
        quantity: quantity,
        unit_price: product.sell_price,
        discount: 0,
        discount_percent: 0,
        vat_rate: Number(product.vat_rate ?? (isMedicineService ? 0 : 23)),
        total: quantity * product.sell_price,
        max_quantity: (isMedicineService || isNonStock) ? 999999 : Number(batch?.quantity || 0),
        notes: '',
        discount_reason: '',
        provider_id: provider?.id || null,
        provider_name: provider?.name || null,
        provider_specialty: provider?.specialty || null,
        available_points_campaign_id: pointsCampaign?.id || null,
        available_points_campaign_name: pointsCampaign?.name || '',
        available_points_cost: Number(pointsCampaign?.redemption_points || 0),
      }];
    });

    setShowBatchModal(false);
    toast.success('Produto adicionado');
  };

  const toggleProductPointsRedemption = (index) => {
    setCart(prev => {
      const item = prev[index];
      if (!item) return prev;
      const updated = [...prev];
      if (item.points_redemption_campaign_id) {
        const previousDiscount = Number(item.points_redemption_previous_discount || 0);
        const previousPercent = Number(item.points_redemption_previous_discount_percent || 0);
        updated[index] = { ...item, discount: previousDiscount, discount_percent: previousPercent, total: Number(item.quantity || 0) * Number(item.unit_price || 0) - previousDiscount, points_redemption_campaign_id: null, points_redemption_points: 0, points_redemption_campaign_name: '' };
        toast.success('Pagamento por pontos removido deste artigo');
        return updated;
      }
      if (!selectedCustomer?.id) {
        toast.error('Selecione primeiro o cliente para usar os seus pontos');
        return prev;
      }
      const campaign = pointsCampaignForProduct(item.product_id);
      if (!campaign) {
        toast.error('Esta promoção já não está disponível');
        return prev;
      }
      const pointsPerUnit = Number(campaign.redemption_points || 0);
      const otherPoints = prev.reduce((sum, line, idx) => idx === index ? sum : sum + (line.points_redemption_campaign_id ? Number(line.points_redemption_points || 0) * Number(line.quantity || 0) : 0), 0);
      const required = otherPoints + pointsPerUnit * Number(item.quantity || 0);
      if (required > customerPointsBalance) {
        toast.error(`Saldo insuficiente: são necessários ${required} pontos e o cliente tem ${customerPointsBalance}`);
        return prev;
      }
      const gross = Number(item.quantity || 0) * Number(item.unit_price || 0);
      updated[index] = { ...item, points_redemption_previous_discount: Number(item.discount || 0), points_redemption_previous_discount_percent: Number(item.discount_percent || 0), points_redemption_campaign_id: campaign.id, points_redemption_campaign_name: campaign.name || '', points_redemption_points: pointsPerUnit, discount: gross, discount_percent: 100, total: 0, discount_reason: `Pontos: ${campaign.name || 'promoção'}` };
      toast.success(`${item.product_name}: ${pointsPerUnit * Number(item.quantity || 0)} pontos`);
      return updated;
    });
  };

  const applyQuickItemDiscount = (index, percent) => {
    if (index === null || index === undefined) return;

    setCart(prev => {
      const updated = [...prev];
      const item = updated[index];

      if (!item) return prev;

      const discountPercent = Number(percent || 0);
      const grossTotal = Number(item.quantity || 0) * Number(item.unit_price || 0);

      item.discount_percent = discountPercent;
      item.discount = Number(((grossTotal * discountPercent) / 100).toFixed(2));
      item.total = Number((grossTotal - item.discount).toFixed(2));
      item.discount_reason = 'manual';
      item.points_redemption_campaign_id = null;
      item.points_redemption_points = 0;

      return updated;
    });

    setEditingItem(null);
    setShowDiscountModal(false);
    toast.success(`Desconto de ${percent}% aplicado`);
  };

  // Open discount modal for cart item
  const openDiscountModal = (index) => {
    setEditingCartIndex(index);
    setDiscountValue(cart[index].discount_percent || 0);
    setDiscountType('percent');
    setShowDiscountModal(true);
  };

  // Apply discount to cart item
  const applyItemDiscount = () => {
    if (editingCartIndex === null) return;

    setCart(prev => {
      const updated = [...prev];
      const item = updated[editingCartIndex];
      const grossTotal = item.quantity * item.unit_price;

      if (discountType === 'percent') {
        item.discount_percent = discountValue;
        item.discount = (grossTotal * discountValue) / 100;
      } else {
        item.discount = discountValue;
        item.discount_percent = (discountValue / grossTotal) * 100;
      }

      item.total = grossTotal - item.discount;
      item.discount_reason = 'manual';
      item.points_redemption_campaign_id = null;
      item.points_redemption_points = 0;

      return updated;
    });

    setShowDiscountModal(false);
    toast.success('Desconto aplicado');
  };

  // Open notes modal for cart item
  const openNotesModal = (index) => {
    setEditingCartIndex(index);
    setItemNotes(cart[index].notes || '');
    setShowNotesModal(true);
  };

  // Apply notes to cart item
  const applyItemNotes = () => {
    if (editingCartIndex === null) return;

    setCart(prev => {
      const updated = [...prev];
      updated[editingCartIndex].notes = itemNotes;
      return updated;
    });

    setShowNotesModal(false);
    toast.success('Notas guardadas');
  };

  // Calculate totals (needed before applyVoucher)
  const cartSubtotal = cart.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  const cartItemDiscount = cart.reduce((sum, item) => sum + (item.discount || 0), 0);

  // Validate and apply voucher
  const applyVoucher = async (codeOverride = null) => {
    const requestedCode = typeof codeOverride === 'string' ? codeOverride : voucherCode;
    if (!String(requestedCode || '').trim()) {
      toast.error('Insira o código do voucher');
      return false;
    }

    try {
      let voucher;
      const inputValue = normalizeVoucherScanCode(String(requestedCode).trim());

      // Check if it's QR code scan data (format: VOUCHER:CODE|VALOR:XX|VALIDO:DATE)
      if (isVoucherScanCode(inputValue)) {
        const response = await vouchersAPI.validateScan(inputValue);
        voucher = response.data.voucher;
      } else {
        const response = await vouchersAPI.validate(inputValue);
        voucher = response.data;
      }

      if (voucher.min_purchase > cartSubtotal) {
        toast.error(`Compra mínima: €${voucher.min_purchase.toFixed(2)}`);
        return false;
      }

      if (voucher.is_percentage && cart.length > 0 && cart.every((item) => isClinicalServiceProduct(item))) {
        toast.error('Os vouchers de desconto percentual não podem ser aplicados a serviços.');
        return false;
      }

      setAppliedVoucher(voucher);
      setVoucherCode('');
      setShowVoucherModal(false);
      toast.success(`Voucher aplicado: ${voucher.is_percentage ? voucher.value + '%' : '€' + voucher.value.toFixed(2)}`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Voucher inválido');
      return false;
    }
  };

  const clearAppliedVoucher = (showToast = false) => {
    setAppliedVoucher(null);
    setVoucherCode('');
    if (showToast) {
      toast.success('Voucher removido da venda');
    }
  };

  // Calculate voucher discount
  const voucherDiscount = appliedVoucher ? (
    appliedVoucher.is_percentage
      ? Math.min((cartSubtotal * appliedVoucher.value) / 100, appliedVoucher.max_discount || Infinity)
      : Math.min(appliedVoucher.value, cartSubtotal)
  ) : 0;

  // Update cart item quantity
  const updateCartQuantity = (index, delta) => {
    setCart(prev => {
      const updated = [...prev];
      const newQty = updated[index].quantity + delta;

      if (newQty <= 0) {
        return prev.filter((_, i) => i !== index);
      }

      if (newQty > updated[index].max_quantity) {
        toast.error('Quantidade excede stock disponível');
        return prev;
      }

      updated[index].quantity = newQty;
      const grossTotal = newQty * updated[index].unit_price;
      // Recalculate discount if percentage
      if (updated[index].discount_percent > 0) {
        updated[index].discount = (grossTotal * updated[index].discount_percent) / 100;
      }
      updated[index].total = grossTotal - updated[index].discount;
      if (updated[index].points_redemption_campaign_id) {
        const otherPoints = updated.reduce((sum, line, idx) => idx === index ? sum : sum + (line.points_redemption_campaign_id ? Number(line.points_redemption_points || 0) * Number(line.quantity || 0) : 0), 0);
        const required = otherPoints + Number(updated[index].points_redemption_points || 0) * newQty;
        if (required > customerPointsBalance) {
          toast.error(`Saldo insuficiente: são necessários ${required} pontos`);
          return prev;
        }
        updated[index].discount = grossTotal;
        updated[index].discount_percent = 100;
        updated[index].total = 0;
      }
      return updated;
    });
  };

  // Remove item from cart
  const removeFromCart = (index) => {
    setCart(prev => {
      const removed = prev[index];
      if (removed) setLastRemovedItem(removed);
      return prev.filter((_, i) => i !== index);
    });
  };

  const restoreLastRemovedItem = async () => {
    const removed = lastRemovedItem;
    if (!removed) return;
    const product = products.find((candidate) => String(candidate.id) === String(removed.product_id));
    if (!product) {
      toast.error(`O artigo '${removed.product_name}' já não está disponível`);
      return;
    }
    setLastRemovedItem(null);
    await selectProductForCart(product);
  };

  useEffect(() => {
    if (cart.length === 0 && appliedVoucher) {
      clearAppliedVoucher(false);
    }
  }, [cart.length, appliedVoucher]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate remaining totals
  const cartVAT = cart.reduce((sum, item) => {
    const lineTotal = Number(item.total || 0);
    const vatRate = Number(item.vat_rate ?? 23);
    if (vatRate <= 0 || lineTotal <= 0) return sum;
    return sum + (lineTotal - (lineTotal / (1 + vatRate / 100)));
  }, 0);
  const cartTotalAfterItems = cartSubtotal - cartItemDiscount - voucherDiscount;
  const cartTotalBeforePoints = Math.max(0, cartTotalAfterItems);

  const pointsValue = Math.max(0.000001, Number(pointsValueConfig || 0.01));
  const promotionalPointsInCart = cart.reduce((sum, item) => sum + (item.points_redemption_campaign_id ? Number(item.points_redemption_points || 0) * Number(item.quantity || 0) : 0), 0);
  const customerPointsAfterPromotions = Math.max(0, customerPointsBalance - promotionalPointsInCart);
  const maxPointsUsableByTotal = Math.floor(cartTotalBeforePoints / pointsValue);
  const safePointsToUse = selectedCustomer?.id && useCustomerPoints
    ? Math.max(0, Math.min(Number(pointsToUse || 0), customerPointsAfterPromotions, maxPointsUsableByTotal))
    : 0;
  const pointsDiscount = Number((safePointsToUse * pointsValue).toFixed(2));
  const cartTotal = Math.max(0, cartTotalBeforePoints - pointsDiscount);

  const prepaidAppliedAmount = (
    convertingOrderMetaState?.payment_required &&
    Number(
      convertingOrderMetaState?.payment_paid_amount
      ?? convertingOrderMetaState?.prepaid_amount
      ?? 0
    ) > 0
  ) ? Number(
    convertingOrderMetaState?.payment_paid_amount
    ?? convertingOrderMetaState?.prepaid_amount
    ?? 0
  ) : 0;

  const amountDueNow = Math.max(0, cartTotal - prepaidAppliedAmount);
  const isFullyCoveredByPrepaid = prepaidAppliedAmount > 0 && amountDueNow <= 0;
  const prepaidInheritedMethod = String(convertingOrderMetaState?.payment_method || '').toLowerCase();

  const cashAmountDue = prepaidAppliedAmount > 0 ? amountDueNow : cartTotal;
  const cashReceivedValue = Number.parseFloat(String(cashReceived || '').replace(',', '.')) || 0;
  const cashChange = Math.max(0, cashReceivedValue - cashAmountDue);
  const cashMissing = Math.max(0, cashAmountDue - cashReceivedValue);

  const mixedAmountDue = cashAmountDue;
  const mixedPrimaryAmountValueRaw = Number.parseFloat(String(mixedPrimaryAmount || '').replace(',', '.')) || 0;
  const mixedPrimaryAmountValue = Math.max(0, Math.min(mixedPrimaryAmountValueRaw, mixedAmountDue));
  const mixedSecondaryAmountValue = Math.max(0, Number((mixedAmountDue - mixedPrimaryAmountValue).toFixed(2)));
  const mixedMethodsValid = mixedPrimaryMethod && mixedSecondaryMethod && mixedPrimaryMethod !== mixedSecondaryMethod;
  const mixedBreakdown = mixedMethodsValid
    ? [
        { method: mixedPrimaryMethod, amount: Number(mixedPrimaryAmountValue.toFixed(2)) },
        { method: mixedSecondaryMethod, amount: Number(mixedSecondaryAmountValue.toFixed(2)) },
      ].filter((x) => x.amount > 0)
    : [];
  const mixedIsValid =
    paymentMethod !== 'mixed'
      ? true
      : mixedMethodsValid &&
        mixedPrimaryAmountValue > 0 &&
        mixedSecondaryAmountValue > 0 &&
        Number((mixedPrimaryAmountValue + mixedSecondaryAmountValue).toFixed(2)) === Number(mixedAmountDue.toFixed(2));

  const cartContainsService = cart.some((item) => {
    const product = products.find((candidate) => String(candidate.id) === String(item.product_id));
    return product ? isClinicalServiceProduct(product) : String(item.batch_id || '').startsWith('SERVICE:');
  });

  const todayIso = new Date().toISOString().slice(0, 10);
  const customerAvailableVouchers = selectedCustomer?.id
    ? availableVouchers.filter((voucher) => (
        String(voucher.customer_id || '') === String(selectedCustomer.id)
        && !voucher.is_used
        && voucher.status !== 'cancelled'
        && voucher.status !== 'used'
        && (!voucher.valid_from || String(voucher.valid_from).slice(0, 10) <= todayIso)
        && (!voucher.valid_until || String(voucher.valid_until).slice(0, 10) >= todayIso)
      ))
    : [];

  const openFinalSaleConfirmation = (skipVoucherPrompt = false) => {
    const shouldSkipVoucherPrompt = skipVoucherPrompt === true;
    if (!shouldSkipVoucherPrompt && !appliedVoucher && customerAvailableVouchers.length > 0) {
      setShowCustomerVoucherPrompt(true);
      return;
    }
    if (cartContainsService && invoiceType === 'fiscal') {
      if (!selectedCustomer?.id) {
        toast.error('Os serviços só podem ser faturados fiscalmente a um cliente identificado com NIF.');
        setShowCustomerModal(true);
        return;
      }
      const nif = String(selectedCustomer?.nif || '').trim();
      if (!nif || nif === '999999990') {
        toast.error('Preencha um NIF válido na ficha do cliente antes de faturar este serviço.');
        selectCustomerWithCompletion(selectedCustomer);
        return;
      }
      if (selectedCustomer?.invoiceWithNif !== true) {
        toast.error('Para faturar serviços, selecione “Com NIF”.');
        setShowInvoiceIdentityChoice(true);
        return;
      }
    }
    setShowCustomerConfirmationModal(true);
  };

  // Create or update sale
  const createSale = async () => {
    if (cart.length === 0) {
      toast.error('Carrinho vazio');
      return null;
    }

    try {
      const saleData = {
        customer_id: selectedCustomer?.id || null,
        invoice_with_nif: selectedCustomer?.invoiceWithNif === true,
        customer_name: selectedCustomer?.name || null,
        terminal_id: user?.terminal_id || 'POS-1',
        operator_id: user?.id,
        operator_name: user?.name,
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          batch_id: String(item.batch_id || '').trim(),
          lot_number: item.lot_number || '',
          product_reference: item.product_reference || '',
          product_barcode: item.product_barcode || '',
          batch_expiry_date: item.expiry_date || '',
          batch_qr_code: item.batch_qr_code || '',
          quantity: Number(item.quantity || 0),
          unit_price: Number(item.unit_price || 0),
          discount: Number(item.discount || 0),
          discount_percent: Number(item.discount_percent || 0),
          vat_rate: Number(item.vat_rate ?? 23),
          notes: item.notes || '',
          provider_id: item.provider_id || null,
          provider_name: item.provider_name || null,
          provider_specialty: item.provider_specialty || null,
          points_redemption_campaign_id: item.points_redemption_campaign_id || null,
          points_redemption_points: Number(item.points_redemption_points || 0),
        })).filter(item => item.batch_id),
      };

      if (!saleData.items.length) {
        toast.error('Nenhum artigo confirmado com lote/validade para faturar');
        return null;
      }

      const missingBatch = saleData.items.find((item) => !item.batch_id);
      if (missingBatch) {
        toast.error(`Artigo sem lote confirmado: ${missingBatch.product_name || 'Artigo'}`);
        return null;
      }

      const response = await salesAPI.create(saleData);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar venda:', error?.response?.data || error);
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => `${d.loc?.join('.')}: ${d.msg}`).join(' | ')
            : 'Erro ao criar venda'
      );
      return null;
    }
  };

  const completeSaleWithDuplicateConfirmation = async (...args) => {
    await salesAPI.updateInvoiceIdentity(args[0], {
      invoice_with_nif: invoiceType === 'fiscal' && selectedCustomer?.invoiceWithNif === true,
    });
    try {
      return await salesAPI.complete(...args, null, cartContainsService && invoiceType === 'internal');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail?.code === 'RAPID_DUPLICATE_SALE_BLOCKED') {
        throw new Error(detail.message || 'Venda duplicada bloqueada.');
      }
      if (error?.response?.status !== 409 || detail?.code !== 'SIMILAR_RECENT_FISCAL_SALE') {
        throw error;
      }

      const confirmed = window.confirm(
        'A venda anterior é exatamente igual a esta.\n\n' +
        'Pode tratar-se de outro cliente com os mesmos artigos. Pretende avançar com uma nova venda?'
      );
      if (!confirmed) {
        throw new Error('Venda não concluída: confirme primeiro se deve retomar a venda anterior.');
      }

      return await salesAPI.complete(...args, detail.previous_sale_id, cartContainsService && invoiceType === 'internal');
    }
  };

  // Complete payment

  // Ler itens copiados do histórico de vendas
  useEffect(() => {
    const copied = sessionStorage.getItem('copyToCart');
    if (copied) {
      try {
        const items = JSON.parse(copied);
        sessionStorage.removeItem('copyToCart');
        if (items.length > 0) {
          const validItems = items.filter(item => item.batch_id);

          if (validItems.length === 0) {
            toast.error('Os itens copiados não têm lote associado. Reabra os artigos pelo stock atual.');
            return;
          }

          const newCart = validItems.map(item => ({
            product_id: item.product_id,
            product_name: item.product_name,
            product_reference: item.product_reference || item.reference || '',
            product_barcode: item.product_barcode || item.barcode || '',
            batch_id: item.batch_id,
            batch_qr_code: item.batch_qr_code || '',
            lot_number: item.lot_number || '',
            expiry_date: item.batch_expiry_date || item.expiry_date || '',
            unit_price: item.unit_price,
            quantity: item.quantity,
            barcode: item.barcode || '',
            vat_rate: item.vat_rate ?? 23,
            discount: 0,
            discount_percent: 0,
            total: (item.unit_price || 0) * (item.quantity || 1),
            max_quantity: item.quantity || 1,
            notes: item.notes || '',
            discount_reason: '',
            provider_id: item.provider_id || null,
            provider_name: item.provider_name || null,
            provider_specialty: item.provider_specialty || null,
          }));

          setCart(newCart);

          if (validItems.length < items.length) {
            toast.warning('Alguns itens copiados foram ignorados por não terem lote associado');
          } else {
            toast.success(items.length + ' itens adicionados ao carrinho');
          }
        }
      } catch (e) {
        console.error('Erro ao ler copyToCart:', e);
        sessionStorage.removeItem('copyToCart');
      }
    }
  }, []);
  useEffect(() => {
    const syncCashState = () => {
      if (typeof window.__cashSessionOpen !== 'undefined') {
        setCashSessionOpen(!!window.__cashSessionOpen);
        delete window.__cashSessionOpen;
      }
    };

    const handleCashSessionChanged = () => syncCashState();
    const handleFocus = () => syncCashState();

    syncCashState();

    window.addEventListener('cash-session-changed', handleCashSessionChanged);
    window.addEventListener('focus', handleFocus);

    const t = setInterval(syncCashState, 30000);

    return () => {
      clearInterval(t);
      window.removeEventListener('cash-session-changed', handleCashSessionChanged);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const handleCashSaved = async () => {
    await loadCashSessionStatus();
    await loadUnclosedCashAlert();
  };


  useEffect(() => {
    if (pendingOpenGeneratedVoucher && !showReceiptModal && generatedVoucherAfterSale) {
      const timer = setTimeout(() => {
        setShowGeneratedVoucherModal(true);
        setPendingOpenGeneratedVoucher(false);
      }, 180);
      return () => clearTimeout(timer);
    }
  }, [pendingOpenGeneratedVoucher, showReceiptModal, generatedVoucherAfterSale]);

  const finalizeSuccessfulSaleFlow = async (sale, effectivePaymentMethod) => {
    const convertingOrderId = sessionStorage.getItem('convertingOrderId');

    if (invoiceType === 'fiscal') {
      try {
        const fiscalRes = await salesAPI.fiscalize(sale.id);
        if (!fiscalRes?.data?.document_id) {
          throw new Error('A Vendus não confirmou a criação da fatura. Tente novamente.');
        }

        toast.success('Fatura ' + (fiscalRes.data.document_number || '') + ' criada no Vendus');
        sessionStorage.removeItem('pendingFiscalSaleId');
        setIsFiscalSale(true);
      } catch (fiscalErr) {
        console.error('Erro fiscalização:', fiscalErr);
        const detail = fiscalErr?.response?.data?.detail || fiscalErr?.message || 'Erro desconhecido';
        throw new Error(`Venda fiscal não concluída: ${detail} Pode tentar novamente.`);
      }
    }

    if (convertingOrderId) {
      const token = localStorage.getItem('ervapos_token') || '';
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

      try {
        const collectedRes = await fetch(`${API}/customer-orders/${convertingOrderId}/mark-collected`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!collectedRes.ok) {
          const errText = await collectedRes.text();
          console.error('Erro a marcar customer-order como levantada:', errText);
        }
      } catch (error) {
        console.error('Error marking customer-order as collected:', error);
      }

      try {
        const convertedRes = await fetch(`${API}/customer-orders/${convertingOrderId}/mark-converted`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sale_id: sale.id,
            sale_number: sale.ncp_number || sale.sale_number || '',
            sale_type: invoiceType,
          })
        });
        if (!convertedRes.ok) {
          console.error('Erro a marcar customer-order como convertida:', await convertedRes.text());
        }
      } catch (error) {
        console.error('Error marking customer-order as converted:', error);
      }

      sessionStorage.removeItem('convertingOrderId');
      sessionStorage.removeItem('convertingOrderMeta');
    }

    try {
      const receiptRes = await salesAPI.getReceipt(sale.id);
      setReceiptData(receiptRes.data);
      setCompletedSaleId(sale.id);
      setReceiptEmail(
        receiptRes?.data?.sale?.customer_email ||
        selectedCustomer?.email ||
        ''
      );
      setReceiptPhone(
        receiptRes?.data?.sale?.customer_phone ||
        selectedCustomer?.phone ||
        ''
      );
      setGeneratedVoucherAfterSale(
        Array.isArray(sale?.generated_vouchers) && sale.generated_vouchers.length
          ? sale.generated_vouchers[0]
          : null
      );
      setResidualVoucherAfterSale(sale?.residual_voucher || null);
      await refreshPOSStockData();
      setShowReceiptModal(true);
    } catch (error) {
      console.error('Error loading receipt:', error);
    }

    toast.success('Venda concluída com sucesso!');

    localStorage.removeItem(mbwayRecoveryKey);
    setCart([]);
    clearPosDraft();
    setCurrentSale(null);
    setSelectedCustomer(null);
    setShowPaymentModal(false);
    setAppliedVoucher(null);
    clearPosDraft();
    setVoucherCode('');
    setUseCustomerPoints(false);
    setPointsToUse(0);
    setCashReceived('');
    setMbwayPhone('');
    setMbwayWaiting(false);
    setMixedPrimaryMethod('cash');
    setMixedSecondaryMethod('card');
    setMixedPrimaryAmount('');
    setShowGeneratedVoucherModal(false);
    setIsFiscalSale(false);
    setConvertingOrderMetaState(null);
    setPaymentMethod('cash');
    sessionStorage.removeItem('convertingOrderMeta');
    sessionStorage.removeItem('convertingOrderId');
    sessionStorage.removeItem('customerOrderAutoOpenDone');
  };

  const handlePayment = async () => {
    const amountToValidate = prepaidAppliedAmount > 0 ? amountDueNow : cartTotal;
      const accountCurrentPaidNowValueRaw = Number.parseFloat(String(accountCurrentPaidNow || '').replace(',', '.')) || 0;
      const accountCurrentPaidNowValue = Math.min(
        Math.max(0, accountCurrentPaidNowValueRaw),
        amountToValidate
      );
      const accountCurrentOpenAmount = Math.max(0, Number((amountToValidate - accountCurrentPaidNowValue).toFixed(2)));

    if (!isFullyCoveredByPrepaid && paymentMethod === 'cash') {
      const received = Number.parseFloat(String(cashReceived || '').replace(',', '.')) || 0;
      if (received <= 0) {
        toast.error('Indica quanto o cliente entregou em dinheiro');
        return;
      }
      if (received < amountToValidate) {
        toast.error('O valor entregue é inferior ao total a pagar');
        return;
      }
    }

    if (!isFullyCoveredByPrepaid && paymentMethod === 'mbway') {
      const phone = normalizePhoneForMbway(mbwayPhone);
      if (!phone) {
        toast.error('Confirma o número MB WAY do cliente');
        return;
      }
    }

    if (!isFullyCoveredByPrepaid && paymentMethod === 'mixed') {
      if (!mixedIsValid) {
        toast.error('Confirma os valores do pagamento misto');
        return;
      }
    }


      if (!isFullyCoveredByPrepaid && paymentMethod === 'account_current') {
        if (!selectedCustomer?.id) {
          toast.error('Conta corrente exige um cliente selecionado');
          return;
        }
        if (accountCurrentPaidNowValue > 0 && !String(accountCurrentPaidMethod || '').trim()) {
          toast.error('Escolhe o método do valor recebido agora');
          return;
        }
      }
    const isOpenNow = await loadCashSessionStatus();
    const cashAlertNow = await loadUnclosedCashAlert();

    if (cashAlertNow?.has_unclosed_previous_day) {
      toast.error('Existe uma caixa de dia anterior por fechar');
      setShowUnclosedCashAlert(true);
      return;
    }

    if (!isOpenNow) {
      toast.error('É necessário abrir caixa antes de faturar no POS');
      setCashModalType('open');
      setShowCashModal(true);
      return;
    }

    setCashSessionOpen(true);
    setProcessing(true);

    try {
      // Create sale if not exists
      let sale = currentSale;
      const recoveryId = localStorage.getItem(mbwayRecoveryKey);
      if (recoveryId) {
        const { data: recovered } = await salesAPI.getById(recoveryId);
        if (recovered.operator_id !== user?.id) throw new Error('A venda MB WAY pertence a outro operador.');
        if (sale?.id !== recoveryId) {
          setCurrentSale(recovered);
          setCart((recovered.items || []).map(item => ({ ...item, max_quantity: item.quantity })));
          setPaymentMethod('mbway');
          setMbwayPhone(recovered.payment_phone || '');
          throw new Error('Venda MB WAY recuperada. Confirme os artigos e volte a fechar; não será feita nova cobrança.');
        }
        sale = recovered;
        const signature = items => JSON.stringify((items || []).map(item => [item.product_id,item.batch_id,Number(item.quantity),Number(item.unit_price),Number(item.discount_percent || 0)]).sort());
        if (signature(cart) !== signature(recovered.items)) {
          setCart((recovered.items || []).map(item => ({ ...item, max_quantity: item.quantity })));
          throw new Error('O carrinho foi reposto com os artigos do pedido MB WAY original. Confirme-os antes de concluir.');
        }
      }
      const pendingFiscalSaleId = sessionStorage.getItem('pendingFiscalSaleId');
      if (!sale && pendingFiscalSaleId) {
        try {
          const pendingRes = await salesAPI.getById(pendingFiscalSaleId);
          const pendingSale = pendingRes?.data;
          if (pendingSale?.id && String(pendingSale?.status || '').toLowerCase() === 'completed' && !pendingSale?.vendus_invoice_id) {
            sale = pendingSale;
            setCurrentSale(pendingSale);
            toast.info('A retomar a venda fiscal pendente sem repetir a baixa de stock');
          } else {
            sessionStorage.removeItem('pendingFiscalSaleId');
          }
        } catch (pendingError) {
          console.error('Erro ao recuperar venda fiscal pendente:', pendingError);
        }
      }
      if (!sale) {
        sale = await createSale();
        if (!sale) {
          setProcessing(false);
          return;
        }
      }

      let effectivePaymentMethod = paymentMethod;
      const convertingOrderMetaStr = sessionStorage.getItem('convertingOrderMeta');

      if (convertingOrderMetaStr) {
        try {
          const convertingOrderMeta = JSON.parse(convertingOrderMetaStr);
          const alreadyPaid =
            !!convertingOrderMeta?.payment_required &&
            (
              !!convertingOrderMeta?.is_prepaid ||
              String(convertingOrderMeta?.payment_status || '').toLowerCase() === 'paid'
            );

          if (alreadyPaid && convertingOrderMeta?.payment_method) {
            effectivePaymentMethod = convertingOrderMeta.payment_method;
          }
        } catch (error) {
          console.error('Error parsing convertingOrderMeta:', error);
        }
      }

      if (isFullyCoveredByPrepaid) {
        effectivePaymentMethod = 'card';
      }

      if (effectivePaymentMethod === 'mbway' && !isFullyCoveredByPrepaid) {
        const phone = normalizePhoneForMbway(mbwayPhone);

        setMbwayWaiting(true);
        rememberMbwaySale(sale);
        const paymentResponse = await salesAPI.createPayment(sale.id, phone);
        sale = paymentResponse?.data?.sale || sale;
        setCurrentSale(sale);
        toast.info(sale.payment_status === 'paid' ? 'Pagamento MB WAY já confirmado. Sem nova cobrança.' : 'A verificar o pedido MB WAY existente...');

        let paid = sale.payment_status === 'paid';
        for (let attempt = 0; !paid && attempt < 40; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
          const saleStatusRes = await salesAPI.getPaymentStatus(sale.id);
          const saleUpdated = saleStatusRes?.data || {};
          const paymentStatus = String(saleUpdated.payment_status || '').toLowerCase();

          if (paymentStatus === 'paid') {
            paid = true;
            break;
          }

          if (paymentStatus === 'failed') {
            throw new Error('Pagamento MB WAY falhou ou foi cancelado');
          }
        }

        if (!paid) {
          throw new Error('Pagamento MB WAY ainda não confirmado');
        }

          const completeRes = await completeSaleWithDuplicateConfirmation(
            sale.id,
            effectivePaymentMethod,
            invoiceType,
            appliedVoucher?.code || null,
            safePointsToUse,
            null,
            null,
            null,
            prepaidAppliedAmount > 0 ? {
              prepaid_amount: Number(prepaidAppliedAmount || 0),
              prepaid_method: String(prepaidInheritedMethod || convertingOrderMetaState?.payment_method || ''),
            } : null,
            paymentMethod === 'account_current' ? {
              paid_now: accountCurrentPaidNowValue,
              paid_method: accountCurrentPaidNowValue > 0 ? accountCurrentPaidMethod : null,
            } : null
          );
        sale = completeRes?.data || sale;
      } else {
          const completeRes = await completeSaleWithDuplicateConfirmation(
            sale.id,
            effectivePaymentMethod,
            invoiceType,
            appliedVoucher?.code || null,
            safePointsToUse,
            effectivePaymentMethod === 'cash' ? cashReceivedValue : null,
            effectivePaymentMethod === 'cash' ? cashChange : null,
            effectivePaymentMethod === 'mixed' ? mixedBreakdown : null,
            prepaidAppliedAmount > 0 ? {
              prepaid_amount: Number(prepaidAppliedAmount || 0),
              prepaid_method: String(prepaidInheritedMethod || convertingOrderMetaState?.payment_method || ''),
            } : null,
            paymentMethod === 'account_current' ? {
              paid_now: accountCurrentPaidNowValue,
              paid_method: accountCurrentPaidNowValue > 0 ? accountCurrentPaidMethod : null,
            } : null
          );
        sale = completeRes?.data || sale;
      }

      setCurrentSale(sale);
      if (invoiceType === 'fiscal' && sale?.id && !sale?.vendus_invoice_id) {
        sessionStorage.setItem('pendingFiscalSaleId', sale.id);
      }
      await finalizeSuccessfulSaleFlow(sale, effectivePaymentMethod);

    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
      toast.error(error?.response?.data?.detail || error?.message || 'Erro ao processar pagamento');
      setMbwayWaiting(false);
    } finally {
      setProcessing(false);
    }
  };


  // Suspend sale
  const handleSuspend = async () => {
    try {
      let sale = currentSale;
      if (!sale) {
        sale = await createSale();
        if (!sale) return;
      }

      await salesAPI.suspend(sale.id);
      if (localStorage.getItem(mbwayRecoveryKey) === sale.id) localStorage.removeItem(mbwayRecoveryKey);
      toast.success('Venda suspensa');

      setCart([]);
      setCurrentSale(null);
      setSelectedCustomer(null);
      setConvertingOrderMetaState(null);
      setPaymentMethod('cash');
      sessionStorage.removeItem('convertingOrderMeta');
      sessionStorage.removeItem('convertingOrderId');
      sessionStorage.removeItem('customerOrderAutoOpenDone');
      setUseCustomerPoints(false);
      setPointsToUse(0);

      // Refresh suspended sales
      const response = await salesAPI.getSuspended();
      setSuspendedSales(response.data || []);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao suspender venda');
    }
  };

  // Resume suspended sale
  const handleResume = async (saleId) => {
    try {
      const response = await salesAPI.resume(saleId);
      const sale = response.data;

      if (sale.payment_method === 'mbway' && (sale.payment_status === 'paid' || sale.payment_request_id || sale.payment_intent_state)) {
        setPaymentMethod('mbway');
        setMbwayPhone(sale.payment_phone || '');
        rememberMbwaySale(sale);
        toast.info('Esta venda tem um pedido MB WAY. O estado será confirmado sem nova cobrança.');
      }

      setCurrentSale(sale);
      setCart(sale.items.map(item => ({
        ...item,
        max_quantity: item.quantity + 100, // Allow some flexibility
      })));

      if (sale.customer_id) {
        const customer = customers.find(c => c.id === sale.customer_id);
        setSelectedCustomer(customer ? { ...customer, invoiceWithNif: sale.invoice_with_nif === true } : null);
      }

      setShowSuspendedModal(false);
      toast.success('Venda retomada');

      // Refresh suspended sales
      const suspendedRes = await salesAPI.getSuspended();
      setSuspendedSales(suspendedRes.data || []);
    } catch (error) {
      toast.error('Erro ao retomar venda');
    }
  };

  // Cancel current sale
  const handleCancel = () => {
    if (cart.length === 0) return;
    if (localStorage.getItem(mbwayRecoveryKey)) {
      toast.error('Existe uma venda MB WAY por concluir. Retome ou suspenda a venda; não descarte um pagamento por confirmar.');
      return;
    }

    if (window.confirm('Cancelar venda atual?')) {
      setCart([]);
      setCurrentSale(null);
      setSelectedCustomer(null);
      setConvertingOrderMetaState(null);
      setPaymentMethod('cash');
      sessionStorage.removeItem('convertingOrderMeta');
      sessionStorage.removeItem('convertingOrderId');
      sessionStorage.removeItem('customerOrderAutoOpenDone');
      setUseCustomerPoints(false);
      setPointsToUse(0);
    }
  };


  const openQuickSalesHistory = async () => {
    try {
      setHistoryLoading(true);
      const res = await salesAPI.getAll({ status: 'completed' });
      const data = Array.isArray(res.data) ? res.data : [];
      const sorted = data
        .sort((a, b) => String(b.completed_at || '').localeCompare(String(a.completed_at || '')))
        .slice(0, 20);
      setQuickSalesHistory(sorted);
      setShowQuickSalesHistoryModal(true);
    } catch (error) {
      console.error('Erro ao carregar histórico rápido de vendas:', error);
      toast.error('Erro ao carregar histórico de vendas');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openCustomerHistory = async () => {
    if (!selectedCustomer?.id) {
      toast.error('Selecione um cliente primeiro');
      return;
    }

    try {
      setHistoryLoading(true);
      const res = await customersAPI.getHistory(selectedCustomer.id);
      const data = Array.isArray(res.data) ? res.data : [];
      const sorted = data.sort((a, b) => String(b.completed_at || '').localeCompare(String(a.completed_at || '')));
      setCustomerHistory(sorted);
      setShowCustomerHistoryModal(true);
    } catch (error) {
      console.error('Erro ao carregar histórico do cliente:', error);
      toast.error('Erro ao carregar histórico do cliente');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistorySaleDetail = (sale) => {
    setSelectedHistorySale(sale || null);
    setHistoryEmail(
      sale?.customer_email ||
      selectedCustomer?.email ||
      ''
    );
    setHistoryPhone(sale?.customer_phone || selectedCustomer?.phone || '');
    setShowHistorySaleDetailModal(true);
  };

  const sendReceiptSmsForSale = async (saleId, phone) => {
    const destination = String(phone || '').trim();
    if (!saleId) {
      toast.error('Venda sem identificador');
      return;
    }
    if (!destination) {
      toast.error('Indique o telemóvel do cliente');
      return;
    }
    try {
      setSendingReceiptSms(true);
      const response = await salesAPI.sendReceiptSms(saleId, destination);
      if (response?.data?.queued_local_only) {
        toast.warning('Telemóvel SMS temporariamente offline. A mensagem ficou guardada para reenvio.');
      } else {
        toast.success('Link do talão enviado por SMS. Disponível durante 3 meses.');
      }
    } catch (error) {
      console.error('Erro ao enviar talão por SMS:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao enviar talão por SMS');
    } finally {
      setSendingReceiptSms(false);
    }
  };


  const loadReceiptBackendPdfForModal = useCallback(async (saleId) => {
    if (!saleId) {
      setReceiptPdfUrl('');
      return;
    }

    setReceiptPdfLoading(true);
    try {
      const response = await salesAPI.getReceiptPDF(saleId, 'receipt');
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const nextUrl = window.URL.createObjectURL(blob);

      setReceiptPdfUrl((previousUrl) => {
        if (previousUrl) {
          window.URL.revokeObjectURL(previousUrl);
        }
        return nextUrl;
      });
    } catch (error) {
      console.error('Erro ao carregar pré-visualização PDF do talão:', error);
      setReceiptPdfUrl('');
      toast.error(error?.response?.data?.detail || 'Erro ao carregar pré-visualização do talão');
    } finally {
      setReceiptPdfLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showReceiptModal && completedSaleId) {
      loadReceiptBackendPdfForModal(completedSaleId);
    }

    if (!showReceiptModal) {
      setReceiptPdfUrl((previousUrl) => {
        if (previousUrl) {
          window.URL.revokeObjectURL(previousUrl);
        }
        return '';
      });
      setReceiptPdfLoading(false);
    }
  }, [showReceiptModal, completedSaleId, loadReceiptBackendPdfForModal]);

  const handleSendHistorySaleEmail = async () => {
    const email = String(historyEmail || '').trim();
    if (!selectedHistorySale?.id) {
      toast.error('Venda sem identificador');
      return;
    }
    if (!email) {
      toast.error('Indique um email');
      return;
    }

    try {
      setSendingHistoryEmail(true);

      const receiptRes = await salesAPI.getReceipt(selectedHistorySale.id);
      const receipt = receiptRes?.data || null;

      setReceiptData(receipt);
      setCompletedSaleId(selectedHistorySale.id);
      setReceiptEmail(email);
      setIsFiscalSale(
        String(receipt?.sale?.invoice_type || '').toLowerCase() === 'fiscal' &&
        !receipt?.sale?.fiscal_from_batch &&
        !!receipt?.sale?.vendus_invoice_id
      );
      setShowReceiptModal(true);

      await new Promise((resolve) => setTimeout(resolve, 300));
      await sendReceiptPreviewEmail(selectedHistorySale.id, email);

      toast.success('Talão enviado por email');
    } catch (error) {
      console.error('Erro ao enviar email do talão:', error);
      toast.error(error?.response?.data?.detail || error?.message || 'Erro ao enviar email');
    } finally {
      setSendingHistoryEmail(false);
    }
  };

  const openHistorySaleReceipt = async (sale) => {
    if (!sale?.id) return;
    try {
      const receiptRes = await salesAPI.getReceipt(sale.id);
      const receipt = receiptRes?.data || null;

      setReceiptData(receipt);
      setCompletedSaleId(sale.id);
      setReceiptEmail(
        receipt?.sale?.customer_email ||
        sale?.customer_email ||
        historyEmail ||
        ''
      );
      setReceiptPhone(receipt?.sale?.customer_phone || sale?.customer_phone || historyPhone || '');
      setIsFiscalSale(
        String(receipt?.sale?.invoice_type || '').toLowerCase() === 'fiscal' &&
        !receipt?.sale?.fiscal_from_batch &&
        !!receipt?.sale?.vendus_invoice_id
      );
      setShowReceiptModal(true);
    } catch (error) {
      console.error('Erro ao abrir talão da venda:', error);
      toast.error('Erro ao abrir talão');
    }
  };

  const buildReceiptPreviewPdfPayload = async () => {
    const docNumber =
      receiptData?.sale?.vendus_invoice_number ||
      receiptData?.sale?.ncp_number ||
      completedSaleId ||
      'talao';

    const previewEl = document.getElementById('receipt-content');
    if (!previewEl) {
      throw new Error('Pré-visualização do talão indisponível');
    }

    return await buildReceiptPreviewPdfFromElement(
      previewEl,
      docNumber
    );
  };

  const sendReceiptPreviewEmail = async (saleId, recipientEmail) => {
    const email = String(recipientEmail || '').trim();

    if (!saleId) {
      throw new Error('Venda sem identificador');
    }

    if (!email) {
      throw new Error('Indique um email');
    }

    // Blindagem: o anexo do talão é sempre gerado no backend.
    // Nunca usamos PDF criado a partir da pré-visualização HTML do frontend.
    await salesAPI.sendEmail(saleId, email);
  };

  const openReceiptPreviewPrintWindow = async () => {
    if (!completedSaleId) {
      throw new Error('Venda sem identificador');
    }

    // Blindagem: impressão sempre pelo PDF backend 80mm.
    const response = await salesAPI.getReceiptPDF(completedSaleId, 'receipt');
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const win = window.open(blobUrl, '_blank');

    if (!win) {
      throw new Error('O navegador bloqueou a abertura da janela de impressão');
    }

    toast.success('Talão backend pronto a imprimir');
  };

  const downloadReceiptPreviewPdf = async () => {
    if (!completedSaleId) {
      throw new Error('Venda sem identificador');
    }

    // Blindagem: descarregar/abrir sempre o PDF backend 80mm.
    const response = await salesAPI.getReceiptPDF(completedSaleId, 'receipt');
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  };

  const formatHistoryDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('pt-PT');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{background:"#f1f5f9"}}>
      <POSLockOverlay
        open={posLocked}
        reason={lockReason}
        users={lockUsers}
        selectedEmail={selectedLockEmail}
        setSelectedEmail={setSelectedLockEmail}
        secret={lockSecret}
        setSecret={setLockSecret}
        unlocking={unlockingPOS}
        onUnlock={handleUnlockPOS}
        onLogout={handlePOSLogout}
      />

      <UserSecurityModal
        open={showUserSecurityModal}
        onClose={() => setShowUserSecurityModal(false)}
        user={user}
      />
      {/* HEADER */}
      <header className="flex items-center justify-between px-4 py-2 border-b flex-shrink-0" style={{borderColor:"#e2e8f0",background:"#f8fafc",minHeight:44}}>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/")} className="text-slate-600 hover:text-slate-900 p-1 rounded-lg" data-testid="pos-back-btn"><ChevronLeft className="w-4 h-4"/></button>
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={brandName}
                className="h-8 w-8 rounded-lg object-contain bg-white p-1"
              />
            ) : null}
            <span className="text-slate-900 font-bold text-sm leading-none">{brandName} <span className="text-slate-600 font-normal">Terminal</span></span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={()=>{if(cashSessionOpen) return; setCashModalType("open");setCashAmount("");setCashNote("");setShowCashModal(true);}}
            disabled={cashSessionOpen}
            title={cashSessionOpen ? "A caixa já está aberta" : "Abrir caixa"}
            className="px-2 py-1 rounded text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            style={cashSessionOpen
              ? {background:"rgba(148,163,184,0.12)",color:"#94a3b8",border:"1px solid rgba(148,163,184,0.20)"}
              : {background:"rgba(74,222,128,0.1)",color:"#4ade80",border:"1px solid rgba(74,222,128,0.2)"}}
          >Abertura</button>
          <button onClick={()=>{setCashModalType("in");setCashAmount("");setCashNote("");setShowCashModal(true);}} className="px-2 py-1 rounded text-xs" style={{background:"rgba(59,130,246,0.1)",color:"#2563eb",border:"1px solid rgba(59,130,246,0.2)"}}>Entrada</button>
          <button onClick={()=>{setCashModalType("out");setCashAmount("");setCashNote("");setShowCashModal(true);}} className="px-2 py-1 rounded text-xs" style={{background:"rgba(245,158,11,0.1)",color:"#f59e0b",border:"1px solid rgba(245,158,11,0.2)"}}>Saída</button>
          <button
            onClick={()=>{if(!cashSessionOpen) return; setCashModalType("close");setCashAmount("");setCashNote("");setShowCashModal(true);}}
            disabled={!cashSessionOpen}
            title={!cashSessionOpen ? "A caixa já está fechada" : "Fechar caixa"}
            className="px-2 py-1 rounded text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            style={!cashSessionOpen
              ? {background:"rgba(148,163,184,0.12)",color:"#94a3b8",border:"1px solid rgba(148,163,184,0.20)"}
              : {background:"rgba(239,68,68,0.1)",color:"#f87171",border:"1px solid rgba(239,68,68,0.2)"}}
          >Fecho</button>
          <button onClick={()=>setShowCashHistoryModal(true)} className="px-2 py-1 rounded text-xs" style={{background:"rgba(15,23,42,0.08)",color:"#0f172a",border:"1px solid rgba(15,23,42,0.15)"}}>
            <FileText className="w-3 h-3 inline mr-1"/>Histórico
          </button>
          <button onClick={openQuickSalesHistory} className="px-2 py-1 rounded text-xs" style={{background:"rgba(16,185,129,0.10)",color:"#047857",border:"1px solid rgba(16,185,129,0.20)"}}>
            <History className="w-3 h-3 inline mr-1"/>Hist. Vendas
          </button>
          <button onClick={openCustomerHistory} className="px-2 py-1 rounded text-xs" style={{background:selectedCustomer ? "rgba(59,130,246,0.10)" : "rgba(148,163,184,0.12)",color:selectedCustomer ? "#1d4ed8" : "#64748b",border:selectedCustomer ? "1px solid rgba(59,130,246,0.20)" : "1px solid rgba(148,163,184,0.20)"}}>
            <Users className="w-3 h-3 inline mr-1"/>Hist. Cliente
          </button>
          {suspendedSales.length>0&&(<button onClick={()=>setShowSuspendedModal(true)} className="px-2 py-1 rounded text-xs" style={{background:"rgba(245,158,11,0.1)",color:"#f59e0b",border:"1px solid rgba(245,158,11,0.2)"}}><Pause className="w-3 h-3 inline mr-1"/>{suspendedSales.length}</button>)}
          <div className="flex items-center gap-1.5 px-2 py-1 rounded" style={{background:"#ffffff"}}>
            <div className={`w-1.5 h-1.5 rounded-full ${cashSessionOpen ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
            <span className="text-slate-700 text-sm font-medium">{user?.name}</span>
            <span className={`text-[10px] font-semibold ${cashSessionOpen ? 'text-emerald-700' : 'text-rose-700'}`}>
              {cashSessionOpen ? 'Caixa aberta' : 'Caixa fechada'}
            </span>
          </div>

          <button
            onClick={() => setShowUserSecurityModal(true)}
            className="px-2 py-1 rounded text-xs"
            style={{background:"rgba(15,23,42,0.08)",color:"#0f172a",border:"1px solid rgba(15,23,42,0.15)"}}
          >
            Segurança
          </button>

          <button
            onClick={() => openPOSLock('manual')}
            className="px-2 py-1 rounded text-xs"
            style={{background:"rgba(245,158,11,0.10)",color:"#b45309",border:"1px solid rgba(245,158,11,0.25)"}}
          >
            Bloquear
          </button>

          <button
            onClick={handlePOSLogout}
            className="px-2 py-1 rounded text-xs"
            style={{background:"rgba(239,68,68,0.10)",color:"#dc2626",border:"1px solid rgba(239,68,68,0.20)"}}
          >
            Logout
          </button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        {/* LEFT — SEARCH + CATEGORIES + QUICK + RESULTS */}
        <div className="flex flex-col min-w-0 overflow-hidden p-3 gap-2" style={{width:"42%",background:"#f8fafc"}}>
          {/* Search */}
          <div className="relative flex-shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{color:"#4ade80"}}/>
            <input ref={searchInputRef} type="text" placeholder="Pesquisar ou código de barras..." value={searchQuery}
              onChange={(e)=>setSearchQuery(e.target.value)} onKeyDown={handleSearchKeyDown} data-testid="pos-search-input"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm font-medium outline-none placeholder:text-slate-400"
              style={{background:"#ffffff",border:"1px solid #94a3b8",color:"#111827",caretColor:"#111827",WebkitTextFillColor:"#111827"}}/>
            {searchQuery&&(<button onClick={()=>setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600"><X className="w-3.5 h-3.5"/></button>)}
          </div>

          {/* Categories — 2 linhas */}
          <div className="flex flex-wrap gap-1.5 flex-shrink-0">
            {["all",...categories].map(cat=>(<button key={cat} onClick={()=>setSelectedCategory(cat)}
              className="px-2.5 py-1 rounded-lg text-xs font-medium transition-all"
              style={selectedCategory===cat ? {background:"#4ade80",color:"#0f172a",fontWeight:700} : {background:"#ffffff",color:"#475569",border:"1px solid #cbd5e1"}}>
              {cat==="all"?"Todos":cat}
            </button>))}
          </div>

          {/* Mais Vendidos */}
          {!searchQuery&&selectedCategory==="all"&&Object.keys(topSelling).length>0&&(()=>{
            const topIds=Object.entries(topSelling).sort((a,b)=>b[1]-a[1]).slice(0,9).map(([id])=>id);
            const topProds=topIds.map(id=>products.find(p=>p.id===id)).filter(Boolean);
            if(!topProds.length) return null;
            return (
              <div className="flex-shrink-0">
                <p className="text-xs font-semibold mb-1.5" style={{color:"#4ade80"}}>⭐ Mais Vendidos</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {topProds.map(p=>(
                    <button
                      key={p.id}
                      onClick={()=>selectProductForCart(p)}
                      className="px-2.5 py-2 rounded-xl text-left transition-all active:scale-95"
                      style={{background:"rgba(74,222,128,0.06)",border:"1px solid rgba(74,222,128,0.2)",minHeight:72}}
                    >
                      <p className="text-slate-900 text-sm font-semibold line-clamp-2 leading-tight">{p.name}</p>
                      <p className="text-emerald-700 text-sm font-bold mt-1">€{p.sell_price.toFixed(2)}</p>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}


          {/* Produtos filtrados */}
          {(searchQuery||selectedCategory!=="all")&&(
            <div className="flex-1 overflow-y-auto" style={{scrollbarWidth:"thin",scrollbarColor:"#cbd5e1 transparent"}}>
              {filteredProducts.length===0 ? (<div className="flex flex-col items-center justify-center h-32 text-slate-600"><ShoppingCart className="w-8 h-8 mb-2 opacity-40"/><p className="text-xs">Nenhum produto encontrado</p></div>) : (
                <div className="grid gap-1.5" style={{gridTemplateColumns:"repeat(auto-fill, minmax(130px, 1fr))"}}>
                  {filteredProducts.slice(0,30).map(product=>(<button key={product.id} onClick={()=>selectProductForCart(product)} data-testid={`product-card-${product.id}`}
                    className="flex flex-col text-left p-2.5 rounded-xl transition-all active:scale-95" style={{background:"#ffffff",border:"1px solid #cbd5e1",boxShadow:"0 1px 2px rgba(15,23,42,0.04)"}}>
                    <p className="text-slate-900 text-xs font-medium line-clamp-2 leading-tight flex-1">{product.name}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-emerald-700 text-sm font-bold">€{product.sell_price.toFixed(2)}</span>
                      {!isClinicalServiceProduct(product) && !isNonStockProduct(product) && (
                        <span className="text-xs px-1 py-0.5 rounded" style={{background:product.total_stock>0?"#e2e8f0":"rgba(239,68,68,0.12)",color:product.total_stock>0?"#334155":"#dc2626"}}>{product.total_stock}</span>
                      )}
                    </div>
                  </button>))}
                </div>
              )}
            </div>
          )}
          {!searchQuery&&selectedCategory==="all"&&(<div className="flex-1 flex flex-col items-center justify-center opacity-40"><ShoppingCart className="w-12 h-12 text-emerald-500"/><p className="text-slate-500 text-xs mt-2">Pesquisa ou lê código de barras</p></div>)}
        </div>

        {/* RIGHT — CART */}
        <div className="flex flex-col border-l flex-1" style={{background:"#f8fafc",borderColor:"#e2e8f0"}}>
          {/* Cliente */}
          <button onClick={()=>setShowCustomerModal(true)} className="flex items-center gap-2.5 px-4 py-2.5 border-b hover:bg-white/5 flex-shrink-0" style={{borderColor:"#e2e8f0"}} data-testid="select-customer-btn">
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{background:selectedCustomer?"rgba(74,222,128,0.15)":"#e2e8f0"}}>
              <User className="w-3.5 h-3.5" style={{color:selectedCustomer?"#4ade80":"#475569"}}/>
            </div>
            <div className="flex-1 text-left">
              <p className="text-lg font-bold leading-tight" style={{color:selectedCustomer?"#16a34a":"#334155"}}>{selectedCustomer?selectedCustomer.name:"Consumidor Final"}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm font-medium text-slate-600 mt-1">
                <span>Telefone: <strong className="text-slate-900">{selectedCustomer?.phone || '—'}</strong></span>
                <span>NIF: <strong className="text-slate-900">{selectedCustomer?.nif || 'Sem NIF'}</strong></span>
                {selectedCustomer && <span>Pontos: <strong className="text-slate-900">{customerPointsBalance}</strong></span>}
              </div>
            </div>
            {selectedCustomer&&(<button onClick={e=>{e.stopPropagation();setSelectedCustomer(null);}} className="text-slate-700 hover:text-rose-600"><X className="w-4 h-4"/></button>)}
          </button>

          {/* Items carrinho */}
          <div className="flex-1 overflow-y-auto px-3 py-2" style={{scrollbarWidth:"thin",scrollbarColor:"#cbd5e1 transparent"}}>
            {cart.length===0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-2 opacity-20">
                <ShoppingCart className="w-10 h-10 text-emerald-500"/><p className="text-slate-700 text-base font-semibold">Carrinho vazio</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {cart.map((item,index)=>(
                  <div key={index} className="rounded-xl p-3 cursor-pointer hover:border-emerald-800 transition-all"
                    style={{background:"#ffffff",border:"1px solid #e2e8f0"}}
                    onClick={()=>setEditingItem({index,price:item.unit_price.toFixed(2),qty:item.quantity,calcVal:""})}>
                    <div className="flex items-start justify-between gap-1 mb-1">
                      <p className="text-slate-900 text-base font-semibold leading-tight flex-1 line-clamp-2">{item.product_name}</p>
                      <button onClick={e=>{e.stopPropagation();removeFromCart(index);}} className="text-slate-700 hover:text-red-500 flex-shrink-0" data-testid={`remove-item-${index}`}><X className="w-4 h-4"/></button>
                    </div>
                    {item.discount>0&&(<p className="text-sm font-medium" style={{color:"#d97706"}}>-{item.discount_percent}% desc</p>)}
                    {item.available_points_campaign_id && !item.points_redemption_campaign_id && <button onClick={e=>{e.stopPropagation();toggleProductPointsRedemption(index);}} className="my-1 w-full rounded-lg bg-amber-100 border border-amber-300 text-amber-900 px-2 py-1.5 text-xs font-bold flex items-center justify-center gap-1"><Gift className="w-3.5 h-3.5" /> Comprar por {Number(item.available_points_cost || 0) * Number(item.quantity || 0)} pontos</button>}
                    {item.points_redemption_campaign_id && <button onClick={e=>{e.stopPropagation();toggleProductPointsRedemption(index);}} className="my-1 w-full rounded-lg bg-amber-500 text-slate-950 px-2 py-1.5 text-xs font-extrabold flex items-center justify-center gap-1"><Check className="w-3.5 h-3.5" /> Pago com {Number(item.points_redemption_points || 0) * Number(item.quantity || 0)} pontos · remover</button>}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <button onClick={e=>{e.stopPropagation();updateCartQuantity(index,-1);}} className="w-6 h-6 rounded flex items-center justify-center text-slate-700 hover:text-slate-950" style={{background:"#e2e8f0"}} data-testid={`decrease-qty-${index}`}><Minus className="w-2.5 h-2.5"/></button>
                        <span className="text-slate-900 text-sm font-bold w-6 text-center">{item.quantity}</span>
                        <button onClick={e=>{e.stopPropagation();updateCartQuantity(index,1);}} className="w-6 h-6 rounded flex items-center justify-center text-slate-700 hover:text-slate-950" style={{background:"#e2e8f0"}} data-testid={`increase-qty-${index}`}><Plus className="w-2.5 h-2.5"/></button>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-700">
                          €{item.unit_price.toFixed(2)}/un · IVA {Number(item.vat_rate ?? 23)}%
                        </p>
                        <p className="text-emerald-700 text-lg font-bold">€{(item.total||0).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {activeCampaigns.length>0&&(<div className="px-3 py-1 flex items-center gap-1.5 flex-shrink-0" style={{background:"rgba(74,222,128,0.05)",borderTop:"1px solid rgba(74,222,128,0.1)"}}><Gift className="w-3 h-3 text-emerald-400"/><p className="text-xs text-emerald-500 truncate">{activeCampaigns.map(c=>c.name).join(", ")}</p></div>)}

          {/* Totals + Actions */}
          <div className="px-4 py-3 border-t flex-shrink-0" style={{borderColor:"#e2e8f0"}}>
            <div className="space-y-1 mb-4 text-sm">
              <div className="flex justify-between text-slate-700 font-medium"><span>Subtotal</span><span className="font-semibold">€{cartSubtotal.toFixed(2)}</span></div>
              {voucherDiscount>0&&(
                <div className="flex items-center justify-between gap-2 font-medium" style={{color:"#d97706"}}>
                  <div className="min-w-0">
                    <span>Voucher</span>
                    {appliedVoucher?.code && (
                      <span className="ml-1 text-xs font-semibold opacity-80">({appliedVoucher.code})</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">-€{voucherDiscount.toFixed(2)}</span>
                    <button
                      type="button"
                      onClick={() => clearAppliedVoucher(true)}
                      className="px-2 py-1 rounded-lg text-xs font-bold"
                      style={{background:"rgba(245,158,11,0.12)",color:"#b45309",border:"1px solid rgba(245,158,11,0.35)"}}
                      data-testid="remove-applied-voucher-btn"
                    >
                      Remover
                    </button>
                  </div>
                </div>
              )}
              {pointsDiscount>0&&(<div className="flex justify-between font-medium" style={{color:"#b45309"}}><span>Pontos</span><span className="font-semibold">-€{pointsDiscount.toFixed(2)}</span></div>)}
              {promotionalPointsInCart>0&&(<div className="flex justify-between font-bold" style={{color:"#b45309"}}><span>Artigos por pontos</span><span>{promotionalPointsInCart} pts</span></div>)}
              <div className="flex justify-between text-slate-700 font-medium"><span>IVA incl.</span><span className="font-semibold">€{cartVAT.toFixed(2)}</span></div>
              {prepaidAppliedAmount > 0 && (
                <div className="flex justify-between text-emerald-700 text-sm font-semibold">
                  <span>Pré-pago assumido</span><span>-€{prepaidAppliedAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-end font-bold pt-2 border-t" style={{borderColor:"#e2e8f0",color:"#15803d"}}>
                <span className="text-slate-900 text-2xl font-extrabold">{prepaidAppliedAmount > 0 ? 'Falta pagar' : 'Total'}</span><span className="text-3xl font-extrabold">€{(prepaidAppliedAmount > 0 ? amountDueNow : cartTotal).toFixed(2)}</span>
              </div>
              {prepaidAppliedAmount > 0 && (
                <div className="text-[11px] text-slate-600">
                  Total da venda: €{cartTotal.toFixed(2)} | Método pré-pago: {String(convertingOrderMetaState?.payment_method || '-').toUpperCase()}
                </div>
              )}
            </div>
            {lastRemovedItem && (
              <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs">
                <span className="min-w-0 truncate font-medium text-amber-900">Removido: {lastRemovedItem.product_name}</span>
                <button type="button" onClick={restoreLastRemovedItem} className="shrink-0 font-bold text-amber-800 underline underline-offset-2">Repor artigo</button>
              </div>
            )}
            <div className="flex gap-1.5 mb-2">
              <button onClick={()=>setShowVoucherModal(true)} className="flex-1 py-1.5 rounded-lg text-slate-600 hover:text-slate-900 text-xs" style={{background:"#ffffff",border:"1px solid #cbd5e1",boxShadow:"0 1px 2px rgba(15,23,42,0.04)"}}><Ticket className="w-3 h-3 inline mr-1"/>Voucher</button>
              <button onClick={handleSuspend} className="flex-1 py-1.5 rounded-lg text-slate-600 hover:text-amber-400 text-xs" style={{background:"#ffffff",border:"1px solid #cbd5e1",boxShadow:"0 1px 2px rgba(15,23,42,0.04)"}}><Pause className="w-3 h-3 inline mr-1"/>Suspender</button>
              <button onClick={handleCancel} disabled={cart.length===0} className="flex-1 py-1.5 rounded-lg text-slate-600 hover:text-red-400 text-xs disabled:opacity-30" style={{background:"#ffffff",border:"1px solid #cbd5e1",boxShadow:"0 1px 2px rgba(15,23,42,0.04)"}}><X className="w-3 h-3 inline mr-1"/>Cancelar</button>
            </div>
            {lastRemovedItem && (
              <button
                type="button"
                onClick={restoreLastRemovedItem}
                className="mb-2 w-full truncate rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-900 hover:bg-amber-100"
                title={`Repor ${lastRemovedItem.product_name}`}
              >
                Repor último artigo removido: {lastRemovedItem.product_name}
              </button>
            )}
            <button onClick={()=>setShowPaymentModal(true)} disabled={cart.length===0||processing} data-testid="pay-btn"
              className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-30"
              style={{background:cart.length>0?"linear-gradient(135deg,#22c55e,#16a34a)":"#e2e8f0",color:cart.length>0?"#052e16":"#475569"}}>
              <CreditCard className="w-4 h-4"/>{prepaidAppliedAmount > 0 ? `Concluir €${amountDueNow.toFixed(2)}` : `Pagar €${cartTotal.toFixed(2)}`}
            </button>
          </div>
        </div>
      </div>

      {/* MODAL EDITAR ITEM CARRINHO */}
      {editingItem!==null&&(
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{background:"rgba(0,0,0,0.7)"}} onClick={()=>setEditingItem(null)}>
          <div className="rounded-2xl p-5 w-[380px] max-w-[96vw]" style={{background:"#f8fafc",border:"1px solid #e2e8f0"}} onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <p className="text-slate-900 font-semibold text-sm">{cart[editingItem.index]?.product_name}</p>
              <button onClick={()=>setEditingItem(null)} className="text-slate-600 hover:text-slate-900"><X className="w-4 h-4"/></button>
            </div>
            {/* Preço */}
            <div className="mb-3">
              <p className="text-slate-600 text-xs mb-1">Preço unitário (€)</p>
              <div className="flex items-center gap-2 p-2.5 rounded-xl" style={{background:"#ffffff",border:"1px solid #cbd5e1",boxShadow:"0 1px 2px rgba(15,23,42,0.04)"}}>
                <span className="text-emerald-400 font-bold text-lg flex-1">{editingItem.calcVal||editingItem.price}</span>
              </div>
            </div>
            {/* Taxa IVA desta linha */}
            <div className="mb-3">
              <p className="text-slate-600 text-xs mb-1">Taxa IVA desta venda</p>
              <div className="grid grid-cols-4 gap-1.5">
                {[0, 6, 13, 23].map((vatRate) => {
                  const currentVat = Number(cart[editingItem.index]?.vat_rate ?? 23);
                  const selected = currentVat === vatRate;
                  return (
                    <button
                      key={vatRate}
                      type="button"
                      onClick={() => {
                        setCart(prev => {
                          const updated = [...prev];
                          if (!updated[editingItem.index]) return prev;
                          updated[editingItem.index] = {
                            ...updated[editingItem.index],
                            vat_rate: vatRate,
                          };
                          return updated;
                        });
                      }}
                      className="py-2 rounded-xl text-xs font-bold transition active:scale-95"
                      style={{
                        background: selected ? "#dcfce7" : "#ffffff",
                        color: selected ? "#15803d" : "#475569",
                        border: selected ? "1px solid #86efac" : "1px solid #cbd5e1",
                      }}
                      data-testid={`item-vat-${vatRate}`}
                    >
                      {vatRate}%
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Altera apenas esta linha da venda. A ficha do artigo não é modificada.
              </p>
            </div>

            {/* Calculadora */}
            <div className="grid grid-cols-4 gap-1.5 mb-3">
              {["7","8","9","←","4","5","6","×","1","2","3","%","0","00",".","✓"].map(k=>(
                <button key={k} onClick={()=>{
                  if(k==="✓"){
                    const newPrice=parseFloat(editingItem.calcVal||editingItem.price);
                    if(!isNaN(newPrice)&&newPrice>=0){
                      const updated=[...cart];
                      updated[editingItem.index]={...updated[editingItem.index],unit_price:newPrice,total:newPrice*updated[editingItem.index].quantity};
                      setCart(updated);
                    }
                    setEditingItem(null);
                  } else if(k==="←"){
                    setEditingItem(prev=>({...prev,calcVal:(prev.calcVal||prev.price).slice(0,-1)}));
                  } else if(k==="×"){
                    setEditingItem(null);
                  } else if(k==="%"){
                    openDiscountModal(editingItem.index); setEditingItem(null);
                  } else {
                    setEditingItem(prev=>({...prev,calcVal:(prev.calcVal==="0"?"":prev.calcVal)+k}));
                  }
                }}
                className="py-3 rounded-xl font-semibold text-sm transition-all active:scale-95"
                style={{
                  background: k==="✓"
                    ? "linear-gradient(135deg,#4ade80,#22c55e)"
                    : k==="×"
                      ? "rgba(239,68,68,0.15)"
                      : "#ffffff",
                  color: k==="✓"
                    ? "#0f172a"
                    : k==="×"
                      ? "#dc2626"
                      : "#0f172a",
                  border:"1px solid #cbd5e1",
                  boxShadow:"0 1px 2px rgba(15,23,42,0.04)"
                }}>
                  {k}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-4 gap-1.5">
                {[5, 10, 15, 20, 25, 30, 45, 50].map((percent) => (
                  <button
                    key={percent}
                    type="button"
                    onClick={() => applyQuickItemDiscount(editingItem.index, percent)}
                    className="py-2 rounded-xl text-xs font-bold transition active:scale-95"
                    style={{background:"#ecfdf5",color:"#047857",border:"1px solid #a7f3d0"}}
                    data-testid={`quick-discount-${percent}`}
                  >
                    {percent}%
                  </button>
                ))}
              </div>

              <div className="flex gap-2">
                <button onClick={()=>{openDiscountModal(editingItem.index);setEditingItem(null);}} className="flex-1 py-2 rounded-xl text-xs text-slate-600 hover:text-slate-900" style={{background:"#ffffff",border:"1px solid #cbd5e1",boxShadow:"0 1px 2px rgba(15,23,42,0.04)"}}><Percent className="w-3 h-3 inline mr-1"/>Outro desconto</button>
                <button onClick={()=>{openNotesModal(editingItem.index);setEditingItem(null);}} className="flex-1 py-2 rounded-xl text-xs text-slate-600 hover:text-slate-900" style={{background:"#ffffff",border:"1px solid #cbd5e1",boxShadow:"0 1px 2px rgba(15,23,42,0.04)"}}><MessageSquare className="w-3 h-3 inline mr-1"/>Nota</button>
                <button onClick={()=>{removeFromCart(editingItem.index);setEditingItem(null);}} className="flex-1 py-2 rounded-xl text-xs text-red-600 hover:text-red-700" style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.2)"}}><Trash2 className="w-3 h-3 inline mr-1"/>Remover</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CAIXA */}
      {showCashModal&&(
        <CashModal
          type={cashModalType}
          onClose={()=>setShowCashModal(false)}
          userName={user?.name}
          prepaymentsSummary={prepaymentsSummary}
          onSaved={(type, payload)=>{
            loadCashSessionStatus();
            loadUnclosedCashAlert();
            if (type === 'close' && payload) {
              setCashCloseReceiptData(payload);
              setShowCashCloseReceiptModal(true);
            }
          }}
        />
      )}

      {showCashHistoryModal&&(
        <CashHistoryModal open={showCashHistoryModal} onClose={()=>setShowCashHistoryModal(false)} />
      )}

      {showCashCloseReceiptModal && (
        <CashCloseReceiptModal
          open={showCashCloseReceiptModal}
          data={cashCloseReceiptData}
          onClose={()=>setShowCashCloseReceiptModal(false)}
        />
      )}

      {showUnclosedCashAlert && (
        <UnclosedCashAlertModal
          open={showUnclosedCashAlert}
          data={unclosedCashAlertData}
          onClose={()=>setShowUnclosedCashAlert(false)}
          onOpenHistory={()=>{
            setShowUnclosedCashAlert(false);
            setShowCashHistoryModal(true);
          }}
          onOpenClose={()=>{
            setShowUnclosedCashAlert(false);
            setCashModalType('close');
            setShowCashModal(true);
          }}
        />
      )}

            {/* Batch Selection Modal */}
      <Dialog open={showBatchModal} onOpenChange={setShowBatchModal}>
        <DialogContent className="modal-light ">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Selecionar Lote</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
                      <p className="text-slate-600 text-base mt-1">
              Produto: <span className="text-slate-900">{selectedProduct?.name}</span>
            </p>
            {productBatches.map((batch) => (
              <Card
                key={batch.id}
                  className="p-4 bg-white border-slate-300 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/40 transition-colors"
                onClick={() => addToCart(selectedProduct, batch, 1)}
                data-testid={`batch-${batch.id}`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-slate-900">Lote: {batch.lot_number}</p>
                      <p className="text-slate-600 text-base mt-1">
                      Validade: {batch.expiry_date}
                    </p>
                  </div>
                  <Badge className="badge-info">
                    {batch.quantity} un
                  </Badge>
                </div>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

        {/* Customer Selection Modal */}
        <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
          <DialogContent
            className="w-[95vw] max-w-2xl p-5"
            style={{ background:"#f8fafc", border:"1px solid #cbd5e1", color:"#0f172a" }}
          >
            <DialogHeader>
              <DialogTitle className="text-slate-900">Selecionar Cliente</DialogTitle>
            </DialogHeader>

            <div className="space-y-3">
              <Input
                placeholder="Pesquisar cliente por nome, telefone, email ou NIF..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 h-12"
                style={{
                  color:"#111827",
                  caretColor:"#111827",
                  WebkitTextFillColor:"#111827",
                  background:"#ffffff",
                  borderColor:"#cbd5e1"
                }}
                data-testid="customer-search-input"
              />

              <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1">
                <Button
                  variant="outline"
                  className="w-full justify-start border-slate-300 bg-white text-slate-800 hover:bg-slate-50 hover:text-slate-900 h-12"
                  style={{
                    color:"#1f2937",
                    background:"#ffffff",
                    borderColor:"#cbd5e1",
                    WebkitTextFillColor:"#1f2937"
                  }}
                  onClick={() => {
                    setSelectedCustomer(null);
                    setCustomerSearch('');
                    setShowCustomerModal(false);
                  }}
                >
                  <User className="w-4 h-4 mr-2" />
                  Consumidor Final (Sem cliente)
                </Button>

                {filteredCustomers.map((customer) => (
                  <Card
                    key={customer.id}
                    className="p-4 bg-white border-slate-300 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/40 transition-colors"
                    onClick={() => {
                      setShowCustomerModal(false);
                      if (customer?.phone) {
                        resolveAndSelectByPhone(customer.phone, customer);
                        return;
                      }
                      selectCustomerWithCompletion(customer);
                    }}
                    data-testid={`customer-${customer.id}`}
                  >
                    <div className="flex justify-between items-center gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 break-words">{customer.name}</p>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600 mt-2">
                          <span>Telefone: <strong className="text-slate-900">{customer.phone || '—'}</strong></span>
                          <span>NIF: <strong className="text-slate-900">{customer.nif || 'Sem NIF'}</strong></span>
                        </div>
                      </div>
                      <Badge className="bg-emerald-500 text-white border-0 shrink-0">
                        {customer.points} pts
                      </Badge>
                    </div>
                  </Card>
                ))}

                {filteredCustomers.length === 0 && (
                  <div className="text-sm text-slate-600 text-center py-6">
                    Nenhum cliente encontrado.
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Completar dados básicos antes de selecionar o cliente */}
        <Dialog open={showCustomerCompletionModal} onOpenChange={(open) => {
          setShowCustomerCompletionModal(open);
          if (!open && !selectedCustomer) setCustomerToComplete(null);
        }}>
          <DialogContent className="modal-light w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <DialogHeader>
              <DialogTitle className="text-slate-900 text-xl">Completar ficha do cliente</DialogTitle>
            </DialogHeader>

            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              Dados que pode melhorar nesta ficha: <strong>{missingCustomerBasics(customerToComplete).join(', ')}</strong>.
              O preenchimento é opcional e não impede a seleção do cliente.
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="md:col-span-2">
                <Label className="text-slate-800">Nome completo</Label>
                <Input value={customerCompletionForm.name} onChange={(e) => setCustomerCompletionForm((form) => ({...form, name:e.target.value}))} className="mt-1 bg-white border-slate-300 text-slate-900" placeholder="Nome e apelido" />
              </div>
              <div>
                <Label className="text-slate-800">Telefone</Label>
                <Input value={customerCompletionForm.phone} onChange={(e) => setCustomerCompletionForm((form) => ({...form, phone:e.target.value}))} className="mt-1 bg-white border-slate-300 text-slate-900" placeholder="9XXXXXXXX" />
              </div>
              <div>
                <Label className="text-slate-800">NIF</Label>
                <Input value={customerCompletionForm.nif} onChange={(e) => setCustomerCompletionForm((form) => ({...form, nif:e.target.value}))} className="mt-1 bg-white border-slate-300 text-slate-900" placeholder="Número de identificação fiscal" />
              </div>
              <div className="md:col-span-2 border-t border-slate-200 pt-4">
                <p className="text-sm font-semibold text-slate-700 mb-3">Dados opcionais</p>
                <Label className="text-slate-800">Email</Label>
                <Input type="email" value={customerCompletionForm.email} onChange={(e) => setCustomerCompletionForm((form) => ({...form, email:e.target.value}))} className="mt-1 bg-white border-slate-300 text-slate-900" placeholder="email@exemplo.pt" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-slate-800">Morada</Label>
                <Input value={customerCompletionForm.address} onChange={(e) => setCustomerCompletionForm((form) => ({...form, address:e.target.value}))} className="mt-1 bg-white border-slate-300 text-slate-900" placeholder="Rua, número" />
              </div>
              <div>
                <Label className="text-slate-800">Código postal</Label>
                <Input value={customerCompletionForm.postal_code} onChange={(e) => setCustomerCompletionForm((form) => ({...form, postal_code:e.target.value}))} className="mt-1 bg-white border-slate-300 text-slate-900" placeholder="0000-000" />
              </div>
              <div>
                <Label className="text-slate-800">Cidade</Label>
                <Input value={customerCompletionForm.city} onChange={(e) => setCustomerCompletionForm((form) => ({...form, city:e.target.value}))} className="mt-1 bg-white border-slate-300 text-slate-900" placeholder="Localidade" />
              </div>
            </div>

            <DialogFooter className="mt-5 gap-2">
              <Button variant="outline" onClick={() => {
                setShowCustomerCompletionModal(false);
                setSelectedCustomer(customerToComplete);
                setCustomerToComplete(null);
                setCustomerSearch('');
              }} className="border-slate-300 bg-white text-slate-800">
                Selecionar sem completar
              </Button>
              <Button onClick={saveAndSelectCompletedCustomer} disabled={savingCustomerCompletion} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {savingCustomerCompletion ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Guardar e selecionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


      {/* Suspended Sales Modal */}
      <Dialog open={showSuspendedModal} onOpenChange={setShowSuspendedModal}>
        <DialogContent className="modal-light ">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Vendas Suspensas</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {suspendedSales.map((sale) => (
              <Card
                key={sale.id}
                  className="p-4 bg-white border-slate-300 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/40 transition-colors"
                onClick={() => handleResume(sale.id)}
                data-testid={`suspended-sale-${sale.id}`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-slate-900">
                      {sale.customer_name || 'Consumidor Final'}
                    </p>
                    <p className="modal-dark-text">
                      {sale.items?.length || 0} items | {sale.operator_name}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-400">
                      €{sale.total?.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-600">
                      {new Date(sale.created_at).toLocaleTimeString('pt-PT')}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>


      <Dialog open={showQuickSalesHistoryModal} onOpenChange={setShowQuickSalesHistoryModal}>
        <DialogContent className="bg-slate-950 border-slate-700 text-white max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="modal-dark-title">Histórico rápido de vendas</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {historyLoading ? (
              <div className="py-10 text-center text-slate-600">
                <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
                A carregar...
              </div>
            ) : quickSalesHistory.length === 0 ? (
              <div className="py-10 text-center text-slate-600">Sem vendas para mostrar.</div>
            ) : (
              <div className="space-y-2">
                {quickSalesHistory.map((sale) => (
                  <Card key={sale.id} className="p-3 rounded-xl border border-slate-800 bg-slate-950/70 text-white">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-white font-semibold">
                          {sale.vendus_invoice_number || sale.ncp_number || sale.id}
                        </div>
                        <div className="modal-dark-text">
                          {sale.customer_name || 'Consumidor Final'}
                        </div>
                        <div className="modal-dark-muted">
                          {formatHistoryDate(sale.completed_at)} | €{Number(sale.total || 0).toFixed(2)}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => openHistorySaleDetail(sale)} className="modal-light-btn-outline">
                          <Eye className="w-4 h-4 mr-2" />
                          Detalhe
                        </Button>
                        <Button variant="outline" onClick={() => openHistorySaleReceipt(sale)} className="border-emerald-300 bg-white text-emerald-700">
                          <Receipt className="w-4 h-4 mr-2" />
                          Talão
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}


          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCustomerHistoryModal} onOpenChange={setShowCustomerHistoryModal}>
        <DialogContent className="bg-slate-950 border-slate-700 text-white max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900">
              Histórico do cliente {selectedCustomer?.name ? `• ${selectedCustomer.name}` : ''}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {historyLoading ? (
              <div className="py-10 text-center text-slate-600">
                <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
                A carregar...
              </div>
            ) : customerHistory.length === 0 ? (
              <div className="py-10 text-center text-slate-600">Sem histórico para este cliente.</div>
            ) : (
              <div className="space-y-2">
                {customerHistory.map((sale) => (
                  <Card key={sale.id} className="p-3 rounded-xl border border-slate-800 bg-slate-950/70 text-white">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-white font-semibold">
                          {sale.vendus_invoice_number || sale.ncp_number || sale.id}
                        </div>
                        <div className="modal-dark-muted">
                          {formatHistoryDate(sale.completed_at)} | {sale.payment_method || '-'}
                        </div>
                        <div className="text-sm text-emerald-700 font-semibold">
                          €{Number(sale.total || 0).toFixed(2)}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => openHistorySaleDetail(sale)} className="modal-light-btn-outline">
                          <Eye className="w-4 h-4 mr-2" />
                          Detalhe
                        </Button>
                        <Button variant="outline" onClick={() => openHistorySaleReceipt(sale)} className="border-emerald-300 bg-white text-emerald-700">
                          <Receipt className="w-4 h-4 mr-2" />
                          Talão
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {selectedHistorySale && (
              <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                <div className="text-white font-semibold">
                  {selectedHistorySale.vendus_invoice_number || selectedHistorySale.ncp_number || selectedHistorySale.id}
                </div>
                <div className="modal-dark-text">
                  Cliente: {selectedHistorySale.customer_name || 'Consumidor Final'} | Total: €{Number(selectedHistorySale.total || 0).toFixed(2)}
                </div>
                <Input
                  type="email"
                  value={historyEmail}
                  onChange={(e) => setHistoryEmail(e.target.value)}
                  placeholder="Email do cliente"
                  className="modal-light-input"
                  style={{ color:"#111827", caretColor:"#111827", WebkitTextFillColor:"#111827" }}
                />
                <Input
                  type="tel"
                  value={historyPhone}
                  onChange={(e) => setHistoryPhone(e.target.value)}
                  placeholder="Telemóvel do cliente"
                  className="modal-light-input"
                  style={{ color:"#111827", caretColor:"#111827", WebkitTextFillColor:"#111827" }}
                />
                <div className="flex gap-2">
                  <Button onClick={handleSendHistorySaleEmail} disabled={sendingHistoryEmail} className="bg-emerald-600 hover:bg-emerald-700">
                    {sendingHistoryEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        A enviar...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Enviar por email
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => sendReceiptSmsForSale(selectedHistorySale.id, historyPhone)}
                    disabled={sendingReceiptSms}
                    className="border-blue-300 bg-white text-blue-700"
                  >
                    {sendingReceiptSms ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Smartphone className="w-4 h-4 mr-2" />}
                    Enviar por SMS
                  </Button>
                  <Button variant="outline" onClick={() => openHistorySaleReceipt(selectedHistorySale)} className="modal-light-btn-outline">
                    <Receipt className="w-4 h-4 mr-2" />
                    Abrir talão
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Provider Modal */}
      <Dialog open={showProviderModal} onOpenChange={setShowProviderModal}>
        <DialogContent className="modal-light w-[95vw] max-w-xl max-h-[88vh] overflow-y-auto p-5">
          <DialogHeader>
            <DialogTitle className="text-slate-900 text-xl">Selecionar Prestador</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-xl border border-slate-300 bg-white p-4">
              <p className="text-sm text-slate-600">Serviço</p>
              <p className="text-lg font-bold text-slate-900">
                {selectedServiceForProvider?.name || '-'}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Escolhe o prestador para este serviço antes de o adicionar ao carrinho.
              </p>
            </div>

            <div className="space-y-2">
              {activeProviders.length === 0 ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                  Não existem prestadores ativos disponíveis.
                </div>
              ) : (
                activeProviders.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => confirmServiceProviderSelection(provider)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-left hover:bg-slate-50"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{provider.name}</p>
                        <p className="text-xs text-slate-500">
                          {provider.specialty || 'Sem especialidade definida'}
                        </p>
                      </div>
                      <span className="inline-flex rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                        Prestador ativo
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>

          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="modal-light w-[96vw] max-w-3xl max-h-[94vh] overflow-hidden p-0">
          <div className="px-6 pt-5 pb-2 border-b border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="text-slate-900 text-xl">Finalizar Venda</DialogTitle>
          </DialogHeader>
          </div>

          <div className="space-y-3 overflow-y-auto px-6 py-4" style={{ maxHeight: 'calc(94vh - 155px)' }}>
            {/* Total */}
            <div className="text-center p-3 bg-emerald-50 rounded-xl border border-emerald-200 shadow-sm">
              <p className="text-base font-bold text-slate-800 mb-1">{prepaidAppliedAmount > 0 ? 'Valor em falta' : 'Total a Pagar'}</p>
              <p className="text-4xl font-extrabold text-emerald-700">
                €{(prepaidAppliedAmount > 0 ? amountDueNow : cartTotal).toFixed(2)}
              </p>
              {prepaidAppliedAmount > 0 && (
                <div className="mt-2 text-xs text-slate-600">
                  Pré-pago assumido: €{prepaidAppliedAmount.toFixed(2)} por {String(convertingOrderMetaState?.payment_method || '-').toUpperCase()}
                </div>
              )}
            </div>

            {/* Payment Method */}
            <div className="space-y-3">
              <p className="text-sm font-bold text-white" style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff' }}>Método de Pagamento</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {[
                  { id: 'cash', label: 'Dinheiro', icon: Banknote },
                  { id: 'card', label: 'Multibanco/Cartão', icon: CreditCard },
                  { id: 'mbway', label: 'MBWay', icon: Smartphone },
                  { id: 'mixed', label: 'Misto', icon: CreditCard },
                    { id: 'account_current', label: 'Conta Cliente', icon: Wallet },
                ].map((method) => (
                  <Button
                    key={method.id}
                    variant={paymentMethod === method.id ? 'default' : 'outline'}
                    onClick={() => {
                      setPaymentMethod(method.id);
                      if (method.id === 'cash') {
                        setCashKeypadValue(String(cashReceived || ''));
                        setShowCashKeypad(true);
                      }
                    }}
                    className={`h-16 px-2 flex flex-col items-center justify-center gap-1 text-center ${paymentMethod === method.id ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50 hover:text-slate-900'}`}
                    data-testid={`payment-${method.id}`}
                  >
                    <method.icon className="w-5 h-5 shrink-0" />
                    <span className="text-xs font-semibold leading-tight whitespace-normal">{method.label}</span>
                  </Button>
                ))}
              </div>
            </div>

            {paymentMethod === 'mbway' && !isFullyCoveredByPrepaid && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-800">Número MB WAY do cliente</p>

                <div className="p-3 bg-white rounded-xl border border-slate-300 shadow-sm space-y-3">
                  <Input
                    type="text"
                    value={mbwayPhone}
                    onChange={(e) => setMbwayPhone(e.target.value)}
                    placeholder="9XXXXXXXX"
                    className="bg-white border-slate-400 text-xl h-14 font-bold text-center text-slate-900 placeholder:text-slate-400"
                    style={{ color:"#0f172a", caretColor:"#0f172a", WebkitTextFillColor:"#0f172a" }}
                    data-testid="mbway-phone-input"
                  />

                  <div className="text-xs text-slate-600">
                    {selectedCustomer?.phone
                      ? `Número sugerido da ficha do cliente: ${selectedCustomer.phone}`
                      : 'Confirma sempre o número antes de enviar o pedido MB WAY.'}
                  </div>

                  <div className="rounded-lg px-3 py-3 bg-blue-50 border border-blue-200">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-blue-800 font-medium">Estado</span>
                      <span className="font-bold text-blue-900">
                        {mbwayWaiting ? 'A aguardar callback...' : 'Pronto para enviar'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {paymentMethod === 'cash' && (
              <div className="space-y-3">
                <p className="text-sm font-bold text-white" style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff' }}>Dinheiro entregue pelo cliente</p>

                <div className="p-3 bg-white rounded-xl border border-slate-300 shadow-sm">
                  <div className="grid grid-cols-1 md:grid-cols-[1.15fr_0.85fr] gap-3 items-start">
                    <div>
                      <Input
                        type="text"
                        min="0"
                        step="0.01"
                        value={cashReceived}
                        onChange={(e) => setCashReceived(e.target.value)}
                        onClick={() => {
                          setCashKeypadValue(String(cashReceived || ''));
                          setShowCashKeypad(true);
                        }}
                        placeholder="0.00"
                        inputMode="decimal"
                        className="bg-white border-slate-400 text-2xl h-14 font-bold text-center text-slate-900 placeholder:text-slate-400"
                        style={{ color:"#0f172a", caretColor:"#0f172a", WebkitTextFillColor:"#0f172a" }}
                        data-testid="cash-received-input"
                      />

                      <div className="grid grid-cols-4 gap-2 mt-3">
                        {[5, 10, 20, 50].map((value) => (
                          <Button
                            key={value}
                            type="button"
                            variant="outline"
                            onClick={() => setCashReceived(String(value))}
                            className="h-10 border-slate-400 bg-white text-slate-900 hover:bg-slate-50 hover:text-slate-900 font-semibold"
                          >
                            €{value}
                          </Button>
                        ))}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setCashKeypadValue(String(cashReceived || ''));
                          setShowCashKeypad(true);
                        }}
                        className="mt-2 h-10 w-full border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100"
                      >
                        Introduzir outro valor
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm rounded-lg px-3 py-2 bg-slate-50 border border-slate-200">
                        <span className="text-slate-700 font-medium">A pagar</span>
                        <span className="font-bold text-slate-900">€{cashAmountDue.toFixed(2)}</span>
                      </div>

                      <div className="flex items-center justify-between text-sm rounded-lg px-3 py-2 bg-slate-50 border border-slate-200">
                        <span className="text-slate-700 font-medium">Entregue</span>
                        <span className="font-bold text-slate-900">€{cashReceivedValue.toFixed(2)}</span>
                      </div>

                      {cashReceivedValue >= cashAmountDue ? (
                        <div className="flex items-center justify-between rounded-lg px-3 py-3 bg-emerald-50 border border-emerald-300">
                          <span className="text-emerald-800 font-semibold">Troco</span>
                          <span className="text-2xl font-extrabold text-emerald-800">€{cashChange.toFixed(2)}</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between rounded-lg px-3 py-3 bg-rose-50 border border-rose-300">
                          <span className="text-rose-800 font-semibold">Falta</span>
                          <span className="text-2xl font-extrabold text-rose-800">€{cashMissing.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {paymentMethod === 'mixed' && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-800">Pagamento Misto</p>

                <div className="p-3 bg-white rounded-xl border border-slate-300 shadow-sm space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Método 1</Label>
                      <Select value={mixedPrimaryMethod} onValueChange={setMixedPrimaryMethod}>
                        <SelectTrigger className="bg-white border-slate-300 text-slate-900">
                          <SelectValue placeholder="Escolher método" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Dinheiro</SelectItem>
                          <SelectItem value="card">Cartão</SelectItem>
                          <SelectItem value="transfer">Transferência</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Valor método 1</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={mixedPrimaryAmount}
                        onChange={(e) => setMixedPrimaryAmount(e.target.value)}
                        placeholder="0.00"
                        className="bg-white border-slate-400 text-xl h-12 font-bold text-center text-slate-900 placeholder:text-slate-400"
                        style={{ color:"#0f172a", caretColor:"#0f172a", WebkitTextFillColor:"#0f172a" }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Método 2</Label>
                      <Select value={mixedSecondaryMethod} onValueChange={setMixedSecondaryMethod}>
                        <SelectTrigger className="bg-white border-slate-300 text-slate-900">
                          <SelectValue placeholder="Escolher método" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Dinheiro</SelectItem>
                          <SelectItem value="card">Cartão</SelectItem>
                          <SelectItem value="transfer">Transferência</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Valor método 2</Label>
                      <Input
                        type="text"
                        value={mixedSecondaryAmountValue.toFixed(2)}
                        readOnly
                        className="bg-slate-50 border-slate-300 text-xl h-12 font-bold text-center text-slate-900"
                        style={{ color:"#0f172a", WebkitTextFillColor:"#0f172a" }}
                      />
                    </div>
                  </div>

                  <div className="rounded-lg px-3 py-3 bg-slate-50 border border-slate-200 space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">Total a pagar</span>
                      <span className="font-bold text-slate-900">€{mixedAmountDue.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">Restante automático</span>
                      <span className="font-bold text-emerald-700">€{mixedSecondaryAmountValue.toFixed(2)}</span>
                    </div>
                    {!mixedMethodsValid && (
                      <div className="text-xs text-rose-600">
                        Escolhe dois métodos diferentes.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

              {paymentMethod === 'account_current' && !isFullyCoveredByPrepaid && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-800">Conta Corrente do Cliente</p>

                  <div className="p-3 bg-white rounded-xl border border-slate-300 shadow-sm space-y-3">
                    {!selectedCustomer?.id ? (
                      <div className="rounded-lg px-3 py-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium">
                        Escolhe primeiro um cliente para usar conta corrente.
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label>Valor entregue agora</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={accountCurrentPaidNow}
                              onChange={(e) => setAccountCurrentPaidNow(e.target.value)}
                              placeholder="0.00"
                              className="bg-white border-slate-400 text-xl h-12 font-bold text-center text-slate-900 placeholder:text-slate-400"
                              style={{ color:'#0f172a', caretColor:'#0f172a', WebkitTextFillColor:'#0f172a' }}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Método do recebido agora</Label>
                            <Select value={accountCurrentPaidMethod} onValueChange={setAccountCurrentPaidMethod}>
                              <SelectTrigger className="bg-white border-slate-300 text-slate-900 h-12">
                                <SelectValue placeholder="Escolher método" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cash">Dinheiro</SelectItem>
                                <SelectItem value="card">Cartão</SelectItem>
                                <SelectItem value="transfer">Transferência</SelectItem>
                                <SelectItem value="mbway">MBWay</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="rounded-lg px-3 py-3 bg-slate-50 border border-slate-200">
                            <div className="text-xs text-slate-600">Total desta venda</div>
                            <div className="text-xl font-bold text-slate-900">€{(prepaidAppliedAmount > 0 ? amountDueNow : cartTotal).toFixed(2)}</div>
                          </div>

                          <div className="rounded-lg px-3 py-3 bg-emerald-50 border border-emerald-200">
                            <div className="text-xs text-emerald-700">Recebido agora</div>
                            <div className="text-xl font-bold text-emerald-800">€{Math.min(Math.max(0, Number.parseFloat(String(accountCurrentPaidNow || '').replace(',', '.')) || 0), (prepaidAppliedAmount > 0 ? amountDueNow : cartTotal)).toFixed(2)}</div>
                          </div>

                          <div className="rounded-lg px-3 py-3 bg-amber-50 border border-amber-200">
                            <div className="text-xs text-amber-700">Fica em aberto</div>
                            <div className="text-xl font-bold text-amber-800">€{Math.max(0, Number((((prepaidAppliedAmount > 0 ? amountDueNow : cartTotal) - Math.min(Math.max(0, Number.parseFloat(String(accountCurrentPaidNow || '').replace(',', '.')) || 0), (prepaidAppliedAmount > 0 ? amountDueNow : cartTotal))).toFixed(2)))).toFixed(2)}</div>
                          </div>
                        </div>

                        <div className="rounded-lg px-3 py-3 bg-blue-50 border border-blue-200 text-blue-900 text-sm">
                          A venda fica faturada agora. Só o valor recebido já entra no caixa. O restante fica na conta corrente do cliente.
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

            {selectedCustomer?.id && customerPointsBalance > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-700">Saldo de Pontos do Cliente</p>

                <div className="p-3 bg-white rounded-xl border border-slate-300 shadow-sm space-y-3">
                  <div className="flex items-center justify-between rounded-lg px-3 py-2 bg-slate-50 border border-slate-200">
                    <span className="text-slate-700 font-medium">Saldo disponível</span>
                    <span className="font-bold text-slate-900">{customerPointsBalance} pts</span>
                  </div>

                  <div className="text-xs text-slate-600">
                    Valor configurado do ponto: €{pointsValue.toFixed(4)} por ponto
                  </div>

                  <label className="flex items-center gap-2 text-sm text-slate-800">
                    <input
                      type="checkbox"
                      checked={useCustomerPoints}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setUseCustomerPoints(checked);
                        if (checked) {
                          setPointsToUse(Math.min(customerPointsBalance, maxPointsUsableByTotal));
                        } else {
                          setPointsToUse(0);
                        }
                      }}
                    />
                    Usar pontos nesta compra
                  </label>

                  {useCustomerPoints && (
                    <div className="space-y-3">
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        value={pointsToUse}
                        onChange={(e) => setPointsToUse(e.target.value)}
                        placeholder="Quantidade de pontos a usar"
                        className="bg-white border-slate-400 text-xl h-14 font-bold text-center text-slate-900 placeholder:text-slate-400"
                        style={{ color:"#0f172a", caretColor:"#0f172a", WebkitTextFillColor:"#0f172a" }}
                      />

                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setPointsToUse(Math.min(customerPointsBalance, maxPointsUsableByTotal))}
                          className="border-slate-300 bg-white text-slate-800 hover:bg-slate-50 hover:text-slate-900"
                        >
                          Usar máximo
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setPointsToUse(0)}
                          className="border-slate-300 bg-white text-slate-800 hover:bg-slate-50 hover:text-slate-900"
                        >
                          Limpar
                        </Button>
                      </div>

                      <div className="rounded-lg px-3 py-3 bg-amber-50 border border-amber-200">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-amber-800 font-medium">Desconto por pontos</span>
                          <span className="font-extrabold text-amber-900">€{pointsDiscount.toFixed(2)}</span>
                        </div>
                        <div className="text-xs text-amber-700 mt-1">
                          Pontos usados: {safePointsToUse} pts
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Invoice Type */}
            <div className="space-y-3">
              <p className="text-sm font-bold text-white" style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff' }}>Tipo de Documento</p>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant={invoiceType === 'internal' ? 'default' : 'outline'}
                  onClick={() => setInvoiceType('internal')}
                  className={`h-16 flex flex-col items-center justify-center gap-1 ${invoiceType === 'internal' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50 hover:text-slate-900'}`}
                  data-testid="invoice-internal"
                >
                  <Tag className="w-5 h-5" />
                  <span className="text-sm font-semibold">Venda Interna</span>
                </Button>
                <Button
                  variant={invoiceType === 'fiscal' ? 'default' : 'outline'}
                  onClick={() => setInvoiceType('fiscal')}
                  className={`h-16 flex flex-col items-center justify-center gap-1 ${invoiceType === 'fiscal' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50 hover:text-slate-900'}`}
                  data-testid="invoice-fiscal"
                >
                  <FileText className="w-5 h-5" />
                  <span className="text-sm font-semibold">Fatura Fiscal</span>
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex-row items-center justify-end gap-3">
            <Button
              variant="outline"
              style={{background:"#ffffff", borderColor:"#cbd5e1", color:"#0f172a", WebkitTextFillColor:"#0f172a"}}
              onClick={() => {
                setShowPaymentModal(false);
                setCashReceived('');
              }}
              className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
            >
              Cancelar
            </Button>
            <Button
              onClick={openFinalSaleConfirmation}
              disabled={
                processing ||
                !paymentMethod ||
                (!isFullyCoveredByPrepaid && paymentMethod === 'cash' && cashReceivedValue < cashAmountDue) ||
                (!isFullyCoveredByPrepaid && paymentMethod === 'mbway' && !String(mbwayPhone || '').trim()) ||
                (!isFullyCoveredByPrepaid && paymentMethod === 'mixed' && !mixedIsValid)
                  || (!isFullyCoveredByPrepaid && paymentMethod === 'account_current' && !selectedCustomer?.id)
              }
              className="h-12 min-w-[260px] bg-emerald-600 hover:bg-emerald-700 text-white text-base font-bold tracking-tight flex items-center justify-center" data-testid="confirm-payment-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {isFullyCoveredByPrepaid
                    ? 'Concluir Venda'
                    : paymentMethod === 'mbway'
                      ? (mbwayWaiting ? 'Aguardar Confirmação...' : 'Enviar Pedido MB WAY')
                          : paymentMethod === 'account_current'
                            ? 'Lançar em Conta Cliente'
                            : `Continuar · ${invoiceType === 'fiscal' ? 'Venda Fiscal' : 'Venda Interna'}`}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teclado numérico tátil para pagamentos em dinheiro */}
      <Dialog open={showCashKeypad} onOpenChange={setShowCashKeypad}>
        <DialogContent className="modal-light w-[92vw] max-w-sm p-5">
          <DialogHeader>
            <DialogTitle className="text-slate-900 text-center">Valor entregue</DialogTitle>
          </DialogHeader>
          <div className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-right text-3xl font-bold text-slate-900 min-h-16">
            €{String(cashKeypadValue || '0').replace('.', ',')}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {['1','2','3','4','5','6','7','8','9'].map((digit) => (
              <Button key={digit} type="button" variant="outline" className="h-14 text-2xl bg-white text-slate-900 border-slate-300" onClick={() => setCashKeypadValue((value) => `${value}${digit}`)}>{digit}</Button>
            ))}
            <Button type="button" variant="outline" className="h-14 text-2xl bg-white text-slate-900 border-slate-300" onClick={() => setCashKeypadValue((value) => `${value}0`)}>0</Button>
            <Button type="button" variant="outline" className="h-14 text-2xl bg-white text-slate-900 border-slate-300" onClick={() => setCashKeypadValue((value) => (String(value).includes('.') || String(value).includes(',')) ? value : `${value || '0'},`)}>,</Button>
            <Button type="button" variant="outline" className="h-14 text-xl bg-white text-slate-900 border-slate-300" onClick={() => setCashKeypadValue((value) => String(value || '').slice(0, -1))}>⌫</Button>
          </div>
          <Button type="button" variant="outline" className="mt-2 h-10 w-full bg-rose-50 text-rose-700 border-rose-200" onClick={() => setCashKeypadValue('')}>Limpar</Button>
          <DialogFooter className="mt-3 grid grid-cols-2 gap-2">
            <Button type="button" variant="outline" className="h-12 bg-white text-slate-800" onClick={() => setShowCashKeypad(false)}>Cancelar</Button>
            <Button type="button" className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold" onClick={() => {
              const normalized = String(cashKeypadValue || '').replace(',', '.');
              const amount = Number.parseFloat(normalized);
              if (!Number.isFinite(amount) || amount < 0) {
                toast.error('Indique um valor válido');
                return;
              }
              setCashReceived(amount.toFixed(2));
              setShowCashKeypad(false);
            }}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação final da identidade fiscal do cliente */}
      <Dialog open={showCustomerVoucherPrompt} onOpenChange={setShowCustomerVoucherPrompt}>
        <DialogContent className="modal-light w-[95vw] max-w-lg p-6">
          <DialogHeader>
            <DialogTitle className="text-xl text-slate-900">Este cliente tem vouchers disponíveis</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-700">Pode aplicar um voucher agora ou continuar esta venda sem o utilizar.</p>
          <div className="max-h-72 space-y-2 overflow-y-auto py-1">
            {customerAvailableVouchers.map((voucher) => (
              <div key={voucher.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-300 bg-white p-3">
                <div>
                  <p className="font-mono font-bold text-slate-900">{voucher.code}</p>
                  <p className="text-xs text-slate-600">
                    {voucher.valid_until ? `Válido até ${String(voucher.valid_until).slice(0, 10)}` : 'Sem data limite indicada'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-emerald-700">
                    {voucher.is_percentage ? `${Number(voucher.value || 0)}%` : `€${Number(voucher.value || 0).toFixed(2)}`}
                  </span>
                  <Button
                    type="button"
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                    onClick={async () => {
                      const applied = await applyVoucher(voucher.code);
                      if (applied) {
                        setShowCustomerVoucherPrompt(false);
                        openFinalSaleConfirmation(true);
                      }
                    }}
                  >
                    Aplicar
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              className="border-slate-300 bg-white text-slate-800"
              onClick={() => setShowCustomerVoucherPrompt(false)}
            >
              Voltar
            </Button>
            <Button
              type="button"
              className="bg-slate-700 text-white hover:bg-slate-800"
              onClick={() => {
                setShowCustomerVoucherPrompt(false);
                openFinalSaleConfirmation(true);
              }}
            >
              Continuar sem voucher
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showInvoiceIdentityChoice && !!selectedCustomer} onOpenChange={setShowInvoiceIdentityChoice}>
        <DialogContent className="max-w-lg p-6 bg-white text-slate-900 border-slate-300" style={{ backgroundColor: '#ffffff', color: '#0f172a', colorScheme: 'light' }}>
          <DialogHeader><DialogTitle className="text-slate-900" style={{ color: '#0f172a' }}>O cliente pretende NIF na fatura?</DialogTitle></DialogHeader>
          <p className="text-slate-900 font-semibold">{selectedCustomer?.name}</p>
          <p className="text-slate-700">O histórico e os pontos ficam associados à ficha em ambas as opções. As vendas internas seguem sempre sem NIF, salvo alteração no fecho do dia.</p>
          <p className="text-slate-700">NIF da ficha: {selectedCustomer?.nif || 'Não preenchido'}</p>
          <DialogFooter>
            <Button variant="outline" className="border-slate-400 bg-white text-slate-900 hover:bg-slate-100" style={{ backgroundColor: '#ffffff', color: '#0f172a', borderColor: '#94a3b8' }} onClick={() => { setSelectedCustomer(c => ({...c, invoiceWithNif: false})); setShowInvoiceIdentityChoice(false); }}>Sem NIF</Button>
            <Button className="bg-emerald-700 text-white hover:bg-emerald-800" style={{ backgroundColor: '#047857', color: '#ffffff' }} disabled={!selectedCustomer?.nif || selectedCustomer?.nif === '999999990'} onClick={() => { setSelectedCustomer(c => ({...c, invoiceWithNif: true})); setShowInvoiceIdentityChoice(false); }}>Com NIF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showCustomerConfirmationModal} onOpenChange={setShowCustomerConfirmationModal}>
        <DialogContent className="modal-light w-[95vw] max-w-lg p-6">
          <DialogHeader>
            <DialogTitle className="text-slate-900 text-xl">
              {invoiceType === 'fiscal' ? 'Confirma Venda Fiscal' : 'Confirma Venda Interna'}
            </DialogTitle>
          </DialogHeader>

          {selectedCustomer ? (
            <div className="space-y-4">
              <p className="text-slate-700">{invoiceType === 'fiscal' && selectedCustomer.invoiceWithNif ? 'Confirma a emissão com o NIF deste cliente?' : 'A venda será sem NIF. O histórico e os pontos continuam associados a este cliente.'}</p>
              <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 text-slate-900">
                <p className="text-lg font-bold">{selectedCustomer.name}</p>
                <div className="mt-3 grid gap-2 text-sm">
                  <p>Telefone: <strong>{selectedCustomer.phone || '—'}</strong></p>
                  <p>NIF na venda: <strong>{invoiceType === 'fiscal' && selectedCustomer.invoiceWithNif ? selectedCustomer.nif : 'Sem NIF'}</strong></p>
                  {invoiceType === 'internal' && <p>No fecho do dia seguirá sem NIF, salvo edição explícita.</p>}
                  {invoiceType === 'fiscal' && <Button variant="outline" onClick={() => setShowInvoiceIdentityChoice(true)}>Alterar opção de NIF</Button>}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-4 text-slate-900">
                <p className="text-lg font-bold text-amber-900">Consumidor Final — sem NIF</p>
                <p className="mt-2 text-sm text-amber-900">Confirma que pretende fechar a venda sem identificar um cliente e sem NIF?</p>
              </div>
              <p className="text-sm text-slate-600">Se o cliente pretende NIF na fatura, cancele e selecione primeiro a ficha correta.</p>
            </div>
          )}

          {cartContainsService && invoiceType === 'internal' && (
            <div className="mt-4 rounded-xl border-2 border-amber-500 bg-amber-50 p-4 text-amber-950">
              <p className="font-bold">Atenção: este carrinho contém serviços.</p>
              <p className="mt-1 text-sm">Está a optar por uma venda exclusivamente interna, sem emissão fiscal com NIF. Confirma que é mesmo esta a operação pretendida?</p>
            </div>
          )}

          <DialogFooter className="mt-5 gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCustomerConfirmationModal(false)}
              className="border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
            >
              Voltar e corrigir
            </Button>
            <Button
              onClick={() => {
                setShowCustomerConfirmationModal(false);
                handlePayment();
              }}
              className={selectedCustomer ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
              data-testid="confirm-customer-sale-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              {invoiceType === 'fiscal' ? 'Confirmar Venda Fiscal' : 'Confirmar Venda Interna'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount Modal */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent className="modal-light ">
          <DialogHeader>
            <DialogTitle className="text-slate-900 text-xl font-bold">Aplicar Desconto</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {editingCartIndex !== null && cart[editingCartIndex] && (
              <p className="modal-dark-text">
                Produto: <span className="text-slate-900">{cart[editingCartIndex].product_name}</span>
              </p>
            )}

            <div className="flex gap-3">
              <Button
                variant={discountType === 'percent' ? 'default' : 'outline'}
                onClick={() => setDiscountType('percent')}
                className={discountType === 'percent' ? 'bg-emerald-500' : 'border-slate-200'}
              >
                <Percent className="w-4 h-4 mr-2" />
                Percentagem
              </Button>
              <Button
                variant={discountType === 'fixed' ? 'default' : 'outline'}
                onClick={() => setDiscountType('fixed')}
                className={discountType === 'fixed' ? 'bg-emerald-500' : 'border-slate-200'}
              >
                €
                Valor Fixo
              </Button>
            </div>

            <div>
              <Label className="text-slate-700">
                {discountType === 'percent' ? 'Desconto (%)' : 'Desconto (€)'}
              </Label>
              <Input
                type="number"
                min="0"
                max={discountType === 'percent' ? 100 : undefined}
                step={discountType === 'percent' ? 1 : 0.01}
                value={discountValue}
                onChange={(e) => setDiscountValue(parseFloat(String(e.target.value).replace(',', '.')) || 0)}
                className="bg-white border-slate-200 text-xl h-12"
                data-testid="discount-value-input"
              />
            </div>

            {/* Quick discount buttons */}
            <div className="flex gap-2 flex-wrap">
              {[5, 10, 15, 20, 25, 50].map(val => (
                <Button
                  key={val}
                  variant="outline"
                  size="sm"
                  onClick={() => applyQuickItemDiscount(editingCartIndex, val)}
                  className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                >
                  {val}%
                </Button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscountModal(false)} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900">
              Cancelar
            </Button>
            <Button onClick={applyItemDiscount} className="bg-emerald-500 hover:bg-emerald-600" data-testid="apply-discount-btn">
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notes Modal */}
      <Dialog open={showNotesModal} onOpenChange={setShowNotesModal}>
        <DialogContent className="modal-light ">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Notas do Produto</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {editingCartIndex !== null && cart[editingCartIndex] && (
              <p className="modal-dark-text">
                Produto: <span className="text-slate-900">{cart[editingCartIndex].product_name}</span>
              </p>
            )}

            <div>
              <Label className="text-slate-700">Descrição / Notas</Label>
              <Textarea
                value={itemNotes}
                onChange={(e) => setItemNotes(e.target.value)}
                placeholder="Adicione uma descrição ou notas para este produto..."
                className="bg-white border-slate-300 min-h-24 text-slate-900 placeholder:text-slate-400"
                style={{color:"#0f172a", caretColor:"#0f172a", WebkitTextFillColor:"#0f172a"}}
                data-testid="item-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotesModal(false)} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900">
              Cancelar
            </Button>
            <Button onClick={applyItemNotes} className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-notes-btn">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={showGeneratedVoucherModal} onOpenChange={setShowGeneratedVoucherModal}>
        <DialogContent className="modal-light  max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Gift className="w-5 h-5 text-emerald-500" />
              Voucher gerado pela campanha ativa
            </DialogTitle>
          </DialogHeader>

          {residualVoucherAfterSale && (
            <div className="space-y-3 px-1">
              <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4 text-center">
                <div className="text-sm font-semibold text-amber-900">Saldo restante do voucher</div>
                <div className="mt-1 text-2xl font-bold tracking-wider text-amber-950">{residualVoucherAfterSale.code}</div>
                <div className="mt-1 text-xl font-bold text-amber-700">€{Number(residualVoucherAfterSale.value || 0).toFixed(2)}</div>
                <div className="mt-2 text-xs text-amber-900">
                  O voucher original foi utilizado e este novo voucher conserva o crédito restante.
                  O talão de impressão e o link SMS incluem os dois documentos consecutivos.
                </div>
                {residualVoucherAfterSale.valid_until && <div className="mt-1 text-xs text-amber-800">Válido até: {residualVoucherAfterSale.valid_until}</div>}
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const response = await vouchersAPI.getPdf(residualVoucherAfterSale.id);
                      const blob = new Blob([response.data], { type: 'application/pdf' });
                      window.open(window.URL.createObjectURL(blob), '_blank');
                    } catch (error) {
                      toast.error('Erro ao abrir o voucher residual');
                    }
                  }}
                  className="mt-3 w-full border-amber-500 text-amber-800 hover:bg-amber-100"
                >
                  <FileText className="w-4 h-4 mr-2" /> Abrir voucher de saldo
                </Button>
              </div>
            </div>
          )}

          {generatedVoucherAfterSale && (
            <div className="bg-white text-black p-4 rounded-lg font-mono text-sm text-center">
              <p className="font-bold text-lg mb-2">VOUCHER DE DESCONTO</p>

              <div className="border-t border-b border-gray-300 py-4 my-2">
                <p className="text-3xl font-bold tracking-widest">{generatedVoucherAfterSale.code}</p>
              </div>

              <div className="my-4 flex justify-center">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/vouchers/${generatedVoucherAfterSale.id}/qr-image`}
                  alt="QR Code do Voucher"
                  className="w-32 h-32"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>

              <p className="text-xl font-bold my-2">
                €{Number(generatedVoucherAfterSale.value || 0).toFixed(2)} DESCONTO
              </p>

              {generatedVoucherAfterSale.valid_until && (
                <p className="text-xs mt-1">Válido até: {generatedVoucherAfterSale.valid_until}</p>
              )}

              <p className="text-xs mt-4 text-gray-500">
                Apresente este voucher ou digitalize o QR Code
              </p>
            </div>
          )}

          <div className="space-y-2 px-1">
            <Label className="text-slate-700">Enviar voucher por email</Label>
            <Input
              type="email"
              value={receiptEmail}
              onChange={(e) => setReceiptEmail(e.target.value)}
              placeholder="Email do cliente"
              className="bg-white border-slate-300 text-slate-900 w-full"
              style={{ color:"#0f172a", caretColor:"#0f172a", WebkitTextFillColor:"#0f172a" }}
            />
          </div>

          <DialogFooter className="gap-2 grid grid-cols-1 md:grid-cols-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowGeneratedVoucherModal(false);
                          }}
              className="w-full border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
            >
              Fechar
            </Button>

            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const response = await vouchersAPI.getPdf(generatedVoucherAfterSale.id);
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);
                  window.open(url, '_blank');
                } catch (error) {
                  console.error('Erro ao abrir PDF do voucher:', error);
                  toast.error('Erro ao abrir PDF do voucher');
                }
              }}
              className="w-full border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
            >
              <FileText className="w-4 h-4 mr-2" />
              Voucher PDF 80mm
            </Button>

            <Button
              onClick={async () => {
                const email = String(receiptEmail || '').trim();
                if (!email) {
                  toast.error('Indique um email para envio');
                  return;
                }

                try {
                  setSendingVoucherEmail(true);
                  await vouchersAPI.sendEmail(generatedVoucherAfterSale.id, email);
                  toast.success('Voucher enviado por email com sucesso');
                } catch (error) {
                  console.error('Erro ao enviar voucher por email:', error);
                  toast.error(error?.response?.data?.detail || 'Erro ao enviar voucher por email');
                } finally {
                  setSendingVoucherEmail(false);
                }
              }}
              disabled={sendingVoucherEmail}
              className="bg-emerald-500 hover:bg-emerald-600 md:col-span-2"
            >
              {sendingVoucherEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A enviar...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar voucher por email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voucher Modal */}
      <Dialog open={showVoucherModal} onOpenChange={setShowVoucherModal}>
        <DialogContent className="modal-light ">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Aplicar Voucher</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-slate-700">Código do Voucher ou Scan QR Code</Label>
              <Input
                value={voucherCode}
                onChange={(e) => setVoucherCode(normalizeVoucherScanCode(e.target.value).toUpperCase())}
                placeholder="Insira código ou digitalize QR Code"
                className="bg-white border-slate-200 text-xl h-12 font-mono"
                data-testid="voucher-code-input"
                autoFocus
              />
              <p className="text-xs text-slate-600 mt-1">
                Pode inserir o código manualmente ou usar um leitor de QR/Código de Barras
              </p>
            </div>

            {/* Available vouchers for customer */}
            {selectedCustomer && availableVouchers.filter(v => v.customer_id === selectedCustomer.id && !v.is_used && v.status !== 'cancelled').length > 0 && (
              <div>
                <p className="text-sm text-slate-600 mb-2">Vouchers do cliente:</p>
                <div className="space-y-2">
                  {availableVouchers
                    .filter(v => v.customer_id === selectedCustomer.id && !v.is_used && v.status !== 'cancelled')
                    .map(v => (
                      <Card
                        key={v.id}
                        className="p-3 bg-white border-slate-200 cursor-pointer hover:border-emerald-500"
                        onClick={() => setVoucherCode(v.code)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="font-mono text-slate-900">{v.code}</span>
                            {v.voucher_type === 'return' && (
                              <Badge className="ml-2 bg-amber-500/20 text-amber-400 text-[10px]">DEV</Badge>
                            )}
                          </div>
                          <Badge className="badge-success">
                            {v.is_percentage ? `${v.value}%` : `€${v.value.toFixed(2)}`}
                          </Badge>
                        </div>
                        {v.valid_until && (
                          <p className="text-xs text-slate-600 mt-1">Válido até: {v.valid_until}</p>
                        )}
                      </Card>
                    ))
                  }
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoucherModal(false)} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900">
              Cancelar
            </Button>
            <Button onClick={applyVoucher} className="bg-emerald-500 hover:bg-emerald-600" data-testid="apply-voucher-confirm-btn">
              <Ticket className="w-4 h-4 mr-2" />
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={showHistorySaleDetailModal} onOpenChange={setShowHistorySaleDetailModal}>
        <DialogContent className="modal-light  max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900">
              {selectedHistorySale?.vendus_invoice_number || selectedHistorySale?.ncp_number || 'Detalhe da venda'}
            </DialogTitle>
          </DialogHeader>

          {selectedHistorySale && (
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div><strong>Data:</strong> {formatHistoryDate(selectedHistorySale.completed_at)}</div>
                  <div><strong>Pagamento:</strong> {PAYMENT_LABELS[selectedHistorySale.payment_method] || selectedHistorySale.payment_method || '-'}</div>
                  <div><strong>Cliente:</strong> {selectedHistorySale.customer_name || 'Consumidor Final'}</div>
                  <div><strong>Total:</strong> €{Number(selectedHistorySale.total || 0).toFixed(2)}</div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="font-semibold text-slate-900 mb-3">Artigos</div>
                <div className="space-y-2">
                  {(selectedHistorySale.items || []).length === 0 ? (
                    <div className="text-sm text-slate-500">Sem artigos para mostrar.</div>
                  ) : (
                    (selectedHistorySale.items || []).map((item, idx) => (
                      <div key={idx} className="rounded-lg border border-slate-200 p-3 text-sm">
                        <div className="font-medium text-slate-900">{item.product_name || item.name || '-'}</div>
                        <div className="text-slate-600">
                          Qtd: {Number(item.quantity || 0)} | Preço: €{Number(item.unit_price || 0).toFixed(2)} | Total: €{Number(item.total || 0).toFixed(2)}
                        </div>
                        {(item.lot_number || item.batch_expiry_date || item.expiry_date) && (
                          <div className="text-xs text-slate-500 mt-1">
                            {item.lot_number ? `Lote: ${item.lot_number}` : ''}
                            {item.lot_number && (item.batch_expiry_date || item.expiry_date) ? ' | ' : ''}
                            {(item.batch_expiry_date || item.expiry_date) ? `Validade: ${item.batch_expiry_date || item.expiry_date}` : ''}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="text-slate-900">€{Number(selectedHistorySale.subtotal || 0).toFixed(2)}</span>
                </div>

                {(Number(selectedHistorySale.discount_total || 0) > 0) && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Descontos</span>
                    <span>-€{Number(selectedHistorySale.discount_total || 0).toFixed(2)}</span>
                  </div>
                )}

                {(Number(selectedHistorySale.voucher_discount || 0) > 0) && (
                  <div className="flex justify-between text-sm text-amber-700">
                    <span>Voucher</span>
                    <span>-€{Number(selectedHistorySale.voucher_discount || 0).toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">IVA</span>
                  <span className="text-slate-900">€{Number(selectedHistorySale.vat_total || 0).toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-200">
                  <span className="text-slate-900">TOTAL</span>
                  <span className="text-emerald-700">€{Number(selectedHistorySale.total || 0).toFixed(2)}</span>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                <div className="text-slate-900 font-semibold">Enviar talão digital</div>
                <Input
                  type="email"
                  value={historyEmail}
                  onChange={(e) => setHistoryEmail(e.target.value)}
                  placeholder="Email do cliente"
                  className="modal-light-input"
                  style={{ color:"#111827", caretColor:"#111827", WebkitTextFillColor:"#111827" }}
                />

                <Input
                  type="tel"
                  value={historyPhone}
                  onChange={(e) => setHistoryPhone(e.target.value)}
                  placeholder="Telemóvel do cliente"
                  className="modal-light-input"
                  style={{ color:"#111827", caretColor:"#111827", WebkitTextFillColor:"#111827" }}
                />

                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => openHistorySaleReceipt(selectedHistorySale)}
                    className="border-emerald-300 bg-white text-emerald-700"
                  >
                    <Receipt className="w-4 h-4 mr-2" />
                    Talão
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => sendReceiptSmsForSale(selectedHistorySale.id, historyPhone)}
                    disabled={sendingReceiptSms}
                    className="border-blue-300 bg-white text-blue-700"
                  >
                    {sendingReceiptSms ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Smartphone className="w-4 h-4 mr-2" />}
                    Enviar por SMS
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleSendHistorySaleEmail}
                    disabled={sendingHistoryEmail}
                    className="border-blue-300 bg-white text-blue-700"
                  >
                    {sendingHistoryEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        A enviar...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Enviar por email
                      </>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => setShowHistorySaleDetailModal(false)}
                    className="modal-light-btn-outline"
                  >
                    Fechar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Preview Modal */}
      <Dialog open={showOrderVerificationModal} onOpenChange={setShowOrderVerificationModal}>
        <DialogContent className="modal-light  max-w-2xl" style={{background:"#f8fafc", borderColor:"#cbd5e1", color:"#0f172a", WebkitTextFillColor:"#0f172a"}}>
          <DialogHeader>
            <DialogTitle className="text-slate-900" style={{color:"#0f172a", WebkitTextFillColor:"#0f172a"}}>Confirmar artigos da encomenda</DialogTitle>
          </DialogHeader>

          {pendingOrderVerification && (
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
                <div><strong>Encomenda:</strong> {pendingOrderVerification.order_number || '-'}</div>
                <div><strong>Cliente:</strong> {pendingOrderVerification.customer_name || '-'}</div>
                <div><strong>Telefone:</strong> {pendingOrderVerification.customer_phone || '-'}</div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                <Label className="text-slate-900" style={{color:"#0f172a", WebkitTextFillColor:"#0f172a"}}>Ler QR do artigo</Label>
                <Input
                  value={orderVerificationInput}
                  onChange={(e) => setOrderVerificationInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && orderVerificationInput.trim()) {
                      handleOrderVerificationScan(orderVerificationInput);
                    }
                  }}
                  placeholder="Ler com pistola ou colar QR aqui e Enter"
                  className="modal-light-input" style={{background:"#ffffff", borderColor:"#cbd5e1", color:"#0f172a", caretColor:"#0f172a", WebkitTextFillColor:"#0f172a"}}
                />
                <div className="modal-dark-muted">
                  Cada leitura confirma o artigo real, lote e validade que vão sair da loja.
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="font-semibold text-sm mb-3 text-slate-900" style={{color:"#0f172a", WebkitTextFillColor:"#0f172a"}}>Artigos pedidos</div>
                  <div className="space-y-2">
                    {(pendingOrderVerification.items || []).map((item, idx) => {
                      const verifiedQty = verifiedOrderItems
                        .filter((v) => v.product_id === item.product_id)
                        .reduce((sum, v) => sum + Number(v.quantity || 0), 0);

                      return (
                        <div key={idx} className="rounded-lg border border-slate-200 p-3 text-sm">
                          <div className="font-medium text-slate-900">{item.product_name}</div>
                          <div className="text-slate-600">
                            Pedido: {Number(item.quantity || 0)} | Confirmado: {verifiedQty}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="font-semibold text-sm mb-3 text-slate-900" style={{color:"#0f172a", WebkitTextFillColor:"#0f172a"}}>Artigos confirmados</div>
                  <div className="space-y-2 max-h-72 overflow-auto">
                    {verifiedOrderItems.length === 0 ? (
                      <div className="text-sm text-slate-500">Ainda sem leituras.</div>
                    ) : (
                      verifiedOrderItems.map((item, idx) => (
                        <div key={idx} className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
                          <div className="font-medium text-slate-900">{item.product_name}</div>
                          <div className="text-slate-700">
                            Lote: {item.lot_number || '-'} | Validade: {item.expiry_date || '-'}
                          </div>
                          <div className="text-slate-700">
                            Quantidade confirmada: {Number(item.quantity || 0)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowOrderVerificationModal(false);
                setPendingOrderVerification(null);
                setVerifiedOrderItems([]);
                setOrderVerificationInput('');
              }}
              className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
            >
              Cancelar
            </Button>
            <Button
              onClick={finalizeVerifiedOrderToPOS}
              disabled={verifyingOrderScan}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Passar para o POS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showReceiptModal}
        onOpenChange={(open) => {
          setShowReceiptModal(open);
          if (!open && generatedVoucherAfterSale) {
            setPendingOpenGeneratedVoucher(true);
          }
        }}
      >
        <DialogContent className="modal-light w-[min(96vw,760px)] max-w-[760px] max-h-[94vh] overflow-y-auto overflow-x-hidden p-5">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-400" />
              Talão de Venda
            </DialogTitle>
          </DialogHeader>

          {receiptPdfUrl ? (
            <div className="bg-white rounded-lg overflow-hidden mx-auto border border-slate-200 shadow-sm" style={{ width: '88mm', maxWidth: '100%', height: '50vh', minHeight: '360px' }}>
              {/* Pré-visualização oficial do talão backend: igual ao PDF de impressão/email */}
              <iframe
                title="Pré-visualização do talão PDF"
                src={receiptPdfUrl}
                className="w-full h-full border-0 bg-white"
              />
            </div>


          ) : receiptPdfLoading ? (
            <div className="bg-white rounded-lg mx-auto border border-slate-200 flex flex-col items-center justify-center gap-3" style={{ width: '88mm', maxWidth: '100%', height: '50vh', minHeight: '360px' }}>
              <Loader2 className="w-7 h-7 animate-spin text-emerald-600" />
              <p className="text-sm text-slate-700">A carregar talão oficial...</p>
            </div>


          ) : receiptData && (
            <div ref={receiptPreviewRef} className="bg-white text-black p-4 rounded-lg font-mono text-sm w-[80mm] mx-auto" id="receipt-content">
              <div className="text-center mb-3">
                {receiptData.company?.logo_url && (
                  <img
                    src={receiptData.company.logo_url}
                    alt="Logo"
                    className="h-14 mx-auto mb-2 object-contain"
                  />
                )}
                <p className="font-bold text-[20px] leading-tight">{receiptData.company?.name || 'Ervanária'}</p>
                {receiptData.company?.address && (
                  <p className="text-[11px] leading-tight text-gray-800">{receiptData.company.address}</p>
                )}
                {receiptData.company?.nif && (
                  <p className="text-[11px] leading-tight text-gray-800">NIF: {receiptData.company.nif}</p>
                )}
                {(receiptData.company?.phone || receiptData.company?.email || receiptData.company?.website) && (
                  <p className="text-[11px] leading-tight text-gray-800">
                    {receiptData.company.phone && `Tel: ${receiptData.company.phone}`}
                    {receiptData.company.phone && receiptData.company.email && ' | '}
                    {receiptData.company.email}
                    {(receiptData.company.phone || receiptData.company.email) && receiptData.company.website && ' | '}
                    {receiptData.company.website}
                  </p>
                )}
                {receiptData.company?.capital && (
                  <p className="text-[11px] leading-tight text-gray-800">
                    Capital Social: €{receiptData.company.capital}
                  </p>
                )}
              </div>

              <div className="border-t border-b border-gray-300 py-2 my-2 text-left">
                <p className="text-[11px] font-medium text-black">
                  Fatura Venda {receiptData.sale?.vendus_invoice_number || receiptData.sale?.ncp_number || '-'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[11px] mb-2">
                <div className="leading-tight">
                  <div className="text-gray-700">Data:</div>
                  <div className="text-black font-medium">
                    {receiptData.sale?.completed_at ? new Date(receiptData.sale.completed_at).toLocaleString('pt-PT') : '-'}
                  </div>
                </div>
                <div className="leading-tight text-right">
                  <div className="text-gray-700">Op:</div>
                  <div className="text-black font-medium">{receiptData.sale?.operator_name || '-'}</div>
                </div>
              </div>

              <div className="text-[11px] mb-2 leading-tight">
                <div><span className="text-gray-700">Cliente:</span> <span className="font-medium text-black">{receiptData.sale?.customer_name || 'Consumidor Final'}</span></div>
                <div>
                  <span className="text-gray-700">Saldo cartão cliente:</span>{' '}
                  <span className="font-medium text-black">
                    {receiptData.sale?.customer_points ?? '-'} pts
                    {receiptData.sale?.customer_points_value != null ? ` = €${Number(receiptData.sale.customer_points_value || 0).toFixed(2)}` : ''}
                  </span>
                </div>
                {(Number(receiptData.sale?.campaign_points_earned || 0) > 0 || Number(receiptData.sale?.campaign_credit_value || 0) > 0) && (
                  <div className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[10px] leading-tight text-amber-900">
                    Com esta compra acumulou <b>{Number(receiptData.sale?.campaign_points_earned || receiptData.sale?.points_earned || 0)} pts</b>
                    {' '}em saldo cartão
                    {Number(receiptData.sale?.campaign_credit_value || 0) > 0 ? `, equivalente a €${Number(receiptData.sale.campaign_credit_value || 0).toFixed(2)}` : ''}.
                  </div>
                )}
              </div>

              <div className="border-t border-gray-300 pt-2 mt-2">
                <table className="w-full text-[11px] table-fixed">
                  <colgroup>
                    <col style={{ width: '48%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                  </colgroup>
                  <thead>
                    <tr className="border-b border-gray-300 text-gray-700">
                      <th className="text-left py-1 font-bold">Artigo</th>
                      <th className="text-right py-1 font-bold">Qtd</th>
                      <th className="text-right py-1 font-bold">Preço</th>
                      <th className="text-right py-1 font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiptData.sale?.items?.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-100 align-top">
                        <td className="py-1 pr-2 text-black break-words">
                          <div>{item.product_name}</div>
                          {(item.lot_number || item.batch_expiry_date || item.expiry_date) && (
                            <div className="text-[9px] text-gray-600 leading-tight mt-0.5 whitespace-nowrap">
                              {item.lot_number ? `Lote: ${item.lot_number}` : ''}
                              {item.lot_number && (item.batch_expiry_date || item.expiry_date) ? ' | ' : ''}
                              {(item.batch_expiry_date || item.expiry_date) ? `Val: ${item.batch_expiry_date || item.expiry_date}` : ''}
                            </div>
                          )}
                        </td>
                        <td className="text-right py-1 text-black">{item.quantity}</td>
                        <td className="text-right py-1 text-black">€{(item.unit_price || 0).toFixed(2)}</td>
                        <td className="text-right py-1 text-black font-medium">€{(item.total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border-t border-gray-300 pt-2 mt-2 space-y-1 text-[11px]">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>€{(receiptData.sale?.subtotal || 0).toFixed(2)}</span>
                </div>
                {(receiptData.sale?.discount_total || 0) > 0 && (
                  <div className="flex justify-between text-red-700">
                    <span>Descontos:</span>
                    <span>-€{receiptData.sale.discount_total.toFixed(2)}</span>
                  </div>
                )}
                {(receiptData.sale?.voucher_discount || 0) > 0 && (
                  <div className="flex justify-between text-red-700">
                    <span>Voucher ({receiptData.sale.voucher_code}):</span>
                    <span>-€{receiptData.sale.voucher_discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>IVA:</span>
                  <span>€{(receiptData.sale?.vat_total || 0).toFixed(2)}</span>
                </div>

                {(Number(receiptData.sale?.prepaid_amount || 0) > 0) && (
                  <div className="flex justify-between text-emerald-700">
                    <span>Pré-pagamento:</span>
                    <span>-€{Number(receiptData.sale?.prepaid_amount || 0).toFixed(2)}</span>
                  </div>
                )}

                {(Number(receiptData.sale?.prepaid_amount || 0) > 0) && !!receiptData.sale?.prepaid_method && (
                  <div className="flex justify-between text-gray-700">
                    <span>Método pré-pago:</span>
                    <span>{String(receiptData.sale?.prepaid_method || '').toUpperCase()}</span>
                  </div>
                )}

                <div className="flex justify-between font-bold text-[16px] border-t border-gray-300 pt-1 mt-1">
                  <span>TOTAL:</span>
                  <span>€{(receiptData.sale?.total || 0).toFixed(2)}</span>
                </div>

                {(Number(receiptData.sale?.prepaid_amount || 0) > 0) && (
                  <div className="flex justify-between font-bold text-[13px]">
                    <span>Falta pagar:</span>
                    <span>
                      €{Number(
                        receiptData.sale?.remaining_paid_at_pos ??
                        Math.max(
                          0,
                          Number(receiptData.sale?.total || 0) - Number(receiptData.sale?.prepaid_amount || 0)
                        )
                      ).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-2 border-t border-gray-300 text-[11px] text-center">
                <p>
                  Pagamento: {
                    {cash: 'Dinheiro', card: 'Cartão', mbway: 'MBWay', multibanco: 'Multibanco', transfer: 'Transferência'}
                      [receiptData.sale?.payment_method] || receiptData.sale?.payment_method
                  }
                </p>
              </div>

              {(receiptData.sale?.qr_data || receiptData.sale?.atcud || receiptData.sale?.ncp_number) && (
                <div className="mt-3 pt-2 border-t border-gray-300 text-center">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(receiptData.sale?.qr_data || receiptData.sale?.atcud || receiptData.sale?.ncp_number || '')}`}
                    alt="QR"
                    className="w-28 h-28 mx-auto"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">QR / ATCUD</p>
                </div>
              )}

              <div className="text-center mt-4 pt-2 border-t border-gray-300 text-[10px] leading-tight">
                <p className="italic">{receiptData.company?.receipt_footer_message || 'Obrigado pela sua preferência!'}</p>
                {isFiscalSale ? (
                  <p className="mt-1 text-emerald-700 font-bold text-[10px]">Documento fiscal emitido com sucesso.</p>
                ) : (
                  <p className="mt-1 text-gray-500 text-[10px]">Documento interno.</p>
                )}
              </div>
            </div>
          )}

          {(receiptData?.sale?.campaign_points_earned > 0 || receiptData?.sale?.campaign_credit_value > 0) && (
            <div className="space-y-3 px-1">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
                <div className="text-sm font-semibold text-amber-800 mb-1">Saldo cartão acumulado</div>
                <div className="text-2xl font-bold text-amber-900">
                  +{Number(receiptData?.sale?.campaign_points_earned || receiptData?.sale?.points_earned || 0)} pts
                </div>
                <div className="text-lg font-bold text-amber-700 mt-1">
                  €{Number(receiptData?.sale?.campaign_credit_value || 0).toFixed(2)}
                </div>
                <div className="text-xs text-amber-800 mt-2">
                  Este valor ficou guardado no cartão do cliente para descontar numa compra futura.
                </div>
              </div>
            </div>
          )}

          {residualVoucherAfterSale && (
            <div className="space-y-3 px-1">
              <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4 text-center">
                <div className="text-sm font-semibold text-amber-900">Novo voucher com o saldo restante</div>
                <div className="mt-1 text-2xl font-bold tracking-wider text-amber-950">{residualVoucherAfterSale.code}</div>
                <div className="mt-1 text-xl font-bold text-amber-700">€{Number(residualVoucherAfterSale.value || 0).toFixed(2)}</div>
                <div className="mt-2 text-xs text-amber-900">O voucher original ficou utilizado. A impressão e o link SMS incluem o talão da venda e este voucher consecutivamente.</div>
                {residualVoucherAfterSale.valid_until && <div className="mt-1 text-xs text-amber-800">Válido até: {residualVoucherAfterSale.valid_until}</div>}
              </div>
            </div>
          )}

          {generatedVoucherAfterSale && (
            <div className="space-y-3 px-1">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-center">
                <div className="text-sm font-semibold text-emerald-800 mb-1">Voucher gerado pela campanha ativa</div>
                <div className="text-2xl font-bold text-emerald-900 tracking-wider">{generatedVoucherAfterSale.code}</div>
                <div className="text-lg font-bold text-emerald-700 mt-1">
                  {generatedVoucherAfterSale.is_percentage
                    ? `${Number(generatedVoucherAfterSale.value || 0).toFixed(2)}%`
                    : `€${Number(generatedVoucherAfterSale.value || 0).toFixed(2)}`}
                </div>
                <div className="my-3 flex justify-center">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/vouchers/${generatedVoucherAfterSale.id}/qr-image`}
                    alt="QR Voucher"
                    className="w-28 h-28"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
                {generatedVoucherAfterSale.valid_until && (
                  <div className="text-xs text-emerald-800">Válido até: {generatedVoucherAfterSale.valid_until}</div>
                )}
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        const response = await vouchersAPI.getPdf(generatedVoucherAfterSale.id);
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        window.open(url, '_blank');
                      } catch (error) {
                        console.error('Erro PDF voucher:', error);
                        toast.error('Erro ao abrir PDF do voucher');
                      }
                    }}
                    className="w-full border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Voucher PDF 80mm
                  </Button>

                  <Button
                    variant="outline"
                    onClick={async () => {
                      const email = String(receiptEmail || '').trim();
                      if (!email) {
                        toast.error('Indique um email para envio');
                        return;
                      }
                      try {
                        setSendingVoucherEmail(true);
                        await vouchersAPI.sendEmail(generatedVoucherAfterSale.id, email);
                        toast.success('Voucher enviado por email');
                      } catch (error) {
                        console.error('Erro email voucher:', error);
                        toast.error(error?.response?.data?.detail || 'Erro ao enviar voucher');
                      } finally {
                        setSendingVoucherEmail(false);
                      }
                    }}
                    disabled={sendingVoucherEmail}
                    className="w-full border-blue-500 text-blue-700 hover:bg-blue-50"
                  >
                    {sendingVoucherEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        A enviar voucher...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Enviar voucher por email
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2 px-1">
            <Label className="text-slate-700">Enviar por email</Label>
            <Input
              type="email"
              value={receiptEmail}
              onChange={(e) => setReceiptEmail(e.target.value)}
              placeholder="Email do cliente"
              className="bg-white border-slate-300 text-slate-900 w-full"
              style={{ color:"#0f172a", caretColor:"#0f172a", WebkitTextFillColor:"#0f172a" }}
            />
            <p className="modal-dark-muted">
              Pode usar o email do cliente ou escrever um email manualmente.
            </p>
          </div>

          <div className="space-y-2 px-1">
            <Label className="text-slate-700">Enviar link do talão por SMS</Label>
            <Input
              type="tel"
              value={receiptPhone}
              onChange={(e) => setReceiptPhone(e.target.value)}
              placeholder="Telemóvel do cliente"
              className="bg-white border-slate-300 text-slate-900 w-full"
              style={{ color:"#0f172a", caretColor:"#0f172a", WebkitTextFillColor:"#0f172a" }}
            />
            <p className="modal-dark-muted">O link permite abrir e descarregar o PDF durante pelo menos 3 meses.</p>
          </div>

          <DialogFooter className="gap-2 grid grid-cols-1 md:grid-cols-3">
            <Button
              variant="outline"
              onClick={() => setShowReceiptModal(false)}
              className="w-full border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
            >
              Fechar
            </Button>

            <Button
              variant="outline"
              onClick={async () => {
                const email = String(receiptEmail || '').trim();
                if (!email) {
                  toast.error('Indique um email para envio');
                  return;
                }
                if (!completedSaleId) {
                  toast.error('Venda sem identificador');
                  return;
                }

                try {
                  setSendingReceiptEmail(true);
                  await sendReceiptPreviewEmail(completedSaleId, email);
                  toast.success('Email enviado com sucesso');
                } catch (error) {
                  console.error('Erro ao enviar email:', error);
                  toast.error(error?.response?.data?.detail || 'Erro ao enviar email');
                } finally {
                  setSendingReceiptEmail(false);
                }
              }}
              disabled={sendingReceiptEmail || !completedSaleId}
              className="w-full border-blue-500 text-blue-700 hover:bg-blue-50"
            >
              {sendingReceiptEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A enviar...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar por email
                </>
              )}
            </Button>

            <Button
              variant="outline"
              onClick={() => sendReceiptSmsForSale(completedSaleId, receiptPhone)}
              disabled={sendingReceiptSms || !completedSaleId}
              className="w-full border-blue-500 text-blue-700 hover:bg-blue-50"
            >
              {sendingReceiptSms ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Smartphone className="w-4 h-4 mr-2" />}
              Enviar por SMS
            </Button>

            {isFiscalSale && completedSaleId && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const response = await salesAPI.getInvoicePDF(completedSaleId, 'a4');
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    window.open(url, '_blank');
                    toast.success('Fatura fiscal gerada');
                  } catch (error) {
                    console.error('Erro PDF fiscal:', error);
                    toast.error('Erro ao gerar fatura fiscal');
                  }
                }}
                className="w-full border-emerald-500 bg-white text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 flex items-center justify-center"
                data-testid="fiscal-pdf-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Fatura Fiscal
              </Button>
            )}

            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await downloadReceiptPreviewPdf();
                  toast.success('Talão PDF aberto no navegador');
                } catch (error) {
                  console.error('Erro ao gerar talão PDF:', error);
                  toast.error(error?.message || 'Erro ao gerar PDF');
                }
              }}
              className="w-full border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
              data-testid="download-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Talão PDF
            </Button>

            <Button
              onClick={async () => {
                if (!completedSaleId) {
                  toast.error('Venda sem identificador');
                  return;
                }

                try {
                  await openReceiptPreviewPrintWindow();
                  toast.success('Talão pronto a imprimir');
                } catch (error) {
                  console.error('Erro ao imprimir talão:', error);
                  toast.error('Erro ao abrir talão para impressão');
                }
              }}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="print-receipt-btn"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        <SelectCustomerModal
          open={phoneResolveOpen}
          phone={phoneResolvePhone}
          matches={phoneResolveMatches}
          onClose={() => {
            setPhoneResolveOpen(false);
            setPhoneResolveMatches([]);
            setPhoneResolvePhone('');
          }}
          onSelect={(c) => {
            selectCustomerWithCompletion(c || null);
            setPhoneResolveOpen(false);
            setPhoneResolveMatches([]);
            setPhoneResolvePhone('');
          }}
        />

    </div>
  );
};

export default POSPage;
